import { landingHomeEn } from './landing-home/en.js';
import { landingSecondaryEn } from './landing-secondary/en.js';
import { landingUnsupportedEn, landingUnsupportedMiscEn } from './landing-unsupported/en.js';
import { landingRemainingEn } from './landing-remaining/en.js';
import { landingUnifiedEn } from './landing-unified/en.js';
import { landingV3SlidesEn } from './landing-v3-slides/en.js';
import { visibleCopyEn } from './visible-copy.en';

export const en = {
  common: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    retry: 'Retry',
    apply: 'Apply',
    optional: 'Optional',
    copied: 'Copied',
    copyFailed: 'Copy failed',
    date: 'Date',
    status: 'Status',
    noMoreRecords: 'No more records',
    loginRequired: 'Login Required',
    loginRequiredDesc: 'Please sign in to continue',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    guest: 'Guest',
  },
  ui: {
    visibleCopy: visibleCopyEn,
    site: {
      customerSupport: 'Customer support',
      freeTierAvailable: 'Free tier available',
      features: {
        aiDesignGeneration: 'AI-powered design generation',
        socialMediaTemplates: 'Social media templates',
        instagramCarousel: 'Instagram carousel creator',
        youtubeThumbnail: 'YouTube thumbnail maker',
        linkedinPost: 'LinkedIn post designer',
        brandKit: 'Brand kit management',
      },
    },
    a11y: {
      heroSection: 'Hero section',
      featuresSection: 'Features section',
      teamSections: 'Team sections',
      solutionsGrid: 'Solutions grid',
      blogPosts: 'Blog posts',
      spotlightPosts: 'Spotlight posts',
      spotlightCategories: 'Spotlight categories',
      pagination: 'Pagination',
      recommendedPosts: 'Recommended posts',
      ctaSection: 'Call to action',
      goToPage: 'Go to page {{page}}',
      filterByUser: 'Filter by this user',
      attachmentIcon: 'Attachment icon',
    },
    media: {
      youtubeVideo: 'YouTube video',
    },
    workspace: {
      seoTitle: 'Workspace - Veeso AI',
      seoDescription:
        'Your Veeso AI workspace. Create, manage, and organize your AI-generated designs all in one place.',
      seoShortDescription: 'Your Veeso AI workspace. Create and manage your designs.',
      welcome: 'Welcome to your workspace.',
      joinDiscord: 'Join Discord',
      dailyCreditsClaimed: 'Daily credits claimed!',
      dailyCreditsReceived: 'You have received your daily free credits.',
      searchTemplates: 'Search templates...',
      loading: 'Loading',
      magicTemplates: {
        emptyTitle: 'No templates.',
        personalEmptyDescription:
          'Upload the reference design you want, and Veeso will make it your style.',
        adminEmptyDescription: 'No templates found in admin view.',
        searchEmptyDescription: 'Try adjusting your search terms.',
        loadFailed: 'Failed to load templates',
        communityLoadFailed: 'Failed to load community templates',
        adminLoadFailed: 'Failed to load admin templates',
        forkingTitle: 'Forking template',
        forkingDescription: 'Creating a copy of this template...',
        forkSuccess: 'Template forked successfully. You can now customize it.',
        forkFailed: 'Fork failed',
        deleteConfirm: 'Are you sure you want to delete this template?',
        deleteSuccess: 'Template deleted successfully',
        deleteFailed: 'Delete failed',
        add: 'Add',
        communityTab: 'Magic templates',
        personalTab: 'My templates',
        adminTab: 'Admin',
      },
      navigation: {
        create: 'Create',
        magicTemplates: 'Magic templates',
        designs: 'Your designs',
        favorites: 'Favorites',
        assets: 'Assets',
        earnCredits: 'Earn credits',
      },
    },
    admin: {
      projects: 'Admin Projects',
      filterByUserId: 'Filter by User ID',
      noMoreDesigns: 'No more designs',
    },
    tools: {
      errorRedirecting: 'Error loading tool. Redirecting...',
      more: 'More',
      categoryPageTitle: '{{category}} Design Tools',
      categories: {
        socialMedia: 'Social Media',
        digitalMarketing: 'Digital Marketing',
        businessPrint: 'Business & Print',
        stationery: 'Stationery',
        campaigns: 'Campaigns',
        industries: 'Industries',
        occasions: 'Occasions',
        workflows: 'Workflows',
        inputMethods: 'Input Methods',
        roles: 'For Every Role',
        templates: 'Templates',
      },
    },
    spotlight: {
      allCategories: 'All',
      categories: {
        news: 'News',
        comparisons: 'Comparisons',
        analysis: 'Analysis',
        guides: 'Guides',
      },
    },
    pricingDebug: {
      seoTitle: 'Pricing Debug - Veeso AI',
      comingSoonTitle: 'Pricing plans are coming soon',
      comingSoonDescription: 'We are working on our pricing plans. Please check back later.',
      paymentTest: 'Payment test (Polar / Stripe)',
      apiLabel: 'API: {{api}}',
      loginStatus: 'Login status:',
      loggedIn: 'Logged in',
      loggedOut: 'Logged out',
      jwtToken: 'JWT Token',
      jwtPlaceholder: 'Paste the JWT token without the Bearer prefix',
      saveToken: 'Save token',
      plan: 'Plan',
      subscriptionApi: 'Subscription (API)',
      createCheckout: 'Create checkout',
      createAndRedirect: 'Create and redirect',
      stripeCheckout: 'Stripe Checkout',
      checkoutDetails: 'Get Checkout Details',
      checkoutId: 'Checkout ID',
      query: 'Query',
      checkoutUrl: 'Checkout URL',
      openEmbeddedCheckout: 'Open embedded checkout',
      lastResponse: 'Last response',
      tokenRequired: 'Token is required',
      checkoutIdRequired: 'Checkout ID is required',
    },
    skills: {
      coverImage: 'Cover Image',
      replace: 'Replace',
      upload: 'Upload',
      colorPalette: 'Color Palette',
      name: 'Name',
      namePlaceholder: 'e.g. Code Reviewer',
      description: 'Description',
      descriptionPlaceholder: 'Briefly describe what this skill does',
      promptInstructions: 'Prompt Instructions',
      promptPlaceholder: 'You are an expert code reviewer...',
      examplePrompt: 'Example Prompt',
      examplePromptPlaceholder:
        "Provide an example of how to use this skill (e.g. 'Create a modern landing page using this style...')",
      publicAccess: 'Public Access',
      publicAccessDescription: 'Allow others to see and use this skill',
      multipleSelection: 'Multiple selection',
      selectUsage: 'Select Usage',
      singleSelection: 'Single select',
      or: 'or',
      customUsagePlaceholder: 'Custom usage...',
      mySkill: 'My Skill',
      public: 'Public',
      private: 'Private',
      noPreview: 'No Preview',
      tags: 'Tags',
      searchTags: 'Search tags...',
      noTagFound: 'No tag found.',
      category: 'Category',
      noCover: 'No cover',
      referenceDesigns: 'Reference designs',
      uploadReferenceImage: 'Upload Reference Image',
      uploadReferenceHint: 'Click or drag images to start',
      analyze: 'Analyze',
      scrollCategoriesLeft: 'Scroll categories left',
      scrollCategoriesRight: 'Scroll categories right',
      promptCopied: 'Skill prompt copied to clipboard',
      promptCopyFailed: 'Failed to copy skill prompt',
      colorCopied: 'Copied {{color}} to clipboard',
      colorCopyFailed: 'Failed to copy color',
      copyStyle: 'Copy style',
      textCopied: 'Copied {{text}} to clipboard',
      copyFailed: 'Failed to copy to clipboard',
      paletteExtracted: 'Color palette extracted successfully',
      noColorsFound: 'No colors found',
      colorAnalysisFailed: 'Failed to analyze colors',
      dimensionsResolved: 'Dimensions resolved from cover image',
      dimensionsUnavailable: 'Could not determine dimensions',
      dimensionsFailed: 'Failed to resolve dimensions',
      updated: 'Skill updated successfully',
      created: 'Skill created successfully',
      operationFailed: 'Operation failed',
      uploadFailed: 'Upload Failed',
      uploadCoverFailed: 'Failed to upload cover image',
      uploadImageFailed: 'Failed to upload image',
      uploadFilesFailed: 'Failed to upload one or more files',
      analysisFailed: 'Analysis Failed',
      analysisFailedDescription: 'Failed to analyze images. Please try again or fill manually.',
      validationError: 'Validation Error',
      nameAndPromptRequired: 'Name and Prompt are required',
      loadFailed: 'Failed to load skill details',
      limitReached: 'Limit Reached',
      referenceImageLimit: 'You can only add up to {{count}} reference images',
      imageLimit: 'You can only add up to {{count}} images',
      editSkill: 'Edit Skill',
      createDesignSkill: 'Create Design Skill',
      saveChanges: 'Save Changes',
      createSkill: 'Create Skill',
      analysisPresets: {
        comprehensive: 'Comprehensive Style Deconstruction',
        layout: 'Layout & Composition Focus',
        colorMood: 'Color & Atmosphere Focus',
        typography: 'Typography & Graphic Details',
      },
      referencePoints: {
        all: 'All',
        layout: 'Layout',
        style: 'Style',
        color: 'Color',
        texture: 'Texture',
        copy: 'Copy',
        font: 'Font',
      },
      usages: {
        background: 'Background',
        product: 'Product',
        model: 'Model',
        logo: 'Logo',
        qrcode: 'QR Code',
        icon: 'Icon',
        decoration: 'Decoration',
        texture: 'Texture',
        chart: 'Chart',
      },
      tagTabs: {
        all: 'ALL',
        scene: 'Scene',
        industry: 'Industry',
        style: 'Style',
      },
    },
    assetPreview: {
      close: 'Close preview',
      pdfUnsupported: 'PDF preview is not supported yet',
      previous: 'Previous image',
      next: 'Next image',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      details: 'Details',
      from: 'From',
      aiGenerated: 'AI Generated',
      name: 'Name',
      dimensions: 'Dimensions',
      format: 'Format',
      fileSize: 'File Size',
      created: 'Created',
    },
    create: {
      input: {
        defaultPlaceholder: 'What do you want to design now?',
        defaultButton: 'Design',
        designing: 'Designing...',
        designReference: 'Design reference',
        designReferenceDescription: 'Pick up a design reference from anywhere',
        documents: 'Documents',
        documentsDescription: 'Turn your documents into a design',
        wordDocument: 'Word document',
        pdfDocument: 'PDF document',
        powerpointDocument: 'PowerPoint presentation',
        excelSpreadsheet: 'Excel spreadsheet',
        textFile: 'Text file',
      },
      documentFallback: 'Document',
      hoverStylePreview: 'Hover over a style to preview',
      generateDesign: 'Generate design',
      change: 'Change',
      templateDialogTitle: 'Magic Templates / My templates / History',
      search: 'Search...',
      designIntent: 'I want to design',
      customScenePlaceholder: 'Type a custom scene...',
      anything: 'Anything',
      useCustomScene: 'Use "{{scene}}"',
      noScenesFound: 'No scenes found',
      styleIs: 'style is',
      parameterIs: '{{parameter}} is',
      addOption: 'Add option',
      noOptionsAvailable: 'No options available',
      explore: 'Explore',
      searchTemplates: 'Search templates...',
      industry: 'Industry',
      style: 'Style',
      loadingStyles: 'Loading styles...',
      edit: 'Edit',
      history: 'History',
      noMaterials: 'No materials found',
      createFromTemplate: 'Create from magic template',
      importFromDocs: 'Import content from docs',
      addReferences: 'Add reference designs',
      uploading: 'Uploading...',
      designReference: 'Design Reference',
      parsing: 'Parsing...',
      scrollLeft: 'Scroll left',
      scrollRight: 'Scroll right',
      size: 'Size',
      defaultStyle: 'Default',
      autoStyle: 'Auto',
      noStyles: 'No styles available',
      styleLabels: {
        illustration: 'Illustration',
        photorealistic: 'Photorealistic',
        abstract: 'Abstract',
        threeD: '3D',
        lineArt: 'Line Art',
      },
      templateEmpty: {
        noResults: 'No results found',
        noneYet: 'No templates yet',
        noneFound: 'No templates found',
        adjustSearch: 'Try adjusting your search query.',
        personal: "You haven't created any templates yet.",
        recommended: 'No recommended templates found.',
      },
      parameters: {
        channel: 'Channel',
        textImagePriority: 'Text vs Image Priority',
        narrativeMode: 'Narrative Mode',
        typeDensity: 'Type Density',
        sectionsBeats: 'Sections / Beats',
        ctaEmphasis: 'CTA Emphasis',
        emotion: 'Emotion',
        hookStrength: 'Hook Strength',
        thumbnailReadability: 'Thumbnail Readability',
        brandPresence: 'Brand Presence',
        contentDensity: 'Content Density',
        deckLength: 'Deck Length',
        visualSystem: 'Visual System',
        tone: 'Tone',
        sectionStructure: 'Section Structure',
        pageCount: 'Page Count',
        gridStrictness: 'Grid Strictness',
        viewingDistance: 'Viewing Distance',
        hierarchyStyle: 'Hierarchy Style',
        menuLength: 'Menu Length',
        priceVisibility: 'Price Visibility',
        conceptBoldness: 'Concept Boldness',
        textPresence: 'Text Presence',
        visualFocus: 'Visual Focus',
        texture: 'Texture',
        formality: 'Formality',
        rsvpEmphasis: 'RSVP Emphasis',
        layoutType: 'Layout Type',
        variants: 'Variants',
      },
      parameterOptions: {
        textFirst: 'Text-first',
        balanced: 'Balanced',
        visualFirst: 'Visual-first',
        storyFirst: 'Story-first',
        messageFirst: 'Message-first',
        conversionFirst: 'Conversion-first',
        compact: 'Compact',
        comfortable: 'Comfortable',
        airy: 'Airy',
        short: 'Short',
        medium: 'Medium',
        long: 'Long',
        soft: 'Soft',
        normal: 'Normal',
        strong: 'Strong',
        calm: 'Calm',
        energetic: 'Energetic',
        premium: 'Premium',
        playful: 'Playful',
        serious: 'Serious',
        dark: 'Dark',
        high: 'High',
        low: 'Low',
        sparse: 'Sparse',
        dense: 'Dense',
        textLed: 'Text-led',
        diagramLed: 'Diagram-led',
        imageLed: 'Image-led',
        business: 'Business',
        tech: 'Tech',
        marketing: 'Marketing',
        corporate: 'Corporate',
        friendly: 'Friendly',
        pitch: 'Pitch',
        report: 'Report',
        tutorial: 'Tutorial',
        loose: 'Loose',
        standard: 'Standard',
        strict: 'Strict',
        near: 'Near',
        far: 'Far',
        headlineFirst: 'Headline-first',
        infoGrid: 'Info-grid',
        minimal: 'Minimal',
        offerFirst: 'Offer-first',
        infoFirst: 'Info-first',
        brandFirst: 'Brand-first',
        safe: 'Safe',
        bold: 'Bold',
        experimental: 'Experimental',
        none: 'None',
        portrait: 'Portrait',
        symbol: 'Symbol',
        abstract: 'Abstract',
        clean: 'Clean',
        film: 'Film',
        paper: 'Paper',
        casual: 'Casual',
        semiFormal: 'Semi-formal',
        formal: 'Formal',
        list: 'List',
        grid: 'Grid',
        categoryCards: 'Category Cards',
      },
      parameterChannels: {
        instagram: 'Instagram Post',
        instagramPortrait: 'Instagram Portrait',
        instagramStory: 'Instagram Story',
        facebook: 'Facebook Post',
        facebookCover: 'Facebook Cover',
        twitter: 'X Post',
        twitterHeader: 'X Header',
        linkedin: 'LinkedIn Post',
        linkedinBanner: 'LinkedIn Banner',
        pinterest: 'Pinterest Pin',
        youtube: 'YouTube Thumbnail',
        youtubeChannel: 'YouTube Channel Art',
        tiktok: 'TikTok Video',
        longForm: 'Long-Form Content',
      },
      removeParameter: 'Remove {{parameter}}',
    },
    learn: {
      aiPresets: {
        rewrite: { label: 'Rewrite', description: 'Say the same thing in a different way.' },
        refine: { label: 'Refine', description: 'Trim extra words and keep the core idea.' },
        professional: {
          label: 'Professional',
          description: 'Make the tone more polished and formal.',
        },
        brand: { label: 'Brand Voice', description: 'Push the copy closer to your product voice.' },
        concise: { label: 'Concise', description: 'Make the text shorter and tighter.' },
        translate: {
          label: 'Translate',
          description: 'Translate between English and other languages.',
        },
        correct: { label: 'Correct', description: 'Fix grammar, typos, and awkward phrasing.' },
        expand: { label: 'Expand', description: 'Add more detail on top of the current text.' },
        summary: { label: 'Summarize', description: 'Generate a short summary.' },
      },
      imageToolbar: {
        replace: {
          label: 'Replace Image',
          description: 'Swap in another image from upload, search, recent assets, or AI tools.',
        },
        generate: {
          label: 'AI Generate',
          description: 'Generate a fresh image from a prompt, style, and model.',
        },
        bgRemover: {
          label: 'Remove Background',
          description: 'Cut out the main subject and remove the background.',
        },
        imageUpscale: { label: 'Upscale', description: 'Make blurry images sharper with AI.' },
        vectorize: {
          label: 'Vectorize',
          description: 'Convert an image into vector-style output for cleaner scaling.',
        },
        removeObjects: {
          label: 'Remove Objects',
          description: 'Brush over unwanted areas to clean them up.',
        },
        crop: { label: 'Crop', description: 'Crop the image to keep only the part you want.' },
        flip: { label: 'Flip', description: 'Flip the image horizontally or vertically.' },
        rotate: { label: 'Rotate 90°', description: 'Rotate the image in 90-degree steps.' },
        stroke: { label: 'Stroke', description: 'Add a border around the image.' },
        shadow: {
          label: 'Shadow',
          description: 'Apply image shadow presets or fine-tune manually.',
        },
        radius: { label: 'Corner Radius', description: 'Adjust the four image corners.' },
        opacity: { label: 'Opacity', description: 'Control overall image transparency.' },
        export: { label: 'Export Image', description: 'Export only this selected image.' },
        more: {
          label: 'More',
          description:
            'Open advanced image options like fit mode, blend mode, clip path, and skew.',
        },
      },
      textToolbar: {
        aiPresets: { label: 'AI Rewrite', description: 'Rewrite selected copy with one click.' },
        font: { label: 'Font', description: 'Pick a preset font or upload your own brand font.' },
        size: { label: 'Size', description: 'Choose a preset size or type in an exact value.' },
        bold: { label: 'Bold', description: 'Make the selected text bold.' },
        italic: { label: 'Italic', description: 'Toggle italic styling for the selected text.' },
        align: {
          label: 'Alignment',
          description: 'Control horizontal alignment, vertical alignment, and writing direction.',
        },
        lineHeight: {
          label: 'Line Height / Tracking',
          description: 'Adjust line spacing and letter spacing.',
        },
        stroke: { label: 'Stroke', description: 'Add a text stroke with preset styles.' },
        textShadow: {
          label: 'Text Shadow',
          description: 'Add shadow presets or tune blur, distance, and color manually.',
        },
        fill: { label: 'Color', description: 'Use solid colors or gradients.' },
        effect: {
          label: 'Text Effects',
          description: 'Switch between visual text treatments like sticker, neon, or clean type.',
        },
        highlight: {
          label: 'Highlight',
          description: 'Add a highlighted background behind text.',
        },
        styleBrush: {
          label: 'Style Brush',
          description: 'Copy one text style and apply it to another block.',
        },
        radius: { label: 'Corner Radius', description: 'Adjust the corners of the text box.' },
        opacity: { label: 'Opacity', description: 'Control transparency for the whole element.' },
        more: {
          label: 'More',
          description: 'Open the Design panel (Color, Decoration, Font).',
        },
      },
    },
    auth: {
      editProfile: 'Edit Profile',
      displayName: 'Display Name',
      bio: 'Bio',
      bioPlaceholder: 'Tell us about yourself...',
      saveChanges: 'Save Changes',
      avatarSizeError: 'Avatar image must be less than 5MB',
      profileUpdateFailed: 'Failed to update profile. Please try again.',
    },
    chat: {
      placeholder: 'Type something...',
    },
  },
  app: {
    Home: 'Home',
    Create: 'Create',
    Projects: 'Projects',
    Templates: 'Templates',
    LivePreview: 'Live Preview',
    Resource: 'Resource',
    Assets: 'Assets',
    Tool: 'Tool',
    AllTools: 'All Tools',
    Settings: 'Settings',
    logout: 'Log out',
    Feedback: 'Feedback',
    Support: 'Support',
    profile: 'Profile',
    Workspace: 'Workspace',
    accountSettings: 'Account Settings',
    generationHistory: 'Generation History',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    invite: 'Invite Friends',
    RedemptionCode: 'Redeem code',
    APIKeys: 'API Keys',
    getMoreCredits: 'Get More Credits',
    earnCredits: 'Earn Credits',
    inviteFriends: 'Invite Friends',
    referralProgram: 'Referral Program',
    creditsEarned: 'Credits Earned',
    shareToEarn: 'Share to Earn Credits',
    dailyRewards: 'Daily Rewards',
    completeProfile: 'Complete Profile',
    creditsRemaining: 'Credits Remaining:',
    upgrade: 'Upgrade',
    clickToAccess: 'Click to access',
    account: 'Account',
    claimFreeCredits: 'Daily credits — limited-time claim',
    claimed: 'Claimed',
  },
  billing: {
    plans: {
      free: 'Free',
      dev: 'Dev',
      basic: 'Basic',
      pro: 'Pro',
    },
    subscription: {
      badge: {
        free: 'Free',
        active: '{{plan}}',
        trialing: '{{plan}} Trial · ends {{until}}',
        past_due: '{{plan}} · Payment issue',
        canceled: '{{plan}} · ends {{until}}',
        revoked: '{{plan}} · ends {{until}}',
      },
      cta: {
        upgrade: 'Upgrade',
        manage: 'Manage',
        fixPayment: 'Update payment',
        renew: 'Renew',
      },
    },
  },
  dailyCredits: {
    success: {
      title: 'Success',
      description: 'Daily credits claimed successfully!',
    },
    error: {
      title: 'Error',
      description: 'Failed to claim credits',
    },
  },
  design: {
    styleBrush: 'Style brush',
    clear: 'Clear',
    fill: 'Fill',
    group: 'Group',
    Ungroup: 'Ungroup',
    background: 'Background',
    replace: 'Replace',
    replaceImage: 'Replace Image',
    generateImage: 'Generate Image using AI',
    generate: 'AI Generate',
    editImage: 'Edit',
    imageSearch: 'Search Images',
    processing: 'Processing...',
    recents: 'Recents',
    assets: 'Assets',
    qrcode: 'QR Code',
    icon: 'Icon',
    uploadImage: 'Upload Image',
    opacity: 'Opacity',
    radius: 'Radius',
    rotation: 'Rotation',
    border: 'Border',
    position: 'Position & Dimensions',
    dimensions: 'Dimensions',
    shadow: 'Shadow',
    password: {
      title: 'Password Required',
      description: 'This project is password protected. Please enter the password to access it.',
      placeholder: 'Enter password',
      submit: 'Submit',
      cancel: 'Cancel',
      invalidPassword: 'Invalid password',
    },
    leave: {
      generating:
        'Project is currently being generated. Leaving may interrupt the generation. Are you sure you want to leave?',
      unsaved: 'You have unsaved changes. Are you sure you want to leave?',
    },
    shadowPanel: {
      color: 'Color',
      parameters: 'Parameters',
      presets: 'Presets',
      none: 'None',
      mixed: 'Mixed',
      multi: 'Multi',
      blur: 'Blur',
      spread: 'Spread',
    },
    flipPanel: {
      flip: 'Flip',
      horizontal: 'Horizontal',
      vertical: 'Vertical',
      flipHorizontal: 'Flip Horizontal',
      flipVertical: 'Flip Vertical',
    },
    framePresetPanel: {
      presets: 'Presets',
      colorPresets: 'Color Presets',
      imagePresets: 'Image Presets',
      texture: 'Texture',
      abstract: 'Abstract',
      showLess: 'Show less',
      showMore: 'Show more',
    },
    framePanel: {
      pageSettings: 'Page Settings',
      presets: 'Presets',
      effects: 'Effects',
      colorPresets: 'Color Presets',
      imagePresets: 'Image Presets',
      texture: 'Texture',
      abstract: 'Abstract',
      noise: 'Noise',
      blur: 'Blur',
    },
    export: 'Export',
    rotatePanel: {
      rotate: 'Rotate',
      rotation: 'Rotation',
      rotate90: 'Rotate 90°',
    },
    backdropFilter: 'Backdrop Filter',
    backdropPanel: {
      none: 'None',
      'grayscale(100%)': 'Grayscale',
      'sepia(100%)': 'Sepia',
      'blur(5px)': 'Blur',
      'brightness(150%)': 'Brightness',
      'contrast(150%)': 'Contrast',
      'invert(100%)': 'Invert',
      'saturate(200%)': 'Saturate',
    },
    textAlignPanel: {
      textAlignment: 'Text Alignment',
      horizontalAlignment: 'Horizontal Alignment',
      verticalAlignment: 'Vertical Alignment',
      alignLeft: 'Align Left',
      alignCenter: 'Align Center',
      alignRight: 'Align Right',
      alignJustify: 'Align Justify',
      alignTop: 'Align Top',
      alignMiddle: 'Align Middle',
      alignBottom: 'Align Bottom',
      writingMode: 'Writing Mode',
      writingModeHorizontal: 'Horizontal',
      writingModeVerticalRl: 'Vertical (R→L)',
      writingModeVerticalLr: 'Vertical (L→R)',
      textOrientation: 'Text Orientation',
      textOrientationMixed: 'Mixed',
      textOrientationUpright: 'Upright',
      textOrientationSideways: 'Sideways',
    },
    dashPanel: {
      lineStyle: 'Line Style',
      style: 'Style',
      solid: 'Solid',
      dashed: 'Dashed',
      dotted: 'Dotted',
      dashDot: 'Dash-Dot',
      dashDotDot: 'Dash-Dot-Dot',
      custom: 'Custom',
      mixed: 'Mixed',
      customPattern: 'Custom Pattern',
      example: 'e.g: 5,5',
    },
    lineSize: {
      lineThickness: 'Line Thickness',
    },
    lineStylePanel: {
      lineCap: 'Line Cap',
      lineJoin: 'Line Join',
      butt: 'Butt',
      round: 'Round',
      square: 'Square',
      miter: 'Miter',
      bevel: 'Bevel',
    },
    Custom: 'Custom',
    Presets: 'Presets',
    letterSpacing: 'Letter Spacing',
    lineHeight: 'Line Height',
    padding: {
      padding: 'Padding',
      top: 'Top',
      bottom: 'Bottom',
      left: 'Left',
      right: 'Right',
    },
    save: 'Save',
    textSettings: 'Text Settings',
    groupSettings: 'Group Settings',
    lineSettings: 'Line Settings',
    children: 'Children',
    mixedNode: 'Mixed Node',
    variableLabel: 'Variable label',
    textStyle: 'Text Style',
    fontFamily: 'Font family',
    fontSize: 'Font size',
    fontWeight: 'Font weight',
    textAlignment: 'Text Alignment',
    highlight: 'Highlight',
    stroke: 'Stroke',
    effect: 'Effect',
    filter: 'Filter',
    color: 'Color',
    size: 'Size',
    decoration: 'Decoration',
    spacing: 'Spacing',
    effects: 'Effects',
    clearEffects: 'Clear Effects',
    highlightTip: 'Select text first, then apply highlight styles',
    advancedSettings: 'Advanced Settings',
    strokeWidth: 'Stroke Width',
    verticalAlign: 'Vertical Align',
    horizontalAlign: 'Horizontal Align',
    alignTop: 'Align Top',
    alignMiddle: 'Align Middle',
    alignBottom: 'Align Bottom',
    alignLeft: 'Align Left',
    alignCenter: 'Align Center',
    alignRight: 'Align Right',
    font: {
      allFonts: 'All Fonts',
      Chinese: 'Chinese Fonts',
      English: 'English Fonts',
      Local: 'Local Fonts',
      fontStyles: 'Font Styles',
      language: 'Language',
      searchFonts: 'Search fonts...',
      addLocalFont: 'Add Font',
      removeLocalFont: 'Remove font',
      noLocalFonts: 'No local fonts yet',
      uploading: 'Uploading...',
      noFontsFound: 'No fonts found',
    },
    pageSettings: 'Page Settings',
    solid: 'Solid',
    gradient: 'Gradient',
    image: 'Image',
    presets: 'Presets',
    noise: 'Noise',
    blur: 'Blur',
    overlay: 'Overlay',
    clipPath: 'Clip Path',
    imageSettings: 'Image Settings',
    objectFit: 'Object Fit',
    bgRemover: 'BG Remover',
    crop: 'Crop',
    imageStyle: 'Image Style',
    imageFilter: 'Image Filter',
    imageSource: 'Image Source',
    backgroundRemoval: 'Background Removal',
    imageUpscale: 'Image Upscale',
    enhanceImageQuality: 'Enhance image quality and resolution',
    imageEditing: 'Image Editing',
    cropDescription: 'Resize and reframe your image',
    removeObjects: 'Remove Objects',
    imageInpaint: 'Paint over unwanted objects',
    borderColor: 'Border Color',
    borderWidth: 'Border Width',
    rotate: 'Rotate',
    textEffect: 'Text Effect',
    textHighlight: 'Text Highlight',
    applyHighlight: 'Apply Highlight',
    highlightColor: 'Highlight Color',
    highlightStyle: 'Highlight Style',
    highlightOpacity: 'Highlight Opacity',
    textDecoration: 'Text Decoration',
    underline: 'Underline',
    strikethrough: 'Strikethrough',
    italic: 'Italic',
    bold: 'Bold',
    shape: 'Shape',
    moreOptions: 'More Options',
    patternColor: 'Pattern Color',
    patternSize: 'Pattern Size',
    applyPattern: 'Apply Pattern',
    glassLight: 'Glass Light',
    default: 'Default',
    filterIntensity: 'Filter Intensity',
    glassBlack: 'Glass Black',
    outline: 'Outline',
    shadowOptions: 'Shadow Options',
    shadowNone: 'None',
    shadowSmall: 'Small',
    shadowMedium: 'Medium',
    shadowLarge: 'Large',
    shadowXLarge: 'X-Large',
    shadowInner: 'Inner',
    shadowSoft: 'Soft',
    shadowHard: 'Hard',
    shadowOffset: 'Offset',
    shadowSpread: 'Spread',
    customShadow: 'Custom Shadow',
    // Image filters
    filters: {
      Binarize: 'Binarize',
      Brightness: 'Brightness',
      Gamma: 'Gamma',
      Sharpen: 'Sharpen',
      Solarize: 'Solarize',
      GrayScale: 'Grayscale',
      GaussianBlur: 'Gaussian Blur',
    },
    rembg: 'Remove Background',
    removeBackground: 'Remove Background',
    removeBackgroundModels: {
      label: 'Model',
      general: 'General',
      precise: 'Precise',
    },
    tools: {
      undo: 'Undo',
      redo: 'Redo',
      showPages: 'Show pages',
      selectionMode: 'Selection Mode',
      handTool: 'Hand tool',
      defaultSelectionMode: 'Select',
      deepSelectionMode: 'Deep Select',
      zoomToFit: 'zoom To Fit',
      zoomIn: 'zoom In',
      zoomOut: 'zoom Out',
      addText: 'Add Text',
      addImage: 'Add Image',
      addShape: 'Add Shape',
      gptImage2GuideTitle: 'GPT image 2.0 is here',
      gptImage2GuideDesc: 'Top-tier asset generation — now covers posters and beyond.',
      addRectangle: 'Add Rectangle',
      addCircle: 'Add Circle',
      addIcon: 'Add Icon',
      addQRCode: 'Add QR Code',
      text: 'Text',
      image: 'Image',
      shape: 'Shape',
      rectangle: 'Rectangle',
      circle: 'Circle',
      icon: 'Icon',
      qrCode: 'QR Code',
      dragAndDrop: 'Drag and drop to canvas',
      clickToAdd: 'Click to add',
    },
    inserts: {
      shapes: 'Shapes',
      badges: 'Badges',
      text: 'Text',
      effects: 'Effects',
      lines: 'Lines',
      filled: 'Filled',
      outline: 'Outline',
      soft: 'Soft',
      rectangle: 'Rectangle',
      roundedRectangle: 'Rounded',
      pill: 'Pill',
      circle: 'Circle',
      oval: 'Oval',
      triangle: 'Triangle',
      star: 'Star',
      heart: 'Heart',
      cross: 'Cross',
      hexagon: 'Hexagon',
      pentagon: 'Pentagon',
      octagon: 'Octagon',
      diamond: 'Diamond',
      arrow: 'Arrow',
      tag: 'Tag',
      glassCard: 'Glass',
      outlineCard: 'Outline card',
      line: 'Line',
      lineThin: 'Thin line',
      lineBold: 'Bold line',
      image: 'Image',
      icon: 'Icon',
      qrcode: 'QR Code',
      display: 'Display',
      heading1: 'Heading 1',
      heading2: 'Heading 2',
      heading3: 'Heading 3',
      heading4: 'Heading 4',
      subtitle: 'Subtitle',
      bodyText: 'Body Text',
      paragraph: 'Paragraph',
      label: 'Label',
      caption: 'Caption',
      quote: 'Quote',
      gradientText: 'Gradient text',
      stroke: 'Stroke',
      outlineBold: 'Bold outline',
      neon: 'Neon',
      bevel: 'Bevel',
      innerGlow: 'Inner glow',
      emboss: 'Emboss',
      raylight: 'Raylight',
      fill: 'Fill',
      longShadow: 'Long shadow',
      badgeSolid: 'Badge',
      badgeSoft: 'Soft badge',
      badgeGradient: 'Gradient badge',
      badgeOutline: 'Outline badge',
      badgeGlass: 'Glass badge',
      badgeTag: 'Tag',
      badgeSale: 'Sale',
      tipClickOrDrag: 'Tip: click to insert, or drag onto the canvas.',
      lastUsed: 'Last',
      radiusTop: 'Top rounded',
      radiusBottom: 'Bottom rounded',
      radiusLeft: 'Left rounded',
      radiusRight: 'Right rounded',
      radiusDiagonalA: 'Diagonal',
      radiusDiagonalB: 'Diagonal (reverse)',
      radiusSingleCorner: 'One corner',
      gradientCardSunset: 'Sunset',
      gradientCardAurora: 'Aurora',
      gradientCardOcean: 'Ocean',
      upload: 'Upload',
      urlUpload: 'Upload via URL',
      or: 'or',
      imageUrl: 'Image URL',
      imageUrlPlaceholder: 'https://example.com/image.jpg',
      add: 'Add',
    },

    // Shadow options
    shadowCustom: 'Custom',
    shadowColor: 'Shadow Color',
    shadowBlur: 'Shadow Blur',
    shadowDistance: 'Shadow Distance',
    shadowAngle: 'Shadow Angle',

    // Image styles
    imageStyleDefault: 'Default',
    imageStyleGlassLight: 'Glass Light',
    imageStyleGlassBlack: 'Glass Black',
    imageStyleOutline: 'Outline',

    // Filter effects
    filterNone: 'None',
    filterVintage: 'Vintage',
    filterSepia: 'Sepia',
    filterDramatic: 'Dramatic',
    filterSharp: 'Sharp',
    filterCool: 'Cool',
    filterWarm: 'Warm',
    publish: {
      title: 'Share design',
      publishDescription: 'Share your design with the community',
      selectPage: 'Select Page',
      choosePage: 'Choose a page',
      page: 'Page',
      name: 'Name',
      description: 'Description',
      tags: 'Tags',
      tagPlaceholder: 'Enter tag',
      publish: 'Publish',
      update: 'Update',
      uploading: 'Uploading...',
      success: {
        title: 'Template Shared Successfully',
        description: 'Your template is now available in the community gallery',
      },
      error: {
        title: 'Unable to Share Template',
        description: 'Something went wrong. Please check your connection and try again.',
      },
      projectPermissions: 'Access level',
      publishTemplate: 'Publish template',
      visibility: 'Project Visibility',
      public: 'Public',
      publicDescription: 'Anyone can view this project in the community gallery',
      private: 'Private',
      privateDescription: 'Only you and people you share with can access this project',
      updating: 'Updating...',
      updatePermissions: 'Update Permissions',
      shareLink: 'Share Link',
      copyLink: 'Copy Link',
      linkCopied: 'Link copied to clipboard!',
      embedCode: 'Embed Code',
      copyCode: 'Copy Code',
      codeCopied: 'Code copied to clipboard!',
      shareUrl: 'Share URL',
      shareUrlPublicDesc: 'Anyone with this link can view your project',
      shareUrlPrivateDesc: 'Only you can access this project with this link',
      publicAccess: 'Public Access',
      privateAccess: 'Private Access',
      urlWillAppearHere: 'URL will appear here',
      urlReady: 'URL is ready',
      urlNotReady: 'URL not available',
      copy: 'Copy',
      copySuccess: 'Copied!',
      copySuccessDesc: 'Share URL has been copied to clipboard',
      copyFailed: 'Copy failed',
      copyFailedDesc: 'Failed to copy URL to clipboard',
      accessPassword: 'Access Password',
      accessPasswordPlaceholder: 'Set access password (optional)',
      accessPasswordDesc: 'Visitors will need this password to view your project',
      share: 'Share',
      accessLevel: 'Access Level',
      anyoneWithLink: 'Anyone with the link',
      canView: 'Can view',
    },
    blendPanel: {
      blendMode: 'Blend Mode',
      mixed: 'Mixed',
      normal: 'Normal',
      multiply: 'Multiply',
      screen: 'Screen',
      overlay: 'Overlay',
      darken: 'Darken',
      lighten: 'Lighten',
      colorDodge: 'Color Dodge',
      colorBurn: 'Color Burn',
      hardLight: 'Hard Light',
      softLight: 'Soft Light',
      difference: 'Difference',
      exclusion: 'Exclusion',
      hue: 'Hue',
      saturation: 'Saturation',
      color: 'Color',
      luminosity: 'Luminosity',
      desc: {
        normal: 'No blending, default mode.',
        multiply: 'Darkens by multiplying base and blend colors.',
        screen: 'Lightens by inverting, multiplying, and inverting again.',
        overlay: 'Combines multiply and screen for contrast.',
        darken: 'Keeps the darker color.',
        lighten: 'Keeps the lighter color.',
        colorDodge: 'Brightens the base color to reflect the blend color.',
        colorBurn: 'Darkens the base color to reflect the blend color.',
        hardLight: 'Combines multiply and screen based on blend color.',
        softLight: 'Soft subtle lighting effect.',
        difference: 'Subtracts darker from lighter color.',
        exclusion: 'Similar to difference but with lower contrast.',
        hue: 'Uses the hue of the blend color.',
        saturation: 'Uses the saturation of the blend color.',
        color: 'Uses the color of the blend color.',
        luminosity: 'Uses the luminosity of the blend color.',
      },
    },
    maskPanel: {
      mask: 'Mask',
      mixed: 'Mixed',
      none: 'None',
      alphaClip: 'Alpha Mask - Clip',
      alphaIntersect: 'Alpha Mask - Intersect',
      desc: {
        none: 'No mask applied.',
        alphaClip: 'Uses alpha channel to clip content.',
        alphaIntersect: 'Uses alpha channel to intersect content.',
      },
    },
    imageFitPanel: {
      objectFit: 'Object Fit',
      mixed: 'Mixed',
      cover: 'Cover',
      contain: 'Contain',
      fill: 'Fill',
      none: 'None',
      scaleDown: 'Scale Down',
      desc: {
        cover: 'Scale to fill, may crop',
        contain: 'Scale to fit completely',
        fill: 'Stretch to fill exactly',
        none: 'Original size, no scaling',
        scaleDown: 'Scale down to fit if larger',
      },
    },
    skewPanel: {
      skew: 'Skew',
      parameters: 'Parameters',
      presets: 'Presets',
      none: 'None',
      mixed: 'Mixed',
      x: 'Skew X',
      y: 'Skew Y',
      preview: 'Preview',
      applyPreset: 'Apply Preset',
      reset: 'Reset',
      custom: 'Custom',
      preset: 'Preset',
      // Preset names (should match SKEW_PRESETS keys)
      skewPreset: {
        none: 'None',
        italic: 'Italic',
        'slight-left': 'Slight Left',
        'slight-right': 'Slight Right',
        'slight-up': 'Slight Up',
        'slight-down': 'Slight Down',
        'diagonal-left': 'Diagonal Left',
        'diagonal-right': 'Diagonal Right',
        'extreme-left': 'Extreme Left',
        'extreme-right': 'Extreme Right',
        'perspective-left': 'Perspective Left',
        'perspective-right': 'Perspective Right',
        custom: 'Custom',
      },
      // Preset descriptions (for tooltips or accessibility)
      skewPresetDesc: {
        none: 'No skew applied.',
        italic: 'Classic italic style skew.',
        'slight-left': 'Slight skew to the left.',
        'slight-right': 'Slight skew to the right.',
        'slight-up': 'Slight skew upwards.',
        'slight-down': 'Slight skew downwards.',
        'diagonal-left': 'Diagonal skew to the top left.',
        'diagonal-right': 'Diagonal skew to the bottom right.',
        'extreme-left': 'Strong skew to the left.',
        'extreme-right': 'Strong skew to the right.',
        'perspective-left': 'Perspective skew to the left.',
        'perspective-right': 'Perspective skew to the right.',
        custom: 'Custom skew values.',
      },
      // UI labels and tooltips
      selectPreset: 'Select a preset',
      selected: 'Selected',
      setCustom: 'Set custom skew values',
      resetToNone: 'Reset skew to none',
      current: 'Current',
      skewPreview: 'Skew preview',
      skewXLabel: 'Horizontal skew (X axis)',
      skewYLabel: 'Vertical skew (Y axis)',
      skewXDesc: 'Adjust the horizontal skew angle.',
      skewYDesc: 'Adjust the vertical skew angle.',
      parametersTab: 'Adjust skew parameters',
      presetsTab: 'Choose from preset styles',
    },
  },
  radiusMenu: {
    mixed: 'Mixed',
    linked: 'Linked',
    presets: 'Presets',
    topLeftShort: 'TL',
    topRightShort: 'TR',
    bottomLeftShort: 'BL',
    bottomRightShort: 'BR',
  },
  actions: {
    Undo: 'Undo',
    Redo: 'Redo',
    Duplicate: 'Duplicate',
    Paste: 'Paste',
    Cut: 'Cut',
    Delete: 'Delete',
    Layer: 'Layer',
    Alignment: 'Alignment',
    SelectLayer: 'Select layer',
    CopyAs: 'Copy as',
    CopyasPNG: 'Copy as PNG',
    CopyasWebp: 'Copy as Webp',
    CopyasHTML: 'Copy as HTML',
    CopyAsWechatPostSVGCode: 'Copy as Wechat Post',
    CopyAsRawHTML: 'Copy as Inline HTML',
    ImageCrop: 'Image Crop',
    Forward: 'Forward',
    Backward: 'Backward',
    BringToFront: 'Bring to Front',
    SendToBack: 'Send to Back',
    alignToPageTop: 'Align to Page Top',
    alignToPageLeft: 'Align to Page Left',
    alignToPageMiddle: 'Align Vertical Center on Page',
    alignToPageCenter: 'Align Horizontal Center on Page',
    alignToPageBottom: 'Align to Page Bottom',
    alignToPageRight: 'Align to Page Right',
    alignToTop: 'Align to Top',
    alignToBottom: 'Align to Bottom',
    alignToMiddle: 'Align to Middle',
    alignToCenter: 'Align to Center',
    alignToLeft: 'Align to Left',
    alignToRight: 'Align to Right',
    AddToLibrary: 'Add to Library',
    BatchCreate: 'Batch Create',
    CopyTextStyle: 'Copy style',
    PasteTextStyle: 'Paste style',
    Advanced: 'Advanced',
    selectAll: 'Select all',
    SplitFrameSections: 'Split Frame Sections',
    MergeFrames: 'Merge frames',
    flipHorizontal: 'Flip Horizontal',
    flipVertical: 'Flip Vertical',
    Distribute: 'Distribute',
    DistributeHorizontal: 'Distribute Horizontal',
    DistributeVertical: 'Distribute Vertical',
    EqualSpacingHorizontal: 'Equal Spacing Horizontal',
    EqualSpacingVertical: 'Equal Spacing Vertical',
  },
  landing: {
    seoTitle: 'Veeso AI - Simple, Fast, and Fun AI Graphic Design for Everyone',
    seoDescription:
      'Ideas into designs in 1 click! Veeso AI creates social & marketing posts instantly. No software to learn – Try free!',
    seoKeywords:
      'veeso, veeso ai, AI poster, Free design tool no download,LinkedIn posts,AI poster generator, free design tools,easy design',
    featuresSubtitle: 'Powerful Features',
    featuresDescription:
      'How to use ai for graphic design? Veeso ai offers a suite of powerful AI-powered tools to streamline your design workflow and help you create stunning visuals with ease.',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    recommendations: 'Recommendations',
    refresh: 'Refresh',
    productFeatures: 'Features',
    useCases: 'Use Cases',
    pricing: 'Pricing',
    products: 'Products',
    templates: 'Templates',
    getStarted: 'Get Started',
    tryForFree: 'Try For Free',
    heroTitle: `Your ideas. Designed in seconds.`,
    heroSubtitle: 'Pro Graphics Design Made Simple with Veeso AI',
    learnMore: 'Learn More',
    browserAllTemplates: 'Browse All Templates',
    startUsing: 'Start Creating Free',
    whatIsSeede: 'What is Veeso AI?',
    richTemplates: 'Rich AI Creative Templates',
    whyNotTry: 'Why not give it a try?',
    whatIsSeedeDesc: `Veeso ai is an AI-powered design tool that helps you create stunning visuals in just one minute—no design skills needed. Perfect for social media content, marketing materials, and creative projects.`,
    mainFeatures: 'Core Features',
    createWithAI: 'Create with Veeso AI, freely and instantly.',
    creationFromCommunity: 'Creation from community',
    creatorCount: 'Veeso AI — Everyone can create professional designs and layouts with one click.',
    coreFeaturesBadge: 'Core features',
    coreFeature1: 'Keep Your Words and Structure Exactly as Intended',
    coreFeature1Desc:
      'Your content stays intact, every word, every structure, exactly as you intended.',
    coreFeature2: 'Auto-layout That Makes Any Content Look Designed',
    coreFeature2Desc: 'Transform any content into polished designs with intelligent auto-layout.',
    coreFeature3: 'Works Naturally with Any Language or Format',
    coreFeature3Desc: 'Seamlessly handle any language, document format, or content type.',
    freeTools: 'Free Image Processing Tools',
    ctaTitle: 'Experience Veeso AI Design Today',
    ctaNow: 'Try Veeso AI for free now.',
    ctaDesc:
      'Turn any content into polished, ready-to-use designs — while keeping every word, structure, and meaning.',
    startCreating: 'Start Creating',
    footerDesc:
      'Veeso AI is an AI design tool that helps to turn you ideas into stunning visuals in seconds. No design skills needed. Perfect for social media content, marketing materials, and creative projects.',
    product: 'Products',
    features: 'Features',
    cases: 'Use Cases',
    resources: 'Resources',
    helpCenter: 'Help Center',
    docs: 'Documentation',
    company: 'Company',
    team: 'Team',
    affiliates: 'Affiliates',
    aboutUs: 'About',
    joinUs: 'Join us',
    blog: 'Blog',
    contactUs: 'Contact',
    dashboard: 'Dashboard',
    tools: 'Tools',
    howItWorks: 'How It Works',
    howYouCanDesign: 'How you can design like a pro',
    faq: {
      title: 'FAQ',
      items: {
        howIsVeesoDifferent: {
          question: 'How is Veeso different from AI image generation tools?',
          answerParagraph1:
            'Most AI design tools generate images. Veeso generates complete, editable design files with:',
          listItems: [
            'Editable text blocks',
            'Logical content hierarchy',
            'Multi-page formatting',
            'Export-ready designs',
          ],
        },
        whoIsVeesoBuiltFor: {
          question: 'Who is Veeso AI built for?',
          answer:
            'Veeso is designed for creators, solo founders, marketers, and small to mid-sized businesses (SMBs) who need professional designs without hiring a designer or spending hours formatting content.',
        },
        whatKindsOfFiles: {
          question: 'What kinds of files can I create with Veeso?',
          answer:
            'You can create presentations, newsletters, social media posts, reports, one-pagers, brochures, and long-form content pages, etc. All are generated from your original text input.',
        },
        canIEditDesigns: {
          question: 'Can I edit the designs after they are generated?',
          answer:
            'Yes. Every output is fully editable. You can adjust text, layout, images, structure, and branding elements directly inside Veeso before exporting.',
        },
        ownBranding: {
          question: 'Can I use my own branding in Veeso?',
          answer:
            'Yes. You can apply your own brand styles, colors, and layouts to ensure consistent visual identity across all your materials.',
        },
        imageEditing: {
          question: 'Does Veeso include image editing?',
          answer:
            'Yes. In addition to generating structured layouts, Veeso allows you to edit and refine images within your designs, keeping everything in one workflow.',
        },
        howFast: {
          question: 'How fast can I create a design using Veeso?',
          answer:
            'Most users can turn raw content into a polished, export-ready design in minutes. Veeso reduces formatting and layout time by automating structure and visual hierarchy.',
        },
        contentFirstPhilosophy: {
          question: 'What is the core Content-First philosophy?',
          answerParagraph1:
            'Content-First means design starts from your information — not from templates or images. Instead of searching for fixed templates and forcing content into them, Veeso analyzes your raw text and automatically structures it into clear sections, headlines, and visual hierarchy. We believe your content should not be locked inside:',
          listItems: ['A specific slide layout', 'A rigid template', 'A single platform format'],
          answerParagraph2:
            'Veeso decouples content from format, allowing the same input to be transformed into:',
          listItems2: ['Presentations', 'Newsletters', 'Social posts', 'Reports', 'Brochures'],
          answerParagraph3: 'One source of content. Multiple design outputs.',
        },
        whatIsContent: {
          question: 'What is considered "content" in Veeso?',
          answer:
            'In Veeso, content refers to any material that communicates a message — including draft text, bullet points, blog articles, investor notes, product descriptions, social copy, reports, images, references, etc. Anything that conveys information can be transformed into structured design output.',
        },
        magicTemplate: {
          question: 'What is the Magic Template?',
          answerParagraph1:
            "Magic Template is Veeso AI's style control system. Instead of fixed, static templates, Magic Template dynamically adapts layout structure based on:",
          listItems: [
            'Content length',
            'Information hierarchy',
            'Tone and intent',
            'Output format',
          ],
          answerParagraph2:
            'It intelligently arranges sections, headlines, visuals, and spacing to create professional design files without manual formatting.',
        },
        isFreeToUse: {
          question: 'Is Veeso free to use?',
          answer:
            'Yes. Veeso offers a free plan that includes daily refresh credits so you can explore and create without any upfront cost. You can upgrade to paid plans for more credits and advanced features.',
        },
      },
    },
    createInFourSteps: 'Create stunning designs in just four simple steps',
    steps: {
      step1Title: 'Describe',
      step1Desc: 'Describe what you want to design.',
      step2Title: 'Create',
      step2Desc: 'Veeso AI creates your first version.',
      step3Title: 'Improve',
      step3Desc: 'Improve it by asking for changes.',
      step4Title: 'Export',
      step4Desc: 'Export your design and publish or print.',
    },
    designSteps: {
      idea: {
        title: 'Just an idea',
        desc: 'Just start from any ideas in your mind. No matter how big or small it is.',
      },
      generate: {
        title: 'Generate',
        desc: "Type what's in your mind and just hit generate button",
      },
      seconds: {
        title: 'Just in seconds',
        desc: 'Wait for seconds, and the magic happens, you get a design like a pro.',
      },
      export: {
        title: 'Improve or Export',
        desc: 'Chatting while brainstorming with veeso AI or export to use directly.',
      },
    },
    socialProof: '{{user}} creators are designing with Veeso',
    // 新增翻译
    featureDesc: 'AI-powered professional design generation tool',
    featuresIntroStrong: 'Seede AI is an AI design tool',
    featuresIntroLight:
      ' that helps to turn your ideas into stunning polished designs in seconds for any content, any language, any format.',
    feature1: 'Complete the design <br/> in one minute',
    feature1Desc:
      'No design experience needed, create marketing designs for any industry in 1 minute',
    feature2: 'Generate designs <br/> from images and documents',
    feature2Desc: 'Upload images or documents directly, AI generates designs instantly',
    feature3: 'Seede AI at anytime, anywhere',
    feature3Desc: 'Generate elements with layered editing, AI-assisted partial optimization',
    tool1: 'Landscape to Portrait Video Tool',
    tool1Desc: 'Convert landscape videos to portrait format for social media',
    tool2: 'Background Removal',
    tool2Desc: 'One-click background removal',
    tool3: 'Magic Eraser',
    tool3Desc: 'Easily remove unwanted elements',
    tool4: 'Image Generation',
    tool4Desc: 'Generate images from text',
    tool5: 'Image Upscaling',
    tool5Desc: 'Enhance image resolution',
  },
  products: {
    auto: {
      title: 'Auto',
      name: 'Auto',
      description: 'Auto detect scene from prompt',
    },
    posterGenerator: {
      title: 'Poster Generator',
      name: 'Poster',
      description: 'Quickly create professional posters',
    },
    invitationGenerator: {
      title: 'Invitation Generator',
      name: 'Invitation Card',
      description: 'Create beautiful digital invitations',
    },
    infographicGenerator: {
      title: 'Infographic Generator',
      name: 'Infographic',
      description: 'Create visually appealing data-driven infographics',
    },
    newsletterGenerator: {
      title: 'Newsletter Generator',
      name: 'Newsletter',
      description: 'Create professional newsletter templates',
    },
    flashcardGenerator: {
      title: 'Flashcard Generator',
      name: 'Flashcard',
      description: 'Generate study flashcards',
    },
    socialMediaGenerator: {
      title: 'Social Media Post',
      name: 'Social Media Post',
      description: 'Create engaging social media content with Veeso AI',
    },
    wechatCoverGenerator: {
      title: 'WeChat Cover Generator',
      name: 'WeChat Cover',
      description: 'Create eye-catching WeChat article covers with Veeso AI',
    },
    eventPromotionGenerator: {
      title: 'Event Promotion Generator',
      name: 'Event Promotion',
      description: 'Design eye-catching event promotions and ads',
    },
    albumCoverGenerator: {
      title: 'Album Cover Generator',
      name: 'Album Cover',
      description: 'Design stunning music album covers with Veeso AI',
    },
    signageGenerator: {
      title: 'Signage Generator',
      name: 'Signage',
      description: 'Create professional signage and display materials',
    },
    menuGenerator: {
      title: 'Restaurant Menu Generator',
      name: 'Restaurant Menu',
      description: 'Create visually stunning restaurant or cafe menus using Veeso AI',
    },
    flyerGenerator: {
      title: 'Flyer Generator',
      name: 'Flyer',
      description:
        'Whether for lost pets, gallery openings, or fundraising events, create beautiful flyers with our easy-to-use tool',
    },
    scrollytellingGenerator: {
      title: 'Long-Form Content Generator',
      name: 'Long-Form Content',
      description:
        'Create visually compelling long-form content that perfectly showcases data stories and memorable moments, making your social media shares more engaging',
    },
    carouselGenerator: {
      title: 'Carousel Generator',
      name: 'Carousel',
      description: 'Create engaging multi-page carousels for social media with Seede AI',
    },
    presentationGenerator: {
      title: 'Presentation Generator',
      name: 'Presentation',
      description: 'Create professional presentation slides with Veeso AI',
    },
    multiPageBrochureGenerator: {
      title: 'Multi-Page Brochure Generator',
      name: 'Multi-Page Brochure',
      description: 'Create professional multi-page brochures with Veeso AI',
    },
    diagramGenerator: {
      title: 'Diagram Generator',
      name: 'Diagram',
      description: 'Create professional diagrams and flowcharts with Veeso AI',
    },
    pullUpBanner: {
      title: 'Pull Up Banner Generator',
      name: 'Pull Up Banner',
      description: 'Create professional pull up banners and standing displays with Veeso AI',
    },
  },
  workspace: {
    templatesGroup: 'Templates',
    promptLibrary: 'Prompt Library',
    featuredDesigns: 'Featured Designs',
    ai: 'Seede AI',
    create: 'Create',
    createTitle: 'What do you want to design now?',
    productBarCustomize: 'Customize',
    productBarPinnedTitle: 'Pinned cards',
    productBarPinnedDesc: 'Choose which design types stay in the horizontal list.',
    productBarResetAll: 'Show all',
    quickStart: 'Quick Start',
    morePromptTemplates: 'More prompt templates',
    allDesigns: 'Your designs',
    recentlyReviewed: 'Recently Reviewed',
    themes: 'Themes',
    trash: 'Trash',
    orders: 'Billing',
    libraries: 'Libraries',
    settings: 'Settings',
    tutorial: 'Tutorial',
    invite: 'Invite Friends',
    redeem: 'Redeem Code',
    newDesign: 'New Design',
    searchDesigns: 'Search designs...',
    sortBy: 'Sort by',
    all: 'All',
    recentlyViewed: 'Recently viewed',
    favorites: 'Favorites',
    recentlyUpdated: 'Recently Updated',
    oldestUpdated: 'Oldest Updated',
    recentlyCreated: 'Recently Created',
    oldestCreated: 'Oldest Created',
    noDesignsFound: 'No designs found',
    createFirstDesign: 'Create your first design to get started',
    loadingProjects: 'Loading projects...',
    deleteProject: 'Delete Project',
    confirmDelete: 'Are you sure you want to delete this project?',
    cancel: 'Cancel',
    delete: 'Delete',
    loadingMore: 'Loading more projects...',
    noProjectsFound: 'No projects found',
    createFirstProject: 'Create your first project to get started',
    newProject: 'New Project',
    searchProjects: 'Search projects...',
    loadingDesigns: 'Loading designs...',
    createNewDesign: 'Create New Design',
    welcomeTitle: 'Welcome to Your Workspace',
    welcomeDescription:
      'Start creating amazing designs with AI-powered tools. Your creativity has no limits.',
    createFirstDesignButton: 'Create Your First Design',
    recentWork: 'Recent Work',
    allProjects: 'All Projects',
    starredProjects: 'Starred Projects',
    viewAll: 'View All',
    emptyState: {
      title: 'No designs found',
      description: 'Create your first design to get started with AI-powered creativity',
      createButton: 'Create New Design',
    },
    filters: {
      all: 'All designs',
      recent: 'Recently viewed',
      favorites: 'Favorites',
      public: 'Public',
      private: 'Private',
    },
    sort: {
      recentlyUpdated: 'Recently Updated',
      oldestUpdated: 'Oldest Updated',
      recentlyCreated: 'Recently Created',
      oldestCreated: 'Oldest Created',
    },
    sorting: {
      recentlyUpdated: 'Recently Updated',
      oldestUpdated: 'Oldest Updated',
      recentlyCreated: 'Recently Created',
      oldestCreated: 'Oldest Created',
      alphabetical: 'Alphabetical',
      reverseAlphabetical: 'Reverse Alphabetical',
    },
    actions: {
      open: 'Open',
      view: 'View',
      duplicate: 'Duplicate',
      rename: 'Rename',
      delete: 'Delete',
      share: 'Share',
      star: 'Star',
      unstar: 'Remove Star',
      moveToTrash: 'Move to Trash',
      restore: 'Restore',
      permanentDelete: 'Delete Permanently',
    },
    noSearchResults: 'No search results',
    noFavorites: 'No favorites',
    noDesigns: 'No designs',
    noResultsFoundFor: 'No results found for {{query}}',
    noSearchResultsDescription:
      "We couldn't find any designs matching '{{query}}'. Try adjusting your search terms.",
    asset: {
      file: 'FILE',
      ai: 'AI',
      untitled: 'Untitled Asset',
    },
    assets: {
      failedToLoad: 'Failed to load assets',
    },
    clearSearch: 'Clear search',
    team: {
      createDialogTitleLine1: 'Create Seede workspace',
      createDialogTitleLine2: 'Design with your teammates and AI design in the future',
      createSubmit: 'Create',
    },
  },
  trash: {
    title: 'Trash',
    empty: {
      title: 'Trash is empty',
      description:
        "When you delete projects, they'll appear here for 30 days before being permanently removed.",
    },
    confirmPermanentDelete:
      'Are you sure you want to permanently delete this project? This action cannot be undone.',
    deletedAt: 'Deleted on',
    restoredSuccessfully: 'Project restored successfully',
    deletedPermanently: 'Project permanently deleted',
    failedToRestore: 'Failed to restore project',
    failedToDelete: 'Failed to permanently delete project',
    footer: {
      info: 'Items in trash are automatically deleted after 30 days',
    },
  },
  settings: {
    title: 'Settings',
    description: 'Manage your account and application preferences',
    tabs: {
      general: 'General',
      billing: 'Billing',
      subscription: 'Subscription',
      credits: 'Credits History',
      invite: 'Invite Friends',
      redeem: 'Redeem Code',
      trash: 'Trash',
      cookie: 'Cookie Preferences',
    },
    cookie: {
      cookieSection: 'Cookie Preferences',
      managePreferences: 'Manage Cookie Preferences',
      cookiePreferences: 'Cookie Preferences',
      manageCookieSettings: 'Manage your cookie settings and preferences',
    },
    profile: {
      title: 'Profile',
      subtitle: 'Manage your account settings and preferences',
      profileSection: 'Profile',
      name: 'User name',
      email: 'Email',
      phone: 'Mobile',
      noEmail: 'Not set',
      noPhone: 'Not set',
      verified: 'Verified',
      cannotChange: 'Cannot change',
      avatar: 'Avatar',
      enterName: 'Enter your name',
      enterEmail: 'Enter your email',
      enterPhone: 'Enter your mobile number',
      save: 'Save',
      saving: 'Saving...',
      saveProfile: 'Save Profile',
      profileUpdated: 'Profile updated',
      profileUpdatedDesc: 'Your profile has been saved successfully',
      saveFailed: 'Save failed',
      saveFailedDesc: 'Failed to save your profile. Please try again.',
      changeAvatar: 'Change',
      uploadingAvatar: 'Uploading...',
      avatarUploadError: 'Failed to upload',
      avatarRequirements: 'JPG, PNG or WebP. Max 5MB.',
      avatarError: 'Avatar Error',
      avatarSizeError: 'Avatar image must be less than 5MB',
      uploadFailed: 'Upload Failed',
      uploadFailedDesc: 'Failed to upload avatar. Please try again.',
      avatarUpdated: 'Avatar Updated',
      avatarUpdatedDesc: 'Your avatar has been updated successfully',
      bindPhone: 'Bind',
      bindPhoneTitle: 'Bind Mobile Number',
      bindPhoneDesc: 'Enter your mobile number to receive a verification code',
      countryCode: 'Country Code',
      phoneNumber: 'Mobile Number',
      enterPhoneNumber: 'Enter mobile number',
      verificationCode: 'Verification Code',
      enterCode: 'Enter code',
      sendCode: 'Send',
      resend: 'Resend',
      sending: 'Sending...',
      binding: 'Binding...',
      confirm: 'Confirm',
      phoneRequired: 'Mobile number is required',
      invalidPhone: 'Invalid mobile number format',
      invalidPhoneDesc: 'Please enter a valid mobile number',
      codeSent: 'Verification code sent',
      codeSentDesc: 'Please check your mobile for the code',
      sendCodeFailed: 'Failed to send verification code',
      sendCodeFailedDesc: 'Please try again',
      fillAllFields: 'Please fill all fields',
      phoneBindSuccess: 'Mobile number bound successfully',
      bindPhoneFailed: 'Failed to bind mobile number',
      bindPhoneFailedDesc: 'Please try again',
      countries: {
        china: 'China',
        usa: 'USA',
        uk: 'UK',
        japan: 'Japan',
        korea: 'Korea',
      },
    },
    security: {
      title: 'Security',
      password: 'Password',
      passwordDesc: '••••••••',
      changePassword: 'Change',
      deleteAccount: 'Delete Account',
      deleteAccountDesc: 'Permanently delete your account and all data',
      delete: 'Delete',
      deleteAccountWarning:
        'This action cannot be undone. All your data will be permanently deleted.',
      deleteAccountConfirm: 'Please type your email to confirm:',
      confirmEmail: 'Email Confirmation',
      enterEmail: 'Enter your email',
      enterPassword: 'Enter your password',
      deleting: 'Deleting...',
    },
    notifications: {
      title: 'Notifications',
      email: 'Email Notifications',
      emailDesc: 'Receive notifications via email',
      desktop: 'Desktop Notifications',
      desktopDesc: 'Show desktop notifications',
      marketing: 'Marketing Emails',
      marketingDesc: 'Receive product updates and tips',
    },
    preferences: {
      title: 'Preferences',
      language: 'Language',
      theme: 'Theme',
      savePreferences: 'Save Preferences',
      settingsSaved: 'Settings saved',
      settingsSavedDesc: 'Your preferences have been updated successfully',
      saveFailedDesc: 'Failed to save your settings. Please try again.',
      autoSaved: 'Auto saved',
      languageChanged: 'Language Changed',
      languageChangedDesc: 'Language has been updated',
    },
    account: {
      title: 'Account',
      changePassword: 'Change Password',
      changePasswordDesc: 'Update your account password',
      deleteAccount: 'Delete Account',
      deleteAccountDesc: 'Permanently delete your account',
    },
  },
  pricing: {
    seoTitle: 'Pricing and Plans | Veeso AI',
    seoDescription:
      'Explore Veeso pricing plans for individuals and small businesses. Create presentations, newsletters, and social designs faster, from content to finished files',
    seoKeywords:
      'Veeso AI pricing, pricing plans, design tool pricing, content-first design, AI design, design efficiency',
    subtitle: 'we are working on the pricing plan, please check back later.',
    designBeginner: 'Free',
    loveDesign: 'Creator',
    designExpert: 'Business',
    subscribe: 'Try for Free',
    popular: 'Popular Choice',
    tryNow: 'Buy Now',
    page: {
      title: 'Pricing',
      subtitle: 'Start for free. Unlock more capacity as your business grows.',
      billingPeriod: {
        monthly: 'Monthly',
        yearly: 'Yearly',
        saveUpTo: 'Save up to 17%',
      },
      plans: {
        free: {
          name: 'Free',
          description: 'Just try it',
          cta: 'Try for Free',
          features: {
            dailyCredits: '{{count}} refresh credits everyday',
            bonusCredits: '{{count}} bonus credits for new users',
            monthlyCredits: '{{count}} monthly credits',
            fullFeaturedEditor: 'Full-featured editor',
            textEdit: 'Text: freely edit, adjust, and delete',
            imagesEdit: 'Images: upload, replace, and use AI editing (credits required)',
            multiLanguage: 'Create designs in 100+ languages',
            communityTemplates: 'Community Templates',
            remixOnly: 'Remix only (credits required)',
            standardExport: 'Standard export (with Veeso badge)',
            exportFormats: 'PNG / PDF',
            standardQuality: 'Standard-quality AI-generated asset images',
            uploadLimit: 'Upload up to 5 asset images per generation (Beta)',
            multiPage: 'Multi-page generation (up to 5 pages)',
            communitySupport: 'Community Support',
          },
        },
        pro: {
          name: 'Pro',
          description: 'Design like a pro',
          cta: 'Get Started',
          monthlyCreditsLabel: 'Monthly Credits',
          monthlyCreditsSuffix: 'credits/month',
          dynamicMonthlyCredits: '{{count}} monthly credits',
          features: {
            dailyCredits: '{{count}} refresh credits everyday',
            selectableCredits: 'Selectable monthly credits',
            everythingInFree: 'Everything in Free, plus:',
            removeBadge: 'Remove Veeso badge',
            commercialLicense: 'Commercial license',
            communityTemplates: 'Community Templates',
            remix: 'Remix (credits required)',
            edit: 'Edit (free)',
            advancedExport: 'Advanced export',
            exportFormats: 'PPT / SVG / HTML',
            higherQuality: 'Higher-quality AI-generated asset images',
            uploadLimit: 'Upload up to 20 asset images per generation (Beta)',
            multiPage: 'Multi-page generation (up to 20 pages)',
            dedicatedSupport: 'Priority support',
            // 以下两条由 pricing.vue 引用，但原 en.js 漏写过，补全：
            creditRollovers: 'Unused credits roll over to next month',
            earlyAccess: 'Early access to beta features',
          },
        },
        ultra: {
          name: 'Ultra',
          description: 'Bring your design to world at scale',
          cta: 'Get Started',
          features: {
            // ultra 卡复用 includesPro 文案，统一在 features 内提供，避免模板重复判断
            everythingInPro: 'Everything in Pro, plus:',
            apiAccess: 'API access',
            brandKit: 'Brand kit management',
            unlimitedStorage: 'Unlimited cloud storage',
            customContracts: 'Custom contracts & invoicing',
            earlyAccess: 'Early access to beta features',
            prioritySupport: 'Dedicated support',
          },
        },
      },
      price: {
        free: '$0',
        perMonth: 'mo.',
        perYear: '/ year',
        savePerYear: 'Save ${{amount}} yearly',
      },
      includes: 'Includes:',
      includesPro: 'Everything in Free, plus:',
      includesUltra: 'Everything in Pro, plus:',
      tiers: {
        title: 'Pro Plan Pricing Tiers',
        table: {
          monthlyCredits: 'Monthly Credits',
          pricePerCredit: 'Price per Credit',
          monthlyPrice: 'Monthly Price',
          yearlyPrice: 'Yearly Price',
          yearlyDiscount: 'Yearly Discount',
          youSave: 'You Save',
          discountOff: '({{percent}}% off)',
        },
      },
      faq: {
        title: 'FAQ',
        subtitle: "Answers about Veeso AI's pricing and usage",
        items: {
          whatIsVeesoAI: {
            question: 'What is Veeso AI?',
            answerParagraph1:
              'For SMB owners, marketers, operations, founders with limited design resources and tight deadlines, producing professional visuals is slow and expensive.',
            answerParagraph2:
              'Veeso is a content-first design tool that turns copywriting into clear, polished visuals in one click, one minute, no design skills required.',
            answerParagraph3:
              'Just paste your content or text, and instantly generate beautiful designs for',
            products: [
              'Social Media Post',
              'Poster',
              'Presentation',
              'Carousel',
              'Multi-Page Brochure',
              'Long-Form Content',
              'Restaurant Menu',
              'Invitation Card',
              'Pull Up Banner',
              'Flyer',
              'Album Cover',
            ],
          },
          whatIsCredit: {
            question: 'What is a credit?',
            answerParagraph1:
              'Credits are used for all AI-powered features, such as generating new designs, chatting with AI to modify your design, removing backgrounds, and other AI tools.',
            answerParagraph2:
              'Manual editing (like moving text, changing colors, or resizing elements) and exporting your designs do NOT use any credits.',
            answerParagraph3: 'Daily credits refresh every day at midnight UTC.',
          },
          getMoreCredits: {
            question: 'How can I get more credits?',
            answerParagraph1: 'There are 5 main ways to get credits:',
            answerParagraph2: '• New users: Receive 300 bonus credits as a welcome gift',
            answerParagraph3:
              '• Daily login: Claim 100 credits every day (refreshes at midnight UTC)',
            answerParagraph4: '• Invite friends: Both you and your friend get 200 credits',
            answerParagraph5:
              '• Upgrade your plan: Subscribe to a paid plan to get monthly credits',
            answerParagraph6:
              '• Special events: Participate in promotional events to earn bonus credits',
          },
          creditExpiry: {
            question: 'Do credits expire?',
            answerParagraph1: 'Yes. Different credits have different validity periods:',
            listItems: {
              dailyCredits: 'Daily credits: Refresh every day at midnight UTC (do not accumulate)',
              newUserBonus: 'New user bonus: Valid for 1 year from registration',
              referralCredits: 'Referral credits: Valid for 30 days from when you receive them',
              promotionCredits:
                'Promotion credits: Validity period varies by event, up to 1 year maximum',
              monthlySubscription:
                'Monthly subscription credits: Valid until the end of your billing cycle (no rollover)',
              yearlySubscription:
                'Yearly subscription credits: Valid until the end of your billing cycle (no rollover)',
            },
          },
          creditConsumption: {
            question: 'How are credits consumed?',
            answerParagraph1:
              'Credits are used in the following order (highest to lowest priority):',
            listItems: {
              dailyCredits: 'Daily credits',
              promotionCredits: 'Promotion credits',
              referralCredits: 'Referral credits',
              newUserBonus: 'New user bonus',
              subscriptionCredits: 'Subscription credits',
            },
          },
          inviteFriends: {
            question: 'How do I invite friends and get credit rewards?',
            answerParagraph1: 'Go to:',
            link: {
              text: 'Invite Friends',
              url: '/invite',
            },
            answerParagraph2: 'Share your invite link. Both you and your friend get 200 credits.',
          },
          designOwnership: {
            question: 'Who owns the designs I create on Veeso AI?',
            answer:
              'All your design source files and projects belong 100% to you. Veeso AI strictly follows data privacy policies; we cannot access or use your content without your permission.',
          },
          commercialUse: {
            question: 'Can I use my creations for commercial purposes?',
            answerParagraph1: 'Free plan: For personal and non-commercial use only.',
            answerParagraph2:
              'Paid plans: Any work generated is licensed for commercial use, including fonts and media assets.',
          },
          studentDiscounts: {
            question: 'Are there student/education discounts?',
            answer: 'Not yet—potentially in the future.',
          },
          contactTeam: {
            question: 'How can I contact the Veeso team?',
            answer: 'Visit:',
            link: {
              text: 'Contact Us',
              url: 'https://veeso.ai/contact',
            },
            answerAfterLink:
              'You can also reach us at hi@veeso.ai and we will respond within 24 hours.',
          },
          creditUsage: {
            question: 'How many credits does each feature cost?',
            answer: 'Here is a breakdown of credit costs for different features:',
            table: {
              headers: {
                feature: 'Feature',
                mode: 'Mode',
                creditsPerTime: 'Credits Usage (per time)',
              },
              headersArray: ['Feature', 'Mode', 'Credits Usage (per time)'],
              rows: [
                ['Generation', 'Fast', '10'],
                ['Generation', 'Ultra', '30'],
                ['AI Chat Editing', 'Fast', '10'],
                ['AI Chat Editing', 'Ultra', '30'],
                ['Background Removal', 'Fast', '0'],
                ['Background Removal', 'Ultra', '5'],
                ['Image Asset Editing', 'Fast', '10'],
                ['Image Asset Editing', 'Ultra', '25'],
              ],
            },
          },
          tieredPricing: {
            question: 'What is tiered pricing?',
            answer:
              'When you upgrade your plan from 6000 credits to 12000 credits, the system will update your total monthly credits to 12000. This means you will only receive the additional 6000 credits, not a fresh 12000 credits.',
          },
          creditRollovers: {
            question: 'How do credit rollovers work?',
            answer:
              'Credits do not roll over. Unused credits expire at the end of your billing cycle. Daily credits do not accumulate to the next day.',
          },
          upgradingDowngrading: {
            question: 'How do I upgrade or downgrade my subscription?',
            answer:
              'You can upgrade or downgrade your subscription plan anytime from the pricing & plans page. If you downgrade, your current plan will remain active until the end of the current billing cycle. The downgraded plan will take effect next month. Upgrading takes effect immediately, and your total monthly credits will be updated to the new plan amount.',
          },
          cancelSubscription: {
            question: 'How to cancel subscription?',
            answer:
              'Downgrade to the free plan and complete the corresponding steps. Your current plan will remain active until the end of the current billing cycle.',
          },
          getInvoice: {
            question: 'How to get an invoice?',
            answer:
              'Go to "Manage" → "Invoices & Payments". You will be redirected to the Stripe portal, where you can find past invoices.',
          },
          planReset: {
            question: 'When do plans and credit benefits reset?',
            answer:
              'Your monthly credit allowance will reset when your billing cycle resets. The billing cycle resets monthly on the same date as your subscription start date. To check the specific date and time, please view your billing section.',
          },
          dailyCreditsReset: {
            question: 'When do daily credits reset?',
            answer: 'Daily credits reset every day at 0:00 (UTC).',
          },
        },
      },
    },
    0: {
      tips: 'For simple projects & exploring Veeso',
      creditsFeatures: [
        '5 daily credits (refresh at 05:00 PT)',
        '300 bonus credits for new users',
        '≈ 5–10 AI generations/edits',
        'Unlimited manual editing & exports',
        'Full-featured editor, includes:',
        'Auto layout',
        'Text: freely edit, resize, or delete',
        'Images: upload, replace, or enhance with AI (consumes credits)',
        'Community library of high-quality templates',
        'Text processing limit: 500 words',
        'Upload material/image limit: up to 3 images',
        'Support import PDF and Word (max 5 MB)',
        'Up to 5 pages per generation',
        'Standard export (with watermark)',
        'Support export: PNG, JPG, WebP',
      ],
    },
    20: {
      tips: 'For removing the Veeso badge',
      creditsFeatures: [
        '5 daily credits (refresh at 05:00 PT)',
        '100 monthly credits',
        '≈ 50-100 AI generations/edits',
        'Unlimited manual editing & exports',
        'All features in Free, plus:',
        'Unlock "Private Generation"',
        'Fonts/images can be used for commercial purposes',
        'Text processing limit: 3000 words',
        'Upload material/image limit: up to 20 images',
        'Support import PDF and Word (max 15 MB)',
        'Up to 10 pages per generation',
        'HD export (no watermark)',
        'Additional export formats: PDF, PPT, HTML',
      ],
    },
    50: {
      tips: 'For unlocking advanced creative control',
      creditsFeatures: [
        '5 daily credits (refresh at 05:00 PT)',
        '200 monthly credits',
        '≈ 100-200 AI generations/edits',
        'Unlimited manual editing & exports',
        'All features in Creator, plus:',
        'Text processing limit: 5000 words',
        'Up to 20 pages per generation',
        'Priority beta access to new features',
        'Early access to more advanced AI models',
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      productHighlights: 'Product highlights include:',
      items: [
        {
          question: 'What is Veeso AI?',
          answer:
            'Veeso AI is an AI design tool that helps you generate high-quality, fully editable designs in just one minute. No design experience needed. Just input any text and images, and create beautiful graphic designs suitable for product detail pages, social media content, policy explanations, banners, science infographics, event agendas, course posters, and more.',
          link: {
            text: 'Veeso AI design examples',
            url: 'https://veesosign.feishu.cn/wiki/S65OwuCLSiCm2qkZHUhccFVJnGg',
          },
          highlights: [
            'Content-first, fully preserve your text and structure: All input text, paragraphs, and structure are "fully preserved" and presented in a more design-oriented way.',
            'Edit freely, flexibly modify text and replace images. After generating beautiful designs with one click, both text and images can be freely replaced, adjusted, edited, or deleted. Creation is completely under your control.',
            'Accurate text, precise rendering of global languages and formats: Supports 100+ languages and various document formats, one-click conversion, accurate output without garbled text, easy design for global content.',
          ],
        },
        {
          question: "What's the difference between the Free and Paid plans?",
          answer:
            'Free plan: Receive 5 credits daily, provides basic design features including full-featured editor, community high-quality templates, standard export (with watermark), etc.',
          answerAfterLink:
            'Paid plan: Unlock more features such as HD export (no watermark), more export formats, commercial licensing, higher text and image processing limits, etc.',
        },
        {
          question: 'What are credits in Veeso AI?',
          answer:
            'Credits are Veeso AI\'s core billing unit. "Advanced Model" design generation or re-editing consumes 2 credits per use, "Standard Model" design generation or re-editing consumes 1 credit per use. If you choose "Batch Generation" mode, credit consumption will be calculated by multiples. For example: generating 4 designs in one batch using Advanced Model consumes 8 credits total.',
          subItems: [
            'Advanced Models include: Gemini 3 Pro (default), Gemini 2.5 Pro, Claude 4.5, GPT-5, Claude 4.5 series, etc.',
            'Standard Models include: Gemini 2.5 Flash, Kimi K2, GPT-5 mini, Qwen3, Deepseek V3, GLM 4.6, Doubao, MiniMax series, etc.',
          ],
        },
        {
          question: 'How can I get more credits?',
          answer: 'There are 5 main ways to get credits:',
          subItems: [
            'New registered users receive an extra 300 credits',
            'Daily login rewards 5 credits (refreshes at 05:00 daily)',
            'Invite friends. Both parties get 200 credits each',
            'Purchase credits through paid plans',
            'Participate in periodic events to earn bonus credits',
          ],
        },
        {
          question: 'Do credits expire?',
          answer: 'Yes, different types of credits have different validity periods:',
          subItems: [
            'New registered user bonus credits: valid for 12 months',
            'Daily login credits: refresh daily at 05:00',
            'Invite friends reward credits: valid for 30 days',
            'Paid purchase credits: valid for 12 months, extension or transfer not currently supported',
            'Event reward credits: validity determined by specific event rules',
          ],
        },
        {
          question: 'What is the credit consumption order?',
          answer: 'Credits are consumed in the following priority order:',
          subItems: [
            'Daily login credits',
            'Event reward credits',
            'Invite friends reward credits',
            'New registered user bonus credits',
            'Paid purchase credits',
          ],
        },
        {
          question: 'How do I invite friends to get reward credits?',
          answer: 'Please visit:',
          link: {
            text: 'Invite Friends',
            url: 'https://veeso.ai/invite',
          },
          answerAfterLink:
            'Share your "invite link" with friends. After your friend registers and "completes their first design generation", both you and your friend will receive 200 credits reward.',
        },
        {
          question: 'Who owns the designs I create on Veeso AI?',
          answer:
            'All your design source files (saved online) and works belong 100% to you. Veeso AI strictly adheres to data privacy policies. Without your authorization, we cannot access or use any of your design content.',
        },
        {
          question: 'Can design works be used for commercial purposes?',
          answer: 'Free plan: Personal non-commercial use only.',
          answerAfterLink:
            'Paid plan: During the validity period of "paid purchase credits", using any type of credits, all created works include commercial use authorization, and fonts and image materials can be used for commercial purposes.',
        },
        {
          question: 'Are student/education discounts available?',
          answer: 'Currently not supported, but we may launch related programs in the future.',
        },
        {
          question: 'How do I cancel a subscription?',
          answer:
            'You can cancel your subscription at any time through your account settings. Your subscription will remain active until the end of your current billing cycle.',
        },
        {
          question: 'How can I contact Veeso support?',
          answer: 'Please visit:',
          link: {
            text: 'Contact Us',
            url: 'https://veeso.ai/contact',
          },
        },
      ],
    },
    breadcrumb: landingUnsupportedMiscEn.pricing.breadcrumb,
  },
  library: {
    title: 'My Libraries',
    description: 'Manage and organize your design collections',
    noLibrariesFound: 'No Libraries Found',
    noLibrariesDescription: 'Create your first library to start organizing your designs',
    untitledLibrary: 'Untitled Library',
    noDescription: 'No description',
    created: 'Created',
    loading: 'Loading libraries...',
    edit: 'Edit Library',
    create: 'Create Library',
    delete: 'Delete Library',
    name: 'Library Name',
    type: 'Type',
    editDescription: 'Description',
    category: 'Category',
    tag: 'Tags',
    html: 'HTML Content',
    save: 'Save Library',
    cancel: 'Cancel',
    confirmDelete: 'Are you sure you want to delete this library?',
    deleteSuccess: 'Library deleted successfully',
    createSuccess: 'Library created successfully',
    updateSuccess: 'Library updated successfully',
    errorLoading: 'Failed to load libraries',
  },
  libraryView: {
    backToLibraries: 'Back to Libraries',
    libraryItems: 'Library Items',
    browseItems: 'Browse items in this collection',
    emptyLibrary: 'This library is empty',
    addFirstDesign: 'Add your first design to this library',
    untitledItem: 'Untitled Item',
    loading: 'Loading library items...',
    editItem: 'Edit Item',
    deleteItem: 'Delete Item',
    confirmDelete: 'Are you sure you want to delete this item?',
    itemDeleted: 'Item deleted successfully',
    itemUpdated: 'Item updated successfully',
    errorLoading: 'Failed to load library items',
  },
  create: {
    title: 'Make a {dynamicText} in a Minute!',
    makeA: 'Make a',
    inAMinute: 'in a Minute!',
    createTitle: 'What do you want to design now?',
    generating: 'Generating...',
    promptLoading: 'Please wait a moment while your design is being created...',
    dynamicTexts: {
      Poster: 'Poster',
      Banner: 'Banner',
      Thumbnail: 'Thumbnail',
      PPT: 'PPT',
      Meme: 'Meme',
    },
    placeholderPrefix: 'Let Veeso AI design ',
    placeholderLabels: [
      'Art Exhibition Poster',
      'Education Training Poster',
      'Travel Destination Poster',
      'Restaurant Menu',
      'Social Media Graphics',
      'Infographic Design',
      'Brand Brochure',
    ],
    dynamicPlaceholders: [
      'Design a minimalist restaurant menu highlighting signature dishes and promotions...',
      'Create a travel destination poster. Destination is Inner Mongolia desert starry sky, 3 days 2 nights, summer activity...',
      'Design an event poster divided into two visual layers: "background image" and "foreground text layout". Text layout style: modernist style...',
      'Create a vocal course poster. Include 5-week course content, $4999, instructor introduction (photo)...',
    ],
    library: {
      title: 'Library',
      manage: 'Manage Libraries',
      select: 'Select a library to preview items',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      itemCount: '{{selected}}/{{total}}',
      noLibraries: 'No libraries found',
      preview: 'Preview',
      clickToSelect: 'Click to select',
      itemSelected: 'Item selected',
      itemsSelected: 'Items selected',
      addToDesign: 'Add to Design',
      removeFromDesign: 'Remove from Design',
      dragToCanvas: 'Drag to canvas',
      searchPlaceholder: 'Search library items...',
      createNew: 'Create New Library',
      deleteLibrary: 'Delete Library',
      editLibrary: 'Edit Library',
      confirmDelete: 'Are you sure you want to delete this library?',
      libraryDeleted: 'Library deleted successfully',
      libraryCreated: 'Library created successfully',
      libraryUpdated: 'Library updated successfully',
    },
    esmNotSupportedTitle: 'Browser Not Supported',
    esmNotSupportedDesc:
      'Your browser does not support the required features. Please use a modern browser like Chrome, Edge, or Safari.',
    inputRequired: 'Input Required',
    inputRequiredDesc: 'Please provide a text description or upload an image to continue',
    projectCreationFailed: 'Project Creation Failed',
    projectCreationErrorDesc: 'An error occurred while creating the project. Please try again.',
    uploadError: 'Material upload failed, please try again',
    parseError: 'Content parsing failed',
    maxImagesReached: 'Image limit reached',
    maxImagesReachedDesc: 'You can add up to {{max}} images',
    maxTagsReached: 'Tag limit reached',
    maxTagsReachedDesc: 'You can add up to {{max}} tags',
    maxColorsReached: 'Color limit reached',
    maxColorsReachedDesc: 'You can add up to {{max}} color tags',
    tryAgain: 'Try Again',
    uploadImage: 'Reference Image',
    attach: 'Attach',
    attachTips:
      'Add attachments - Upload reference images, materials, or documents to enhance your design',
    addMaterial: 'Add Material',
    addMaterialTips: 'Add image materials to design',
    giveInspirationPrompt: 'Get Inspiration',
    dynamicTextOptions: ['Poster', 'Banner', 'Meme'],
    generate: 'Generate',
    singleMode: 'Single',
    batchMode: 'Batch',
    generateBatch: 'Generate Batch',
    promptPlaceholder: 'Describe the design you want (e.g., "A modern coffee shop poster")',
    modelLabel: 'Select Model:',
    sizeLabel: 'Select Size:',
    presetTitle: 'Or choose a preset prompt:',
    quickStart: 'Quick start',
    use: 'Use',
    usePrompt: 'Use Prompt',
    loginRequired: 'Login Required',
    loginMessage: 'Welcome to Veeso AI',
    continueWithEmail: 'Continue with Email',
    continueWithPhone: 'Continue with Phone',
    loginButton: 'Log In',
    registerButton: 'Register',
    maybeLater: 'Maybe Later',
    referenceUrl: 'Reference URL',
    enterReferenceUrl: 'Enter reference URL',
    colorScheme: 'Color Scheme',
    deviceNotSupportedTitle: 'Mobile Generation Not Supported',
    deviceNotSupportedDesc:
      'For the best experience, please use a computer. Mobile devices are not currently supported.',
    multimodal: 'Multimodal',
    whatToCreateToday: 'What would you like to create today?',
    designDimensions: 'Design Dimensions',
    sizes: {
      auto: 'Auto',
      height: 'Height',
      width: 'Width',
      custom: 'Custom',
      wechatMpCover: 'WeChat Official Account Cover',
      mobilePoster: 'Mobile',
      redNote: 'Xiaohongshu Post',
      newsletter: 'Newsletter',
      infographic: 'Infographic',
      instagram: 'Instagram',
      facebook: 'Facebook',
      twitter: 'X (Twitter)',
      linkedin: 'LinkedIn Post',
      pinterest: 'Pinterest',
      youtube: 'YouTube Thumbnail',
      scrollytelling: 'Long-Form Content',
      tvScreen: 'TV Screen',
      a2: 'A2',
      a3: 'A3',
      a4: 'A4',
      a5: 'A5',
      a2h: 'A2 (Landscape)',
      a3h: 'A3 (Landscape)',
      a4h: 'A4 (Landscape)',
      a5h: 'A5 (Landscape)',
      businessCard: 'Business Card',
      postcard: 'Postcard',
      flyer: 'Flyer',
      brochure: 'Brochure',
      poster: 'Poster',
      banner: 'Banner',
      billboard: 'Billboard',
      menu: 'Menu',
      rollUpBanner: 'Roll-up Banner | 80×200cm',
      tableCard10x15: 'Table Card | 10×15cm',
      tableCard15x20: 'Table Card | 15×20cm',
      lightBoxPoster: 'Light Box Poster | 60×90cm',
    },
    custom_size: {
      title: 'Create Custom Size',
      name: 'Name (Optional)',
      name_placeholder: 'My Custom Size',
      width: 'Width (pixels)',
      height: 'Height (pixels)',
      cancel: 'Cancel',
      create: 'Create',
    },
    size_categories: {
      common: 'Common',
      social: 'Social Media',
      device: 'Device',
      print: 'Print',
      aspect: 'Aspect Ratios',
      ratio: 'Ratio',
      marketing: 'Marketing',
      custom: 'Custom Size',
      add_custom: 'Add Custom Size',
    },
    credits: 'Credits',
    guide: 'Get More Credits',
    projectType: {
      title: 'Change Project Visibility',
      public: {
        label: 'Public',
        description: 'Anyone can view and remix',
        tips: 'Anyone can remix this project. This means it can be copied and edited based on it. To disable remixing, upgrade and switch to private.',
      },
      private: {
        label: 'Private',
        description: 'Only visible to you unless shared',
        tag: 'Pro',
      },
      upgrade: 'Upgrade to Pro',
    },
    mobileWarning: {
      title: 'Mobile Devices Not Supported',
      message: 'Please use desktop browser',
      button: 'Got It',
    },
    optimizePrompt: {
      title: 'Smart Prompt Optimization',
      action: 'Smart Prompt Optimization (for prompts less than 60 characters)',
      aiCommunication: 'Optimize your design prompt with Veeso AI for more precise results',
      aiCommunicationShort: 'AI Design Communication',
      optimize: 'Optimize',
      description:
        'Your prompt seems a bit short. Would you like to use our optimized version for better results?',
      original: 'Original Prompt:',
      optimized: 'Optimized Prompt:',
      optimizing: 'Optimizing prompt...',
      originalDesc: 'The original input prompt that will be used as the optimization base.',
      optimizedDesc: 'You can edit the AI-generated optimized prompt',
      useOriginal: 'Use Original',
      useOptimized: 'Use Optimized',
    },
    promptExamples: {
      auto: {
        artPoster: {
          label: 'Event Poster',
          prompt: `Design a modern AI product launch event poster for Veeso.

Event Title: Veeso AI Global Launch 2026
Theme: Content First. Clarity Wins.
Date: March 10, 2026
Time: 10:00 AM (PST)
Location: Online Event – https://veeso.ai
Host: Veeso AI Team
Description: Discover how Veeso helps professionals refine writing, improve clarity, and elevate communication quality using AI.`,
          options: [
            {
              label: 'Creator Meetup',
              prompt: `Design a modern community meetup poster for Veeso.

Event Title: Veeso Creator Circle – Live Writing Lab
Tagline: Write Better. Think Sharper.
Date: May 6, 2026
Time: 6:30 PM PST
Location: Online – https://veeso.ai
Hosted by: Veeso Community Team

About the Event:
An interactive session where creators submit real writing samples and receive live clarity edits using Veeso AI.
Focused on improving structure, tone, and precision — not generating more content.

Who Should Attend:
Content creators, indie founders, marketers, newsletter writers.

What’s Happening:
• Live writing breakdowns
• Real-time editing demonstrations
• Community Q&A
• Creator networking breakout rooms`,
            },
            {
              label: 'Design Workshop',
              prompt: `Create a high-end workshop poster for Veeso.

Event Title: Designing for Clarity – A Veeso Workshop
Subtitle: Where UX Meets Writing Precision
Date: April 22, 2026
Location: San Francisco (Limited Seats) + Live Stream
Registration: https://veeso.ai

Workshop Focus:
A hands-on session exploring how product owners, marketers, operations, founders can design interfaces that enhance clarity, reduce noise, and improve communication quality using AI tools like Veeso.

Session Topics:
• The Psychology of Clear Communication
• Editing vs Generating: Why Precision Wins
• Designing AI Tools That Refine Instead of Replace
• Live Product Teardown + Redesign Exercise

Audience:
Product designers, UX writers, SaaS founders, operators.`,
            },
            {
              label: 'Afterparty',
              prompt: `Design a bold, stylish afterparty event poster for Veeso.

Event Title: The Clarity Afterparty
Subtitle: Less Noise. More Signal.
Date: March 10, 2026 – 8:30 PM
Location: San Francisco – Private Venue
RSVP: https://veeso.ai

About:
An invite-only gathering following the Veeso AI launch.
For founders, creators, and operators who believe content quality matters more than content volume.

Atmosphere:
Minimal speeches. Good conversations. Great drinks. Sharp minds.`,
            },
          ],
        },
        socialMedia: {
          label: 'Data Visualization',
          prompt: `Create a clean, modern data visualization poster for Veeso.

Report Title: The State of Clear Communication 2026
Source: Veeso Internal Usage Insights
Website: https://veeso.ai

Key Metrics to Visualize:
• 72% of users improved writing clarity score after 3 edits
• Average editing time reduced by 48%
• 65% of users prefer editing tools over content generation
• 3.2x increase in document completion rate`,
          options: [
            {
              label: 'SaaS Growth Dashboard',
              prompt: `Design a SaaS-style analytics dashboard visualization for Veeso.

Dashboard Title: Veeso Growth Snapshot – Q1 2026
Website: https://veeso.ai

Metrics:
• Monthly Active Users: 42,500
• Paid Conversion Rate: 5.4%
• Average Revenue Per User: $24/month
• User Retention (3-Month): 66%
• Average Edits per Session: 5.8

Charts to Include:
• Line chart showing MAU growth from Jan to March
• Funnel visualization for Free → Paid conversion
• Retention curve graph
• KPI summary cards at top
`,
            },
            {
              label: 'Performance Report',
              prompt: `Create a structured writing performance infographic for Veeso.

Report Title: Editing vs Generating – What Actually Improves Writing?
Website: https://veeso.ai

Study Findings:
• 68% higher clarity score when editing instead of regenerating
• 52% reduction in redundant sentences
• 40% improvement in structural coherence
• 2.1x increase in reader comprehension ratings

Visualization Requirements:
• Side-by-side comparison chart (Editing vs Generating)
• Before/After clarity score bars
• Reader comprehension line graph
• Highlight key insight callouts`,
            },
            {
              label: 'Infographic Insights',
              prompt: `Design a modern infographic poster showing creator writing behavior insights from Veeso.

Title: How Creators Refine Content in 2026
Source: Veeso User Behavior Analysis
Website: https://veeso.ai

Insights to Visualize:
• Average newsletter draft length: 1,200 words
• Average edits before publish: 4.6 rounds
• 74% of creators prioritize tone refinement
• 58% use clarity tools before publishing on social platforms
• 3x higher engagement when structure is optimized

Charts to Include:
• Pie chart for editing priorities (tone, structure, grammar, clarity)
• Bar chart for edit rounds
• Engagement comparison graph
• Highlight statistic blocks`,
            },
          ],
        },
        productPoster: {
          label: 'Web Design',
          prompt: `Design a modern SaaS homepage for Veeso.

Brand: Veeso
Website: https://veeso.ai
Tagline: Content in, Design Out.

Hero Section:
Headline: Turn Messy Writing Into Clear Communication
Subheadline: Veeso helps professionals refine, structure, and elevate their writing using AI — without losing their voice.
Primary CTA: Try Veeso Free
Secondary CTA: See Live Demo

Core Features Section:
• Real-time Clarity Editing
• Structure & Flow Optimization
• Tone Refinement Tools
• Precision-Focused AI (Not Just Generation)

Social Proof Section:
“72% of users improved clarity after 3 edits.”
Used by creators, founders, and product owners, marketers, operations, founders worldwide.

Footer:
Product | Pricing | Blog | Community | https://veeso.ai`,
          options: [
            {
              label: 'Newsletter',
              prompt: `Create a dedicated newsletter landing page design for Veeso.

Newsletter Name: The Clarity Brief
Tagline: Practical Writing Insights for Builders
Subscribe at: https://veeso.ai

Hero Section:
Headline: Write Sharper. Think Clearer.
Subheadline: Weekly breakdowns on writing precision, AI editing workflows, and clarity-driven product thinking.
Email Input Field + CTA: Subscribe Free

What You’ll Get:
• Real writing teardown examples
• Editing frameworks
• AI workflow breakdowns
• Founder insights on communication

Preview Section:
Show 3 recent editions:
1. Editing vs Generating – Why Precision Wins
2. The Anatomy of a Clear Product Memo
3. Reducing Noise in AI Content Workflows`,
            },
            {
              label: 'Product Feature Page',
              prompt: `Design a detailed product feature page for Veeso.

Feature Name: Clarity Engine
Website: https://veeso.ai

Hero Section:
Headline: The AI That Refines — Not Replaces
Subheadline: Improve structure, eliminate redundancy, and strengthen logic in seconds.

Feature Blocks:
• Structural Analysis
• Tone Adjustment
• Redundancy Detection
• Logical Flow Enhancement

Use Case Sections:
For Founders: Sharpen investor updates
For Marketers: Improve conversion messaging
For Product Owners, Marketers, Operations, Founders: Clarify documentation

Metrics Highlight:
• 48% faster editing
• 2.1x improved reader comprehension
• 65% user preference for editing over regenerating`,
            },
            {
              label: 'Email Marketing',
              prompt: `Design a high-converting email marketing layout for Veeso.

Brand: Veeso
Website: https://veeso.ai
Theme: Content in, Design Out.

Email Type: Product Activation Email
Subject Line: Your Writing Just Got Sharper
Preheader: See how Veeso improves clarity in minutes

Header Section:
Logo + clean navigation (Product | Pricing | Blog)

Hero Block:
Headline: Turn Drafts Into Clear Communication
Subheadline: Start refining your writing with precision-focused AI.
Primary CTA Button: Open Veeso Now
Secondary CTA: View Live Demo

Feature Highlights (3 Columns):
• Real-Time Clarity Editing
• Structure Optimization
• Tone Refinement Without Losing Voice

Social Proof Section:
“72% of users improved clarity after 3 edits.”
Used by founders, marketers, and product owners, marketers, operations, founders.

Educational Section:
Mini breakdown: Editing vs Generating
Short paragraph explaining why refinement leads to stronger communication.

Footer:
Manage Preferences | Unsubscribe | https://veeso.ai
Company Info + social icons placeholder`,
            },
          ],
        },
      },
      poster: {
        artExhibition: {
          label: 'Art Exhibition',
          prompt:
            "Create an elegant art exhibition poster, exhibition name 'Contemporary Art Gallery', duration one month",
        },
        educationTraining: {
          label: 'Education Training Institution',
          prompt:
            "Design a professional education training institution brand poster, divided into two visual layers: 'background image' and 'foreground text layout'. Text layout style: modernist style, using clear, sans-serif fonts, grid layout, with a sense of hierarchy. Maintain good white space, text should not block key image areas.",
        },
        fashionShow: {
          label: 'Fashion Show',
          prompt:
            "Create a modern minimalist tech conference poster, title '2025 AI Design Summit', location Beijing",
        },
      },
      'social-media': {
        educationCourse: {
          label: 'Education Course',
          prompt: "Design a warm food sharing social media image, theme 'Home Recipe Sharing'",
        },
        fitnessCheckIn: {
          label: 'Fitness Check-in',
          prompt:
            "Create a professional business event promotional image, event name 'Entrepreneurs Summit 2025'",
        },
        readingNotes: {
          label: 'Reading Notes',
          prompt:
            "Create a stylish product launch social media image, new product 'Spring Limited Edition Perfume'",
        },
      },
      carousel: {
        linkedInCarousel: {
          label: 'LinkedIn Carousel',
          prompt: `Create a professional LinkedIn carousel post for Veeso.

Title: How to Turn Raw Text Into Polished Design

Slide 1 – Cover
• Headline: Content In → Design Out
• Subheadline: Turn your ideas into ready-to-share visuals in minutes
• Logo + https://veeso.ai

Slide 2 – The Problem
• People spend hours formatting reports, social posts, and newsletters
• Non-designers struggle to make content look professional

Slide 3 – The Solution
• Veeso AI transforms raw text and images into structured, polished outputs
• Supports presentations, social media, newsletters, reports

Slide 4 – How It Works
• Step 1: Upload content
• Step 2: Select style or template
• Step 3: Get a polished, editable output

Slide 5 – Benefits
• Save 3–5 hours per week
• Maintain brand consistency
• Easily remix & iterate content

Slide 6 – CTA
• Try Veeso free today
• https://veeso.ai`,
        },
        instagramCarousel: {
          label: 'Instagram Carousel',
          prompt: `Create an engaging Instagram carousel for Veeso.

Title: 5 Ways Veeso Helps You Publish Faster

Slide 1 – Cover
• Headline: Content In → Design Out
• Logo + https://veeso.ai
• Bold, eye-catching color

Slide 2 – Tip 1
• Use Veeso to instantly turn text into social post graphics

Slide 3 – Tip 2
• Generate newsletter layouts directly from raw drafts

Slide 4 – Tip 3
• Quickly create presentation slides without design skills

Slide 5 – Tip 4
• Ensure brand consistency with template-based outputs

Slide 6 – Tip 5
• Collaborate & remix content easily with your colleagues

Slide 7 – CTA
• Swipe up or visit https://veeso.ai to start for free`,
        },
        twitterCarousel: {
          label: 'Multi-image Tweet Post',
          prompt: `Create a Multi-image tweet post for Veeso.

Title: How We Save 3+ Hours Weekly for Creators

Slide 1 – Cover
• Headline: Content In → Design Out
• Logo + https://veeso.ai

Slide 2 – Pain Point
• Writing content is fast
• Designing content is slow

Slide 3 – Veeso Workflow
• Upload raw content → AI generates structured slides/posts/newsletters

Slide 4 – Impact
• 3x faster social post production
• 2x faster newsletter formatting
• Maintain brand look & feel

Slide 5 – User Testimonial
• "Veeso turned my rough draft into a shareable report in minutes" – Founder, Startup

Slide 6 – CTA
• Start your free trial → https://veeso.ai`,
        },
      },
      longFormContent: {
        infographic: {
          label: 'Infographic',
          prompt: `Create a long-form infographic for Veeso.

Title: How Veeso Saves Creators 3+ Hours Weekly
Sections:

Section 1 – Header
• Headline: Content In → Design Out
• Subheadline: Transform raw text into polished outputs in minutes
• Logo + https://veeso.ai

Section 2 – Workflow
• Step 1: Input your content (text, images, references)
• Step 2: AI generates structured layouts
• Step 3: Export for social, newsletter, or presentation
• Visual: Flow diagram of steps

Section 3 – Key Metrics
• 42,500 Monthly Active Users
• 3.2x faster content production
• 66% 3-Month retention
• Visual: Bar chart / icons

Section 4 – Benefits
• Save time
• Maintain brand consistency
• Easy collaboration & remix

Section 5 – CTA
• Try Veeso Free
• https://veeso.ai`,
        },
        productFeaturePage: {
          label: 'Product Feature Page',
          prompt: `Design a long-form product feature page for Veeso.

Title: Clarity Engine – Refine Your Content Instantly
Website: https://veeso.ai

Sections:

Section 1 – Hero
• Headline: Content In → Design Out
• Subheadline: Real-time AI layouts and structure optimization
• CTA: Try Veeso Free

Section 2 – Problem
• People spend hours formatting drafts
• Non-designers struggle to create professional visuals

Section 3 – Features
• Real-Time Clarity Editing
• Template Library & Layout Suggestions
• Tone & Structure Optimization
• Collaboration & Remix Tools

Section 4 – Visual Demo
• Before / After content transformation
• Screenshot mockups of social posts, newsletters, presentations

Section 5 – Metrics
• Avg 3x faster output
• 72% users improved clarity on first edit

Section 6 – CTA
• Start using Veeso → https://veeso.ai`,
        },
        caseStudyPage: {
          label: 'Case Study Page',
          prompt: `Create a long-form case study page for Veeso.

Title: How Veeso Helped a Startup Deliver Investor Decks in 10 Minutes
Website: https://veeso.ai

Sections:

Section 1 – Cover
• Headline: Content In → Design Out
• Subheadline: Real results from real users
• Logo + https://veeso.ai

Section 2 – The Challenge
• Startup founders spent hours converting ideas into presentation decks
• Limited design skills
• Missed deadlines & inconsistent visuals

Section 3 – The Solution
• Veeso AI transforms raw text and images into polished slides
• Easy template selection & customization
• Fast iteration & collaboration

Section 4 – Results
• Investor deck completed in 10 minutes
• 3x faster workflow
• Consistent branding across slides
• Screenshots: Before/After slides

Section 5 – User Quote
• "Veeso turned our rough notes into a shareable deck instantly" – Founder, Startup

Section 6 – CTA
• Try Veeso Free → https://veeso.ai`,
        },
      },
      infographic: {
        marketTrends: {
          label: 'Market Trends',
          prompt:
            "Create a data-driven market trend infographic, theme '2025 E-commerce Growth Report'",
        },
        knowledgeScience: {
          label: 'Educational Content',
          prompt: "Design an educational knowledge infographic, content 'Waste Sorting Guide'",
        },
        environmentalStats: {
          label: 'Environmental Stats',
          prompt:
            "Create an environmental statistics infographic, title 'Global Carbon Emissions Status'",
        },
        productFeaturePage: {
          label: 'Product Feature Page',
          prompt: `Create a product feature page infographic for Veeso.

Title: Clarity Engine – The AI That Refines, Not Replaces
Website: https://veeso.ai

Hero Section:
• Feature Name: Clarity Engine
• Tagline: Improve structure, eliminate redundancy, strengthen logic in seconds

Feature Blocks:
• Structural Analysis – Identifies weak points in content flow
• Tone Adjustment – Refines voice without losing authenticity
• Redundancy Detection – Removes repetitive content intelligently
• Logical Flow Enhancement – Strengthens argument structure

Visual Elements:
• Before/After comparison charts
• Feature icons with brief descriptions
• User workflow diagram
• Metrics highlighting impact (48% faster editing, 2.1x improved comprehension)

Use Cases:
• For Founders: Sharpen investor updates
• For Marketers: Improve conversion messaging
• For Product Owners, Marketers, Operations, Founders: Clarify documentation

Design Direction:
- Clean, modern infographic style
- Clear visual hierarchy
- Data visualization elements
- Brand-consistent color palette`,
        },
        caseStudyPage: {
          label: 'Case Study Page',
          prompt: `Design a case study page infographic for Veeso.

Title: How StartupX Saved 5 Hours Weekly with Veeso
Website: https://veeso.ai

Case Study Structure:

Section 1 – Client Overview
• Company: StartupX (B2B SaaS, 15 employees)
• Challenge: Newsletter formatting took 6+ hours weekly
• Industry: Product marketing

Section 2 – The Problem
• Before Veeso: Manual design work, inconsistent branding, time-consuming iterations
• Pain Points: Non-designers struggled with layout, brand consistency issues

Section 3 – Solution Implementation
• Tool: Veeso AI Design Platform
• Process: Raw newsletter drafts → AI-generated layouts → Quick edits → Publish
• Timeline: Implementation in 1 day

Section 4 – Results & Metrics
• Time Saved: 5 hours per week (83% reduction)
• Output Quality: Improved brand consistency
• User Satisfaction: 90% positive feedback
• ROI: 20x time savings vs. subscription cost

Section 5 – Testimonial
• Quote: "Veeso transformed our newsletter workflow. What used to take hours now takes minutes." – Marketing Lead, StartupX

Section 6 – Key Takeaways
• AI-powered design tools can dramatically reduce production time
• Brand consistency improves with template-based workflows
• Non-designers can create professional outputs

Visual Elements:
• Before/After comparison
• Timeline visualization
• Metrics charts and graphs
• Process flow diagram

Design Direction:
- Professional case study layout
- Data-driven visualizations
- Clear success metrics
- Trust-building design elements`,
        },
      },
      presentation: {
        projectReport: {
          label: 'Project Report',
          prompt: `Create a structured project report presentation for Veeso.

Project Title: Veeso Clarity Engine Optimization – Q1 2026
Audience: Internal Staff + Investors
Presented by: Veeso Product Team
Website: https://veeso.ai

Slide 1 – Cover
Veeso Clarity Engine Optimization
Q1 2026 Project Review

Slide 2 – Executive Summary
• Goal: Improve editing accuracy and reduce redundancy detection errors
• Timeline: Jan–March 2026
• Result: +18% clarity scoring accuracy

Slide 3 – Problem Statement
• Users over-relying on regeneration instead of refinement
• Redundant sentence detection not contextual enough
• Tone adjustment lacked nuance in long-form writing

Slide 4 – Objectives
• Increase clarity score reliability
• Reduce editing time
• Improve user retention in first 30 days

Slide 5 – Key Metrics Before Optimization
• Avg Editing Time: 9.2 minutes
• Clarity Improvement Rate: 61%
• 30-Day Retention: 54%

Slide 6 – Solutions Implemented
• Context-aware redundancy model
• Improved logical flow analysis
• Tone gradient detection system

Slide 7 – Results After Deployment
• Avg Editing Time: 4.8 minutes
• Clarity Improvement Rate: 72%
• 30-Day Retention: 66%

Slide 8 – User Feedback Highlights
Quotes from founders and creators about improved writing precision.

Slide 9 – Lessons Learned
• Editing > Generating positioning validated
• Clarity scoring must remain transparent
• Visual feedback improves engagement

Slide 10 – Next Phase Roadmap
• Advanced structure heatmap
• Collaboration editing mode
• Enterprise clarity analytics`,
        },
        productLaunch: {
          label: 'Pitch Deck',
          prompt: `Create an investor-ready pitch deck for Veeso, emphasizing 'Content In → Design/Clarity Out.'

Company: Veeso
Website: https://veeso.ai
Tagline: Content In. Design Out.
Stage: Seed / Early Growth
Audience: VC Investors & Angel Investors

Slide 1 – Cover
Veeso
Content In. Design Out.
https://veeso.ai

Slide 2 – The Problem
People spend hours turning text into polished visuals:
• Social posts, newsletters, reports, presentations
• High friction between raw content and readable design
• Non-designers struggle to make content look professional

Slide 3 – Market Insight
• Over 65% of content creators spend >50% of time formatting
• Demand for productivity tools that bridge content → design
• Enterprises, creators, and founders all face this workflow bottleneck

Slide 4 – The Opportunity
The AI wave has generated content.
The next wave is transforming content into clear, structured, shareable outputs.
Veeso sits at this intersection.

Slide 5 – Solution
Veeso: AI-powered design and layout engine
• Users input raw content (text, images, references)
• Veeso generates structured, publication-ready outputs:
   - Social media posts
   - Newsletters
   - Reports & presentations
• Supports editing, remixing, and iterative refinement

Slide 6 – Product Demo
• Upload a text draft → get polished layout instantly
• Templates + AI-powered suggestions
• Before/After visuals of content → final output
• Slide, poster, newsletter previews

Slide 7 – Traction
• 42,500 Monthly Active Users
• 14% Paid Conversion
• Users create 3.2x more structured outputs
• Active across social, product, and marketing workflows

Slide 8 – Business Model
Freemium SaaS:
• Free: limited outputs
• Pro: $24/month, unlimited layouts + AI suggestions
• Enterprise: collaborative workflows + analytics

Slide 9 – Market Opportunity
Global content creation + productivity software market expanding rapidly
Veeso bridges creation → design, unlocking massive workflow efficiency

Slide 10 – Competition
• Canva / Figma / traditional design tools focus on manual layout
• AI content generators focus on text/images only
Veeso differentiates: Input raw content, output polished, structured, ready-to-use designs.

Slide 11 – Unit Economics
• ARPU: $xx/month
• CAC: $xx
• Avg Lifetime: 6 months
• LTV: $xx
• Gross Margin: 68%
• Scalable via AI-assisted layout generation

Slide 12 – Vision
Become the universal content-to-design layer
• One workflow for all content → presentations, newsletters, social posts, reports

Slide 13 – Roadmap
• Multi-format AI templates
• Collaboration
• Enterprise analytics dashboard
• API integrations for workflow automation

Slide 14 – The Ask
Raising: $X Seed Round
Use of Funds:
• Product scaling
• Model refinement
• Go-to-market expansion

Slide 15 – Closing
Content In. Design Out.
https://veeso.ai`,
        },
        financialReport: {
          label: 'Financial Report',
          prompt: `Create a structured financial report presentation for Veeso.

Report: Veeso Financial Overview – FY 2026 Q1
Website: https://veeso.ai
Audience: Investors & Board

Slide 1 – Cover
Veeso Financial Report
Q1 2026

Slide 2 – Revenue Overview
• Total Revenue: $612,000
• MRR: $204,000
• ARR Run Rate: $2.45M

Slide 3 – Revenue Breakdown
• Pro Plan (78%)
• Business Plan (17%)
• Enterprise Pilot (5%)

Slide 4 – User Metrics
• Monthly Active Users: 42,500
• Paid Users: 2,295
• Conversion Rate: 5.4%

Slide 5 – Cost Structure
• Model & Infrastructure: 32%
• Engineering: 28%
• Marketing: 21%
• Operations: 19%

Slide 6 – Gross Margin
• Gross Margin: 68%
• Target Margin by Q4: 75%

Slide 7 – CAC & LTV
• CAC: $38
• ARPU: $24/month
• Avg Lifetime: 3 months
• LTV: $72
• LTV/CAC Ratio: 1.9x

Slide 8 – Burn & Runway
• Monthly Burn: $120,000
• Runway: 14 months

Slide 9 – Forecast
Projected ARR End of 2026: $4.8M

Slide 10 – Strategic Priorities
• Improve paid conversion
• Expand enterprise tier
• Reduce model cost per session`,
        },
      },
      'pull-up-banner': {
        environmentalInitiative: {
          label: 'Environmental Initiative',
          prompt:
            'Design an environmental protection themed pull-up banner with core content including main visual area, core information layer, design specifications, and visual requirements.',
        },
        productLaunch: {
          label: 'Product Launch',
          prompt:
            'Design a pull-up banner for Veeso App showcasing diverse young people connecting through mobile devices in urban, natural, or leisure scenes, emphasizing authentic interest connections.',
        },
        financialReport: {
          label: 'Financial Report',
          prompt:
            "Create a simple pull-up banner design, title '2025 Shanghai Auto Show Investment Promotion'",
        },
      },
      brochure: {
        trifoldBrochure: {
          label: 'Trifold Brochure',
          prompt: `Create a trifold brochure for Veeso.

Front Panel:
• Logo + tagline: Content In → Design Out
• Hero headline: Turn Your Drafts Into Polished Visuals
• CTA: Visit https://veeso.ai

Inside Panels:
Panel 1 – Problem
• People spend hours turning text into designs
• Non-designers struggle with layout & formatting

Panel 2 – Solution
• Veeso AI transforms raw content into structured, ready-to-use visuals
• Works for social posts, newsletters, presentations, and reports

Panel 3 – Features
• Real-time AI Layout Suggestions
• Template Library & Editing
• Brand Consistency & Style Control
• Collaboration & Remixing

Back Panel:
• Testimonials:
"Veeso makes my newsletter prep 3x faster" – Startup Founder
• CTA: Start Free Trial at https://veeso.ai
• Social icons placeholder`,
        },
        flyer: {
          label: 'Flyer',
          prompt: `Design a promotional flyer for Veeso.

Front Side:
• Headline: Content In → Design Out
• Subheadline: Transform raw text into shareable designs in seconds
• Key Visual: Mockup of AI-generated newsletter and social post
• CTA Button: Try Veeso Free

Back Side:
Section 1 – Features
• AI-Powered Clarity & Layout
• Multi-format Export (PDF, Slides, Images)
• Brand Template Library

Section 2 – Benefits
• Save hours weekly
• Maintain visual consistency
• Collaborate and remix content

Section 3 – Testimonials
• "Veeso saved me 5 hours preparing my investor deck" – Product Manager`,
        },
        booklet: {
          label: 'Booklet',
          prompt: `Create a multi-page brochure booklet for Veeso.

Cover Page:
• Logo + tagline: Content In → Design Out
• Headline: Unlock Professional-Quality Designs Instantly
• Visual: Before/After content → polished design

Inside Pages:
Page 2 – Introduction
• About Veeso: AI-powered content-to-design workflow
• Mission: Save creators and professionals time, maintain brand consistency

Page 3–4 – How It Works
• Step 1: Input your raw text/images
• Step 2: Choose style or template
• Step 3: Get polished, editable output
• Illustrations for each step

Page 5–6 – Core Features
• Real-Time AI Layout
• Template Library
• Collaboration Tools
• Export Options: PDF, Slides, Images

Page 7–8 – Case Studies
• Example 1: Newsletter made in 5 minutes
• Example 2: Investor deck created from raw text

Page 9 – Testimonials
• "Veeso turned my rough notes into a complete slide deck in 10 minutes" – Startup Founder

Page 10 – CTA
• Try Veeso Free
• Visit https://veeso.ai
• Social icons placeholder

Back Cover:
• Logo + tagline
• Website
• Minimal visual`,
        },
      },
      flyer: {
        promotionActivity: {
          label: 'Promotion Activity',
          prompt:
            'Design a supermarket promotion flyer for an anniversary sale event with vibrant promotional style, red and yellow color scheme, clear promotional information, and 5:5 image-to-text ratio.',
        },
        restaurantOpening: {
          label: 'Restaurant Opening',
          prompt:
            'Create a new restaurant opening promotional flyer introducing restaurant features and opening discounts with appetizing food style, warm color scheme, and 6:4 image-to-text ratio.',
        },
        educationTraining: {
          label: 'Education Training',
          prompt:
            "Design a children's English training institution enrollment flyer highlighting course features and promotional policies with lively and cute style, blue, green, and yellow color scheme, and 4:6 image-to-text ratio.",
        },
      },
    },
    optimizationChat: {
      generateNow: 'Generate Now',
      title: 'AI Design Requirements Optimization',
      description: "Let's refine your design requirements for the best results",
      startPrompt: 'What specific design elements or style do you need?',
      customInputPlaceholder:
        "If you're not satisfied with the existing options, you can also enter your requirements here...",
      thinking: 'Processing...',
      optimizedPromptLabel: 'Optimized Prompt:',
      submitCustom: 'Submit',
      continueOptimizing: 'Continue Optimizing',
      clickToContinue: 'Click the options below to continue optimizing',
      retry: 'Retry',
      errorGenerating: 'Content Generation Error',
      errorMessage: 'Content generation failed. Please try again.',
      finalPromptError: 'Final prompt generation failed.',
      conversationError: 'Unable to continue conversation.',
      nextQuestionError: 'Unable to generate next question.',
      optimizeError: 'Unable to start optimization conversation.',
      useOptimized: 'Use Optimized Prompt',
      finalPromptSuccess: 'Your design prompt has been optimized!',
      maxStepsReached: 'Maximum conversation steps reached',
      designRequirements: 'Design Requirements',
      visualStyle: 'Visual Style',
      layoutPreferences: 'Layout Preferences',
      colorScheme: 'Color Scheme',
      typographyChoices: 'Typography Choices',
      additionalNotes: 'Additional Notes',
    },
    insufficientCredits: {
      title: 'Insufficient Credits',
      description: 'You have used all your credits. Invite friends to get more credits!',
    },
    generateMode: {
      title: 'Generation Mode',
      fast: 'Fast Mode',
      workflow: 'Advanced Mode',
      fastDescription: 'Single Agent mode, fast generation speed',
      workflowDescription:
        'Multi-Agent generation mode, fast generation speed with higher design quality',
    },
    batch: {
      design: 'design',
      designs: 'designs',
      generateOne: 'Generate 1 design',
      generateMultiple: 'Generate {{count}} designs',
      title: 'Batch Generation',
      description:
        'Generate multiple design variations at once to explore different creative directions.',
      hint: 'More designs = More creative choices',
      single: 'Generate 1 design at a time',
      double: 'Generate 2 designs at once',
      quad: 'Generate 4 designs at once',
      nine: 'Generate 9 designs at once',
      singleImage: 'Single',
      multipleImages: 'Images',
    },
    inputRequiredTitle: 'Input Required',
    promptTemplateHint:
      'Click on the prompt templates above to get started quickly, or enter your own creative ideas',
    promptTemplates: 'Prompt Templates',
    recentProjects: 'My Projects',
    latestTemplates: 'Latest Templates',
    featuredProjects: 'Featured Designs',
    communityFavorites: 'Community Favorites and Trending Designs',
    loadingFeatured: 'Loading featured projects...',
    continueWork: 'Continue from where you left off',
    featured: 'featured designs',
    readyToUse: 'Scroll down to see more prompt examples, click to use creative prompts',
    viewAll: 'View All',
    viewMore: 'View More',
    items: 'items',
    view: 'View',
    open: 'Open',
    noRecentProjects: 'No recent projects found',
    firstDesign: 'Create your first design by selecting a template or using Veeso AI',
    noTemplates: 'No templates available',
    noFeaturedProjects: 'No featured design here.',
    checkBackLater: 'Featured design from the community.',
    goToWorkspace: 'Go to Workspace',
    viewAllProjects: 'View all your projects',
    colorSchemes: {
      defalt: 'Default Color Scheme',
      monochrome: 'Monochrome',
      business: 'Business Professional',
      cafe: 'Cafe',
      restaurant: 'Restaurant',
      vintage: 'Vintage',
      modern: 'Modern Corporate',
      nature: 'Nature',
      luxury: 'Luxury',
      tech: 'Tech',
      pastel: 'Pastel',
      retro: 'Retro',
      custom: 'Custom Colors',
    },
  },
  imageGenerator: {
    title: 'AI Image Generator',
    subtitle: 'Transform Text into Stunning Images in Seconds',
    prompt: {
      label: 'Prompt',
      placeholder: 'Describe the image you want to generate...',
      enhance: 'Enhance Prompt',
      enhancing: 'Enhancing...',
      getInspiration: 'Get Inspiration',
      clear: 'Clear',
    },
    history: 'History',
    historyDialog: {
      title: 'History',
      description: 'View your previous image generations.',
    },
    generate: 'Generate',
    stylePreset: 'Style Preset',
    moreOptions: 'More Options',
    createFirst: {
      title: 'Create Your First Image',
      description:
        'Transform your ideas into stunning visuals. Simply describe what you want to create and let AI do the magic.',
      tryExample: 'Try an example prompt →',
      outputSize: 'Output Size:',
    },
    errors: {
      missingPrompt: {
        title: 'Missing Prompt',
        description: 'A prompt is required to generate an image.',
      },
      generic: {
        title: 'Uh oh! Something went wrong.',
      },
    },
  },
  useCases: {
    title: 'Use Cases',
    description:
      'Explore how Veeso AI can transform your creative workflow with these powerful use cases',
    seoTitle: 'AI Design Use Cases | Veeso AI',
    seoDescription:
      'Discover how Veeso AI can help you create stunning designs for social media, posters, newsletters, and more with our AI-powered design tools.',
    seoKeywords:
      'AI design, design use cases, social media posts, posters, newsletters, podcast covers, flash cards, video thumbnails',
    learnMore: 'Learn More',
    tryNow: 'Try now',
    socialMedia: {
      title: 'Social Media Posts',
      description: 'Create engaging social media content with Veeso AI',
    },
    minimalistPoster: {
      title: 'Minimalist Posters',
      description: 'Design clean and modern minimalist posters with professional layouts',
    },
    letterCard: {
      title: 'Letter Cards',
      description: 'Create beautiful letter-style cards with elegant typography and layouts',
    },
    podcastCover: {
      title: 'Podcast Covers',
      description: 'Design eye-catching podcast cover art with Apple-inspired aesthetics',
    },
    videoCover: {
      title: 'Video Covers',
      description: 'Create professional video thumbnails with Notion-style minimalism',
    },
    flashCard: {
      title: 'Flash Cards',
      description: 'Design effective vocabulary and study flash cards with clear layouts',
    },
    newsletter: {
      title: 'Newsletters',
      description: 'Create professional newsletter designs with clean typography and imagery',
    },
  },
  batchMode: {
    title: 'Batch Project Preview',
    description:
      'Your batch creations are ready! Review and select the designs that best match your vision. Click on any project to explore and refine it further.',
    startAnotherBatch: 'Start Another Batch',
    foundPerfectDesign: 'Found your perfect design?',
    emptyStateTitle: 'No Batch Designs Yet',
    emptyStateDescription:
      'Create multiple design variations at once by starting a batch generation from the create page.',
    startCreating: 'Start Creating',
    regenerate: 'Regenerate',
    yourPrompt: 'Your Prompt',
    generationFailed: 'Generation failed',
    edit: 'Edit',
    status: {
      pending: 'Pending',
      generating: 'Generating',
      completed: 'Completed',
      error: 'Error',
    },
  },
  designError: {
    title: 'Unable to Access Design',
    messages: {
      Forbidden:
        "You don't have permission to view this project. It may be private or has been deleted.",
      NetworkError:
        'Network error occurred while trying to fetch the resource. Please check your internet connection and try again.',
      NotFound: 'The requested resource could not be found. Please check the URL and try again.',
      Unauthorized:
        "You don't have permission to access this resource. Please login or contact support.",
      ServerError: 'The server encountered an error. Please try again later or contact support.',
    },
    errorDetails: 'Error Details',
    returnHome: 'Return to Home',
    errorIcon: 'Error Icon',
  },
  preview: {
    title: 'Preview',
    share: 'Share',
    description: 'Preview and edit your designs',
    back: 'Back to Create',
    export: 'Export',
    createNew: 'Create New',
    readonly: 'Read only',
    chat: 'Chat',
    edit: 'Edit',
    layers: 'Layers',
    images: 'Image',
    text: 'Text',
    library: 'Library',
    dragToCanvas: 'Drag to canvas',
    send: 'Send',
    sending: 'Sending...',
    sendShortcut: 'Shift + Enter to send',
    credits: 'Credits',
    typeMessage: 'Type your message...',
    tokensGenerated: '{{count}} tokens generated',
    generationComplete: '✅ Design generation complete!',
    savingMasterpiece: '💾 Saving your masterpiece...',
    generationError: '🔄 Oops! Something went wrong. Let me try again...',
    generationStopped: '⏹️ Generation stopped',
    stopGeneration: 'Stop Generation',
    somethingWrong: 'Sorry, something went wrong. Please try again.',
    imageEditSuccess: 'Image edited successfully! Your design has been updated.',
    textEditSuccess: 'Text edited successfully! Your design has been updated.',
    timeoutError:
      'Timeout waiting for design to render. This usually happens when the generated code has syntax errors.',
    veesoRootError:
      "The design couldn't be rendered properly. This might be due to complex elements or browser limitations.",
    tryAgainSimpler: 'Please try again with a simpler design request or refresh the page.',
    needMoreCredits: 'You need more credits to continue',
    getMoreCredits: 'Buy Credits',
    inviteForCredits: 'Get free Credits',
    upgradeAccount: 'Upgrade your account for unlimited access',
    creditsExhausted: 'Your credits have been exhausted',
    freeCreditsAvailable: 'Get 5 free credits by inviting a friend',
    limitReached: 'You have reached your usage limit',
    continueWithCredits: 'Add credits to continue creating amazing designs',
    generationCompleted: 'Generation completed',
    designingPoster: '🎨 Veeso AI is designing your poster...',
    copy: 'Copy',
    error: 'Error',
    regenerate: 'Regenerate',
    task: {
      analysis: 'Requirement Analysis',
      typography: 'Typography',
      style: 'Design Style',
      layout: 'Page Layout',
      code: 'Design Building',
      build: 'Design Synthesis',
      image: 'Image Processing',
    },
    snapshot: {
      title: 'Project Snapshot',
      description: 'Project version snapshot',
      noPreviewAvailable: 'No preview available',
      restoreSnapshot: 'Restore This Version',
      viewSnapshot: 'View Snapshot',
      snapshotDescription: 'Project version snapshot',
      restoreConfirm: 'Are you sure you want to restore this version?',
      restoreSuccess: 'Version restored successfully',
      restoreFailed: 'Failed to restore design version.',
      created: 'Snapshot created',
      loading: 'Loading snapshot...',
    },
    chatMessage: {
      copyToClipboard: 'Copy to clipboard',
      copySuccess: 'Copied to clipboard',
      copySuccessDescription: 'Text has been copied successfully',
      copyFailed: 'Copy failed',
      copyFailedDescription: 'Unable to copy text to clipboard',
    },
  },
  exportMenu: {
    title: 'Export Settings',
    description: 'Choose format and quality for export',
    exportImage: 'Export',
    copyToClipboard: 'Copy to Clipboard',
    copying: 'Copying...',
    selectPage: 'Select Page',
    choosePage: 'Choose page to export',
    fileFormat: 'File Format',
    fileName: 'File Name',
    page: 'Page',
    selectPageError: 'Please select at least one page to export',
    exportAll: 'Export All',
    exportAllPages: 'Export all pages at once',
    selectFormat: 'Select format',
    pngFormat: 'PNG (High Quality)',
    jpgFormat: 'JPG (Compressed)',
    webpFormat: 'WebP (Modern Format)',
    pdfFormat: 'PDF (Document Format)',
    mp4Format: 'MP4 (Video)',
    pptFormat: 'PPT (Alpha)',
    htmlFormat: 'HTML',
    fps: 'FPS',
    duration: 'Duration',
    resolution: 'Resolution',
    selectResolution: 'Select resolution',
    standard: 'Standard (1x)',
    highRes: 'High Resolution (2x)',
    ultraHighRes: 'Ultra High Resolution (3x)',
    extremeHighRes: 'Extreme High Resolution ({{value}}x)',
    printOptimized: 'Print Optimized',
    dpi: 'DPI (Dots Per Inch)',
    selectDpi: 'Select DPI',
    cmykColorSpace: 'CMYK Color Space',
    cmykDescription: 'Optimized for professional printing',
    copiedToClipboard: 'Copied to Clipboard',
    copySuccess: 'Image has been successfully copied to clipboard',
    copiedAs: 'Copied as {{format}}',
    exporting: 'Exporting...',
    preparingDownload: 'Preparing your download...',
    downloadComplete: 'Your download is ready',
    downloadFailed: 'Download failed. Please try again.',
    success: 'Success',
    copyFailed: 'Copy failed. Please try again.',
    printSize: 'Print Size (cm)',
    customSize: 'Custom Size (cm)',
    quickPresets: 'Quick Presets',
    error: 'Error',
    cmykMode: 'CMYK Color Space',
    printOptimizedDescription: 'Optimize for professional printing',
    multiplePages: 'Multiple Pages',
    allPages: 'All Pages',
    currentPage: 'Current Page',
    downloadStarted: 'Download started',
    processingFiles: 'Processing files...',
    selectedPages: '{{count}} pages selected',
    viewingDistance: {
      veryFar: 'For billboards and very large formats (10+ m)',
      far: 'For viewing from a distance (3-10 m)',
      medium: 'For normal viewing distance (1-3 m)',
      close: 'For close inspection (0.3-1 m)',
      detailed: 'For detailed professional printing (< 0.3 m)',
    },
    sizeTable: {
      title: 'Standard Paper Size Reference Table',
      format: 'Format',
      width: 'Width',
      height: 'Height',
      dimensions: 'cm',
      clickToApply: 'Click to apply this size',
      customSize: 'Custom Size',
    },
    copyNotSupported: 'Copy Not Supported',
    copyNotSupportedDesc: "Your browser doesn't support copying images to clipboard",
    copyNotSupportedMultiple: 'Copying multiple images at once is not supported',
    bleedSize: 'Bleed Size (mm)',
    bleedSizeDescription: 'Extra area for trimming (recommended: 3mm)',
    badgeStyle: 'Badge Style',
    selectBadgeStyle: 'Select badge style',
    hideBadge: 'Hide badge',
    badgeProOnly: 'Pro users can choose to hide the footer badge',
  },
  batchCreate: {
    title: 'Batch Create',
    subtitle: 'Generate multiple designs from a CSV/JSON dataset',
    steps: {
      import: 'Import',
      map: 'Map',
      preview: 'Preview',
      create: 'Create',
    },
    import: {
      title: 'Import Data',
      description: 'Upload a CSV or JSON file to start batch generation.',
      dropTitle: 'Drop a file here or click to browse',
      dropHint: 'Supports CSV/JSON (max 10MB)',
      useSample: 'Use sample data',
      sampleLoaded: 'Sample data loaded ({{count}} records)',
      examples: 'Examples',
      csvFormat: 'CSV Format',
      jsonFormat: 'JSON Format',
      replaceFile: 'Replace file',
      reset: 'Reset',
    },
    map: {
      title: 'Map Fields',
      description: 'Connect data fields to text/image layers in the template.',
      dataPreview: 'Data Preview',
      recordsImported: '{{count}} records imported',
      showingFirst: 'Showing {{shown}} of {{total}} records',
      fieldMapping: 'Field Mapping',
      autoMap: 'Auto map',
      clearAll: 'Clear',
      selectField: 'Select a data field',
      current: 'Current',
      example: 'Example',
      mappedCount: '{{count}} mapped',
      unmappedCount: '{{count}} unmapped',
      searchPlaceholder: 'Search layers...',
    },
    preview: {
      title: 'Preview',
      empty: 'Map at least one field to generate a preview.',
      generating: 'Generating preview…',
      record: 'Record {{index}} / {{total}}',
      prev: 'Previous',
      next: 'Next',
      jumpTo: 'Go to',
      refresh: 'Refresh',
    },
    create: {
      title: 'Configure & Create',
      description: 'Set output preferences and generate files.',
      outputSettings: 'Output Settings',
      format: 'Format',
      itemsToCreate: 'Items to create',
      summary: 'Summary',
      totalRecords: 'Total records',
      mappedFields: 'Mapped fields',
      outputFormat: 'Output format',
      fileNamePrefix: 'Filename prefix',
    },
    buttons: {
      cancel: 'Cancel',
      back: 'Back',
      next: 'Next',
      create: 'Create Batch',
      addToCanvas: 'Add to Canvas',
      downloadImages: 'Download Images',
      close: 'Close',
    },
    errors: {
      fileTooLarge: 'File is too large (max 10MB).',
      unsupportedFormat: 'Unsupported file format. Please upload CSV or JSON.',
      parseFailed: 'Failed to parse the file. Please check the format and try again.',
      jsonArrayRequired: 'JSON must be an array of objects.',
      emptyData: 'No records found in the file.',
      selectAtLeastOneField: 'Please map at least one field to continue.',
    },
    progress: {
      preparing: 'Preparing…',
      rendering: 'Rendering {{current}} / {{total}}…',
      importing: 'Importing to canvas {{current}} / {{total}}…',
      downloading: 'Downloading…',
      complete: 'Complete!',
    },
    toast: {
      importedToCanvas: 'Imported {{count}} frames to canvas',
    },
  },
  signIn: {
    title: 'Sign in',
    createYourAccount: 'Create your account',
    signUp: 'Sign up',
    signIn: 'Sign in',
    welcomeBack: 'Welcome back!',
    joinToday: 'Join Veeso.ai today',
    googleUnavailable: 'Google Sign-in Unavailable',
    useEmailInstead: 'Please use email sign-in instead.',
    accessTools: 'Create stunning visuals in minutes. No design skills needed.',
    email: 'Email',
    emailPlaceholder: 'Enter your email',
    inputPlaceholder: 'Please enter',
    continueWith: 'Continue with',
    continueWithEmail: 'Continue with Email',
    continueWithPhone: 'Continue with Phone',
    phone: 'Phone',
    phonePlaceholder: 'Enter your phone number',
    smsCode: 'Code',
    emailCode: 'Code',
    smsCodePlaceholder: 'Enter 6-digit code',
    sendCode: 'Send Code',
    resendCode: 'Resend',
    smsCodeSent: 'SMS code sent successfully',
    smsCodeRequired: 'Please enter SMS code',
    phoneRequired: 'Please enter phone number',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm your password',
    inviteCode: 'Invite Code (Optional)',
    haveInviteCode: 'Have an invite code?',
    inviteCodePlaceholder: 'Enter your invite code',
    loginButton: 'Sign in',
    registerButton: 'Sign up',
    noAccount: "Don't have an account? Sign up",
    hasAccount: 'Already have an account? Sign in',
    loginFailed: 'Login failed. Please check your email and password and try again.',
    networkError: 'Network error',
    orContinueWith: 'Or continue with',
    passwordMismatch: 'Passwords do not match',
    agreeToTerms: 'By clicking "Continue with Google" or sign up, you agree with our',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    and: 'and',
    forgotPassword: 'Forgot your password?',
    agreeToTermsRequired: 'Please accept our Terms of Service and Privacy Policy to continue',
    emailRequired: 'Please enter your email address',
    invalidEmail: 'Please enter a valid email address',
    emailCodeSent: 'Email verification code sent successfully',
    emailMessages: {
      6001: 'Email is required',
      6002: 'Invalid email format',
      6003: 'Email already exists',
      6004: 'Temporary email addresses are not allowed',
      6005: 'Failed to send verification email',
    },
    messages: {
      6001: 'email already exists',
      6002: 'invalid email、phone number or password',
      6003: 'password is too short',
      6004: 'invalid email',
      6005: 'Temporary email addresses are not allowed',
    },
    smsMessages: {
      6001: 'Phone number is required',
      6002: 'Invalid phone number format',
      6003: 'Phone number is already registered',
      6004: 'Failed to send SMS verification code',
      6005: 'Internal server error',
    },
  },
  forgotPassword: {
    seoTitle: 'Reset Password - Veeso AI',
    seoDescription:
      'Reset your Veeso.ai account password securely. Enter your email to receive a verification code and create a new password.',
    seoKeywords: 'reset password,forgot password,password recovery,veeso ai,account recovery',
    title: 'Forgot Password?',
    description:
      "Enter your email address and we'll send you a verification code to reset your password.",
    resetTitle: 'Reset Your Password',
    resetDescription:
      "We've sent a verification code to {{email}}. Enter the code below along with your new password.",
    verificationCode: 'Verification Code',
    codePlaceholder: 'Enter the 6-digit code',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'Enter your new password (min 6 characters)',
    sendCode: 'Send Verification Code',
    sending: 'Sending...',
    resetButton: 'Reset Password',
    resetting: 'Resetting...',
    backToSignIn: '← Back to Sign In',
    backToEmail: '← Back to Email',
    successTitle: 'Password Reset Successfully!',
    successDescription:
      'Your password has been reset successfully. You can now sign in with your new password.',
    signInNow: 'Sign In Now',
    sideTitle: 'Secure Password Recovery',
    sideDescription:
      'Reset your password securely and get back to creating amazing designs with Veeso.ai',
    emailRequired: 'Please enter your email address',
    invalidEmail: 'Please enter a valid email address',
    sendCodeFailed: 'Failed to send verification code. Please try again.',
    codeRequired: 'Please enter the verification code',
    passwordRequired: 'Please enter a new password',
    passwordTooShort: 'Password must be at least 6 characters long',
    passwordMismatch: 'Passwords do not match',
    resetFailed: 'Failed to reset password. Please try again.',
  },
  designHeaderMenu: {
    back: 'Back',
    backToCreate: 'Back to Create',
    workspace: 'Workspace',
    view: 'View',
    canvasLayout: 'Canvas Layout',
    canvasLayoutFixed: 'Fixed layout',
    canvasLayoutFree: 'Free layout',
    canvasLayoutFreeHint: 'In free layout, you can freely move the artboards on the canvas.',
    layout: 'Layout',
    zoom: 'Zoom',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomToFit: 'Zoom to fit',
    shortcuts: 'Keyboard shortcuts',
    help: 'Help',
    docs: 'Docs',
    support: 'Support',
    untitled: 'Untitled',
    readonly: 'Read only',
    export: 'Export',
    project: 'Project',
    makePublic: 'Make Public',
    makePrivate: 'Make Private',
    visibility: 'Visibility',
    public: 'Public - Anyone with the link can view',
    private: 'Private - Only you can view',
    visibilitySuccess: 'Project visibility updated',
    visibilitySuccessDesc: 'Your project is now {{visibility}}',
    visibilityFailed: 'Failed to change visibility',
    visibilityFailedDesc:
      'There was an error changing your project visibility. Please try again later.',
    save: 'Save',
    saving: 'Saving...',
    savingDesc: 'Your project is being saved.',
    saveSuccess: 'Project Saved',
    saveSuccessDesc: 'Your project has been saved successfully.',
    edit: 'Edit',
    undo: 'Undo',
    redo: 'Redo',
    cut: 'Cut',
    copy: 'Copy',
    paste: 'Paste',
    duplicate: 'Duplicate',
    delete: 'Delete',
    selectAll: 'Select All',
    copyStyle: 'Copy Style',
    pasteStyle: 'Paste Style',
    saveFailed: 'Save Failed',
    saveFailedDesc: 'Failed to save your project. Please try again.',
    horizontalLayout: 'Horizontal',
    verticalLayout: 'Vertical',
    audioSettings: 'Audio Notification',
    audioSettingsDesc: 'Manage audio notification settings for design completion',
    audioEnabled: 'Enable Audio',
    audioEnabledDesc: 'Play sound when design generation completes',
    audioVolume: 'Volume',
    audioType: 'Sound Type',
    audioTypeSuccess: 'Success',
    audioTypeNotification: 'Notification',
    audioTypeChime: 'Chime',
    audioTest: 'Test Sound',
  },
  templates: {
    title: 'Templates',
    description: 'Explore our collection of professionally designed templates',
    loading: 'Loading templates...',
    filter: 'Filter by tag',
    allTags: 'All tags',
    noTemplates: 'No templates found',
    createFromTemplate: 'Create from template',
    backToAllTemplates: 'Back to All Templates',
    allTemplates: 'All Templates',
    categories: 'Categories',
    viewAll: 'View All',
    browseByCategory: 'Browse By Category',
    recentlyUpdated: 'Recently Updated',
  },
  templateDetail: {
    title: 'Template Details',
    backToTemplates: 'Back to Templates',
    useTemplate: 'Use This Template',
    loading: 'Loading template...',
    description: 'Description',
    tags: 'Tags',
    copyPrompt: 'Copy Prompt',
    remix: 'Remix',
    creating: 'Creating...',
    relatedTemplates: 'Related Templates',
    templateInfo: 'Template Information',
    dimensions: 'Dimensions',
    category: 'Category',
    createdBy: 'Created By',
    dateCreated: 'Date Created',
    lastModified: 'Last Modified',
    templateStats: 'Template Stats',
    usageCount: 'Usage Count',
    favoriteCount: 'Favorite Count',
    shareTemplate: 'Share Template',
    downloadTemplate: 'Download Template',
    similarTemplates: 'Similar Templates',
    editTemplate: 'Edit Template',
    previewTemplate: 'Preview Template',
    templateVersion: 'Version',
    templateSize: 'File Size',
    templateFormat: 'Format',
    prompt: 'Prompt',
    promptCopied: 'Prompt copied to clipboard!',
    copyPromptError: 'Failed to copy prompt',
    editContent: 'Edit Content',
    contentSpecifications: 'Content Specifications',
    model: 'Model',
    universal: 'Universal',
    version: 'Version',
    created: 'Created',
    updated: 'Updated',
    content: 'Content',
    moreElements: '+ {{count}} more elements',
    useOriginalPrompt: 'Use Original Prompt',
    chooseImage: 'Choose image',
    variable: 'Variable {{index}}',
    enterText: 'Enter text',
    copyToClipboard: 'Copy to clipboard',
  },
  templatesTag: {
    title: 'Templates by Tag',
    description: 'Explore templates filtered by specific tags',
    noTemplatesFound: 'No templates found with this tag',
    loading: 'Loading tagged templates...',
    viewAllTemplates: 'View All Templates',
    taggedWith: 'Templates tagged with {{tag}}',
  },
  help: {
    title: 'Help Center',
    faqTitle: 'Frequently Asked Questions',
    faqs: [
      {
        question: 'How to get started with Veeso.ai?',
        answer:
          'You can start using Veeso.ai by registering an account and logging in. We provide an easy-to-use interface and tutorials to help you get started quickly.',
      },
      {
        question: 'What file formats does Veeso.ai support?',
        answer: 'We support common image formats such as JPG, PNG, SVG, and Webp.',
      },
      {
        question: 'How to upgrade to the Pro version?',
        answer:
          'You can find the upgrade option in your account settings and choose a subscription plan that suits you.',
      },
      {
        question: 'How to get technical support?',
        answer:
          'You can check our help documentation or contact our technical support at support@veeso.ai.',
      },
      {
        question: 'How to export my designs?',
        answer:
          'After editing, click the export button in the top right corner and select your desired format（PNG/PDF/JPG） and resolution to download.',
      },
      {
        question: 'Can I collaborate with colleagues on Veeso.ai?',
        answer:
          "Currently, Veeso.ai doesn't support collaboration features. We're working on adding collaboration functionality in a future update.",
      },
      {
        question: 'Is there a mobile app for Veeso.ai?',
        answer: 'Currently, Veeso.ai is optimized for desktop use.',
      },
      {
        question: 'Can I use Veeso.ai for commercial projects?',
        answer:
          'Absolutely! Veeso.ai can be used for both personal and commercial projects. Our Pro version offers additional features for professional use.',
      },
      {
        question: 'Do you offer custom templates for specific industries?',
        answer:
          'Yes, we provide a wide range of templates categorized by industry and use case. You can also request custom templates through our support channel.',
      },
      {
        question: 'How often are new features added to Veeso.ai?',
        answer:
          'We regularly update Veeso.ai with new features and improvements. We release major updates quarterly and smaller enhancements monthly.',
      },
      {
        question: 'Can I cancel my subscription at any time?',
        answer:
          'Yes, you can cancel your subscription at any time from your account settings. Your access will continue until the end of your billing period.',
      },
    ],
    contactTitle: 'Need more help?',
    contactDescription:
      "If the FAQs don't solve your problem, please contact us through the following methods:",
    contactEmail: 'Email: help@veeso.ai',
    workingHours: 'Working hours: Monday to Friday 9:00 - 18:00',
  },
  notFound: {
    title: 'Page Not Found',
    description: "The page you're looking for doesn't exist or has been moved.",
    goHome: 'Go Back Home',
  },
  contact: {
    title: 'Contact Us',
    getInTouch: 'Get in Touch',
    contactDescription: 'For any inquiries or support, please contact us at:',
    contactInfo: 'Contact Info',
    email: 'Email: hi@veeso.ai',
    socialMedia: 'Social Media',
    followUs: 'Follow us for updates',
    twitterHandle: '@veesoAI',
    support: 'Support',
    supportDescription: 'Need help with our products?',
    business: 'Business',
    businessDescription: 'For partnerships & opportunities',
    contactEmail: 'hi@veeso.ai',
  },
  tools: {
    title: 'Tools',
    mainProduct: '🎨 Veeso AI',
    mainProductDesc: 'AI-powered design tool for social media and marketing',
    text2image: '✨ Text to Image',
    text2imageDesc: 'Generate images from text descriptions',
    video: '🎥 Landscape to Portrait Video Tool',
    videoDesc: 'Convert landscape videos to portrait format for social media',
    imageProcessing: '🖼️ Image Processing',
    imageProcessingDesc: 'Edit and enhance your images',
    imageGeneration: '🎨 Image Generation',
    imageGenerationDesc: 'Create AI-generated images',
    imageUpscaling: '⚡ Image Upscaling',
    imageUpscalingDesc: 'Enhance image resolution and quality',
    backgroundRemoval: '✂️ Background Removal',
    backgroundRemovalDesc: 'Remove image backgrounds automatically',
    magicEraser: '🪄 Magic Eraser',
    magicEraserDesc: 'Remove unwanted objects from images',
    playground: '🧩 Code to Canvas Playground',
    playgroundDesc: 'Convert HTML/React/SVG to canvas and experiment with designs',
    playgroundTitle: 'Code to Canvas Playground',
    playgroundSubtitle: 'Experiment with HTML/React/SVG and see it rendered on canvas',
    playgroundFeatures: 'Interactive design environment with real-time preview',
    playgroundUsage: 'Great for prototyping and testing design ideas',
  },
  videoTool: {
    title: '🎥 Landscape to Portrait Video Tool',
    description: 'Convert landscape videos to portrait format for social media',
    uploadVideo: 'Upload Video',
    uploadVideoDesc: 'Supports common formats like MP4, WebM',
    uploadBackground: 'Upload Background Image',
    uploadBackgroundDesc: 'Supports JPG, PNG and other common formats',
    clickToUploadVideo: 'Click to upload video',
    clickToUploadBackground: 'Click to upload background image',
    bitrate: 'Bitrate',
    encoder: 'Encoder',
    processing: 'Processing...',
    processProgress: 'Processing Progress',
    timeElapsed: 'Time Elapsed: {{time}}s',
    startMerge: 'Start',
    errorMessages: {
      uploadRequired: 'Please upload video and background image',
      resolutionTooHigh:
        'Video resolution is too high, please use lower resolution video or select higher encoding level',
      processFailed: 'Video processing failed, please try again',
    },
  },
  invite: {
    seo: {
      title: 'Invite Friends | Veeso AI Referral Program',
    },
    title: 'Invite Friends',
    guide: 'Get More Credits',
    credits: 'Credits',
    succeededDescription: 'Successfully verified, 200 credits awarded',
    pending: 'Pending',
    pendingDescription: 'Waiting for email verification',
    subtitle: 'Both you and your friend get 200 credits.',
    description: 'Both you and your friend get 200 credits.',
    validityPeriod: 'Referral credits valid for 30 days',
    inviteLink: 'Invitation Link',
    copyLink: 'Copy Link',
    copySuccess: 'Copied',
    copyDescription: 'Invitation link has been copied to clipboard',
    records: 'Invitation Records',
    recordCount: 'Total {{count}} records',
    noRecords: 'No invitation records',
    noRecordsDesc: 'Share your invitation link to see records',
    user: 'User',
    registered: 'Registered',
    usage: 'Usage',
    usageDescription: 'Used',
    pricingFrom: 'From $20',
    creditsAwarded: '+200 Credits',
    clappingHandAlt: 'Clapping hand illustration',
    howItWorks: {
      title: 'How it works',
      steps: [
        'Share your invite link with friends',
        'They sign up and verify their email',
        'Credits are added after verification',
      ],
    },
  },
  profile: {
    title: 'My Profile',
    usage: 'Usage',
    usageDescription: 'Credits Used',
    buyCredits: 'Get Credits Now',
    inviteFriends: 'Invite & Earn Credits',
    recentProjects: 'Recent Projects',
    viewAll: 'View All',
    createNew: 'Create New Project',
    sendVerification: 'Send Email verification',
    editProfile: 'Edit Profile',
    changeAvatar: 'Change Avatar',
    displayName: 'Display Name',
    bio: 'Bio',
    bioPlaceholder: 'Tell us about yourself',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    cancel: 'Cancel',
    profileUpdated: 'Profile updated successfully',
    updateFailed: 'Failed to update profile',
    characterLimit: '{{current}}/{{max}} characters',
    avatarUploadFailed: 'Failed to upload avatar',
    avatarUploadSuccess: 'Avatar uploaded successfully',
    loadMore: 'Load More Projects',
    notFound: 'User Not Found',
    notFoundDesc: 'The user profile you are looking for does not exist or may have been removed.',
    backHome: 'Back to Home',
    noProjects: 'No Public Projects Yet',
    noProjectsDesc: 'This user has not published any public projects yet. Check back later!',
  },
  success: {
    title: 'Payment Successful!',
    description: 'Your payment has been processed successfully. Thank you for your purchase.',
    dashboard: 'Go to Dashboard',
    createNew: 'Create New Design',
    seoTitle: 'Payment Successful - Veeso AI',
    confirmationMessage: 'Your credits have been added to your account',
    whatNext: "What's next?",
    startCreating: 'Start creating amazing designs with your credits',
    createNewDesc: 'Start a new design from scratch',
    dashboardDesc: 'View your projects and designs',
    exploreTemplates: 'Explore Templates',
    exploreTemplatesDesc: 'Browse professional templates',
    needHelp: 'Need help?',
    visitHelpCenter: 'Visit our Help Center',
    autoRedirect: 'Auto-redirecting in {seconds} seconds',
  },
  dashboard: {
    RecentDesigns: 'Recent Designs',
    ShowAll: 'Show All',
    ShowStarred: 'Show Starred',
    NewDesign: 'New design',
    CreateNewProject: 'Create New Project',
    LoadingProjects: 'Loading projects...',
    WelcomeTitle: 'Welcome to Veeso',
    WelcomeDescription:
      'Click the button below to add a new design project and start your creative journey.',
    NoProjects: 'No projects yet',
    StarredProjects: 'Starred Projects',
  },
  project: {
    open: 'Open',
    duplicate: 'Duplicate',
    delete: 'Delete',
    star: 'Star',
    unstar: 'Unstar',
    rename: 'Rename',
    lastEdited: 'Last edited',
    untitled: 'Untitled Project',
    confirmDelete: 'Are you sure you want to delete this project?',
    deleteSuccess: 'Project deleted successfully',
    duplicateSuccess: 'Project duplicated successfully',
  },
  auth: {
    emailVerificationRequired: 'Email Verification Required',
    verificationDescription: "Please verify your email address. We've sent a verification link to:",
    resendVerificationEmail: 'Resend Verification Email',
    sending: 'Sending...',
    verificationEmailSent: 'Verification email sent successfully!',
    checkInbox: 'Please check your inbox and spam folder for the verification email.',
    verificationEmailFailed: 'Failed to send verification email',
    tryAgainLater: 'Please try again later or contact support if the problem persists.',
    enterCode: 'Enter Code',
    cancel: 'Cancel',
    verify: 'Verify',
    verifying: 'Verifying...',
    verificationSuccess: 'Verification Successful!',
    emailVerified: 'Your email has been successfully verified.',
    verificationFailed: 'Verification Failed',
    invalidCode: 'Invalid verification code. Please try again.',
    close: 'Close',
    login: 'Login',
    signup: 'Sign Up',
    forgotPassword: 'Forgot Password?',
    resetPassword: 'Reset Password',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    name: 'Name',
    loginSuccess: 'Login successful!',
    signupSuccess: 'Sign up successful!',
    passwordResetSuccess: 'Password reset successful!',
    passwordResetRequest: 'Password reset link sent to your email',
    invalidCredentials: 'Invalid email or password',
    accountExists: 'Account already exists with this email',
    accountNotFound: 'No account found with this email',
    passwordMismatch: 'Passwords do not match',
    requiredField: 'This field is required',
  },
  posterStyles: {
    default: 'Style',
    service: 'Service Oriented',
    beauty: 'Beauty & Cosmetics',
    lifestyle: 'Lifestyle & Wellness',
    fashion: 'Fashion Forward',
    minimalist: 'Minimalist',
    lightshadow: 'Light & Shadow',
    gradient: 'Gradient Flow',
    vintage: 'Vintage Retro',
    geometric: 'Geometric Abstract',
    neon: 'Neon Glow',
    organic: 'Organic Nature',
    typographic: 'Typography Focus',
    isometric: 'Isometric 3D',
    glassmorphism: 'Glass Morphism',
    brutalism: 'Digital Brutalism',
  },
  resize: {
    title: 'Image Resize Tool',
    description: 'Resize your images to any dimension while maintaining quality',
    uploadImage: 'Upload Image',
    dropImage: 'Click to upload or drag and drop',
    supportedFormats: 'PNG, JPG, WEBP (MAX 10MB)',
    settings: 'Resize Settings',
    dimensions: 'Dimensions',
    width: 'Width',
    height: 'Height',
    maintainAspectRatio: 'Maintain aspect ratio',
    format: 'Format',
    quality: 'Quality',
    resize: 'Resize Image',
    processing: 'Processing...',
    reset: 'Reset',
    result: 'Result',
    download: 'Download Resized Image',
    noImageSelected: 'Please select an image first',
    processingError: 'Error processing image. Please try again.',
    imageLoadError: 'Error loading image. Please try a different file.',
  },
  border: {
    title: 'Image Border Tool',
    description:
      'Add a border to your pictures to make them stand out and win more likes on social media',
    uploadImage: 'Upload Image',
    livePreview: 'Live Preview',
    dropImage: 'Click to upload or drag and drop',
    supportedFormats: 'PNG, JPG, WEBP (MAX 10MB)',
    settings: 'Border Settings',
    width: 'Border Width',
    color: 'Border Color',
    style: 'Border Style',
    radius: 'Border Radius',
    format: 'Format',
    quality: 'Quality',
    apply: 'Apply Border',
    processing: 'Processing...',
    reset: 'Reset',
    result: 'Result',
    download: 'Download ',
    noImageSelected: 'Please select an image first',
    processingError: 'Error processing image',
    imageLoadError: 'Error loading image',
  },
  removeBg: {
    title: 'Background Removal Tool',
    description: 'Remove background from your images with one click using AI technology',
    uploadImage: 'Upload Image',
    dropImage: 'Click to upload or drag and drop',
    supportedFormats: 'PNG, JPG, WEBP (MAX 10MB)',
    settings: 'Output Settings',
    format: 'Format',
    quality: 'Quality',
    removeBackground: 'Remove Background',
    processing: 'Processing...',
    download: 'Download',
    reset: 'Reset',
    result: 'Result',
    preview: 'Preview',
    noImageSelected: 'Please select an image first',
    processingError: 'Error removing background from image',
    resultNote: 'Background has been removed. You can now download the image.',
    previewNote: "Click 'Remove Background' to process this image",
    rembg: 'Remove Background',
  },
  feedback: {
    title: 'How would you rate this AI-generated design?',
    thankYou: 'Thank you for your feedback!',
    goodQuality: 'Good',
    average: 'Average',
    poorQuality: 'Poor',
    submitSuccess: 'Feedback submitted successfully',
    submitError: 'Error submitting feedback. Please try again.',
    prompt: 'How would you rate this AI-generated design?',
    description: 'Your feedback helps us improve the AI design experience',
    poorQualityExplanation:
      'Poor generation quality may be due to unclear requirements or model limitations. Try adjusting your prompt or switching to a different model.',
    useAnotherModel: 'Use another model',
    tryDifferentModel: 'Explore alternative models for better results',
  },
  token: {
    title: 'API Tokens',
    description: 'Manage your API tokens for programmatic access',
    createNew: 'Create New Token',
    loading: 'Loading tokens...',
    noTokens: 'No API Tokens',
    noTokensDescription:
      "You haven't created any API tokens yet. Create one to access the API programmatically.",
    createFirst: 'Create Your First Token',
    name: 'Name',
    created: 'Created',
    key: 'Key',
    lastUsed: 'Last used',
    actions: 'Actions',
    delete: 'Delete',
    never: 'Never',
    securityNotice: 'Security Notice',
    securityDescription:
      'API tokens have full access to your account. Keep them secure and never share them publicly.',
    createToken: 'Create API Token',
    tokenName: 'Token Name',
    tokenNamePlaceholder: 'Enter a name for your token',
    tokenNameHelp: "Choose a descriptive name to identify this token's purpose",
    yourNewToken: 'Your New API Token',
    copyWarning: "Make sure to copy this token now. You won't be able to see it again!",
    done: 'Done',
    cancel: 'Cancel',
    create: 'Create Token',
    nameRequired: 'Token name is required',
    createError: 'Failed to create token',
    deleteSuccess: 'Token deleted successfully',
    deleteError: 'Failed to delete token',
    copySuccess: 'Token copied to clipboard',
    copyDescription: 'Keep this token secure',
    fetchError: 'Failed to load tokens',
  },
  inpaint: {
    title: 'Remove Objects',
    description: 'Paint over unwanted objects to remove them from your image',
    brushSize: 'Brush Size',
    brushSizeTooltip: 'Press [ and ] to quickly adjust brush size',
    history: 'History',
    clear: 'Clear',
    reset: 'Reset',
    saveChanges: 'Save changes',
    loading: 'Processing...',
    progress: 'Progress',
    zoom: 'Zoom',
    paintInstructions: 'Paint over the objects you want to remove',
    processingTime: 'Processing time: {{time}}s',
    noImageSelected: 'No image selected',
    exportSuccess: 'Image exported successfully',
    exportError: 'Failed to export image',
  },
  projectInfo: {
    page: 'Page',
    info: 'Project Info',
    save: 'Save',
    cancel: 'Cancel',
    name: 'Name',
    description: 'Description',
    namePlaceholder: 'Enter project name',
    descriptionPlaceholder: 'Enter project description',
    noDescription: 'No description',
    public: 'Public',
    private: 'Private',
    created: 'Created',
    updated: 'Updated',
  },
  redemption: {
    title: 'Redeem Your Code',
    subtitle: 'Enter your redemption code below to add credits to your account',
    seoTitle: 'Redeem Code - Veeso AI',
    seoDescription: 'Redeem your code to get credits for AI design generation',
    codeLabel: 'Redeem code',
    codePlaceholder: 'Enter your redemption code',
    redeemButton: 'Redeem',
    currentCredits: 'Current Credits',
    creditsDescription: 'Credits are used for AI design generation',
    helpTitle: 'How to use redemption codes?',
    help1: 'Enter your redemption code in the field above',
    help2: 'Click the "Redeem" button to add credits to your account',
    help3: 'Use your credits to generate amazing AI designs',
    emptyCode: 'Please enter a redemption code',
    success: 'Redemption Successful!',
    successDescription: 'Your benefits have been added to your account.',
    error: 'Redemption Failed',
    errorDescription: 'Invalid or expired redemption code.',
    alreadyRedeemed: 'You have already redeemed this code.',
    expiredOrLimit: 'This redemption code has expired or reached its usage limit.',
    invalidCode: 'Invalid or inactive redemption code.',
    credits: 'credits',
    redeemIllustrationAlt: 'Redeem illustration',
    type: {
      credits: 'Credits',
      subscription: 'Subscription',
      feature: 'Feature',
    },
  },
  themeSelector: {
    tooltip: 'Color Theme',
    default: 'Default',
    classicBlackWhite: 'Classic Black & White',
    oceanBlue: 'Ocean Blue',
    sunsetOrange: 'Sunset Orange',
    forestGreen: 'Forest Green',
    lavenderPurple: 'Lavender Purple',
    rosePink: 'Rose Pink',
    goldenLuxury: 'Golden Luxury',
    midnightBlue: 'Midnight Blue',
    defaultDescription: 'Default color scheme',
    classicBlackWhiteDescription: 'Classic black and white colors',
    oceanBlueDescription: 'Fresh ocean style',
    sunsetOrangeDescription: 'Warm sunset tones',
    forestGreenDescription: 'Natural forest colors',
    lavenderPurpleDescription: 'Elegant purple series',
    rosePinkDescription: 'Romantic pink tones',
    goldenLuxuryDescription: 'Premium golden colors',
    midnightBlueDescription: 'Deep night sky colors',
    emeraldLuxury: 'Emerald Luxury',
    emeraldLuxuryDescription: 'Luxurious emerald green tones',
    coralWarm: 'Coral Warm',
    coralWarmDescription: 'Vibrant coral warmth',
    indigoDeep: 'Indigo Deep',
    indigoDeepDescription: 'Deep indigo sophistication',
    amberWarm: 'Amber Warm',
    amberWarmDescription: 'Warm amber glow',
    tealFresh: 'Teal Fresh',
    tealFreshDescription: 'Fresh teal vibrancy',
    burgundyElegant: 'Burgundy Elegant',
    burgundyElegantDescription: 'Elegant burgundy richness',
    slateModern: 'Slate Modern',
    slateModernDescription: 'Modern slate sophistication',
    copperVintage: 'Copper Vintage',
    copperVintageDescription: 'Vintage copper warmth',
    turquoiseTropical: 'Turquoise Tropical',
    turquoiseTropicalDescription: 'Tropical turquoise freshness',
    plumRich: 'Plum Rich',
    plumRichDescription: 'Rich plum elegance',
    sageCalm: 'Sage Calm',
    sageCalmDescription: 'Calming sage serenity',
    charcoalSophisticated: 'Charcoal Sophisticated',
    charcoalSophisticatedDescription: 'Sophisticated charcoal depth',
    crimsonBold: 'Crimson Bold',
    crimsonBoldDescription: 'Bold crimson energy',
    navyProfessional: 'Navy Professional',
    navyProfessionalDescription: 'Professional navy confidence',
    mintFresh: 'Mint Fresh',
    mintFreshDescription: 'Fresh mint coolness',
    bronzeClassic: 'Bronze Classic',
    bronzeClassicDescription: 'Classic bronze elegance',
    steelIndustrial: 'Steel Industrial',
    steelIndustrialDescription: 'Industrial steel strength',
    ivoryElegant: 'Ivory Elegant',
    ivoryElegantDescription: 'Elegant ivory sophistication',
    mahoganyRich: 'Mahogany Rich',
    mahoganyRichDescription: 'Rich mahogany warmth',
    addCustomTheme: 'Add Custom Theme',
    hoverToPreview: 'Hover to preview colors',
    primaryColor: 'Primary Color',
    secondaryColor: 'Secondary Color',
    deleteCustomTheme: 'Delete Custom Theme',
    createCustomTheme: 'Create Custom Theme',
    createThemeDesc: 'Design your exclusive color scheme',
    themeName: 'Theme Name',
    themeDescription: 'Theme Description',
    themeNamePlaceholder: 'My Theme',
    themeDescriptionPlaceholder: 'Enter theme description',
    colorScheme: 'Color Scheme',
    smartColorExtraction: 'Smart Color Extraction',
    uploadImage: 'Upload image to extract colors',
    supportedFormats: 'Supports JPG, PNG formats, max 5MB',
    replaceImage: 'Replace',
    extractingColors: 'Extracting colors...',
    customColors: 'Custom Colors',
    primaryColorLabel: 'Primary Color',
    secondaryColorLabel: 'Secondary Color',
    color3Label: 'Background Color',
    color4Label: 'Border Color',
    preview: 'Preview',
    themePreview: 'Theme Preview',
    cancel: 'Cancel',
    saveTheme: 'Save Theme',
    copied: 'Copied!',
    pleaseSelectImage: 'Please select an image file',
    imageSizeLimit: 'Image size cannot exceed 5MB',
    colorExtractionFailed: 'Color extraction failed, please try again',
    imageLoadFailed: 'Image loading failed, please try again',
    imageProcessFailed: 'Image processing failed, please try again',
    cardExample: 'Card Example',
    cardExampleDesc: 'This is the card effect using your color scheme',
  },
  credits: {
    balance: 'Credit Balance',
    remaining: 'Remaining {{count}} credits',
    remainingCredits: 'Remaining credits',
    inviteFriends: 'Invite Friends',
    inviteReward: 'Both you and your friend get 200 credits.',
    recharge: 'Topup Credits',
    rechargeDesc: 'Purchase credit packages and enjoy more design features',
    rechargeNow: 'Buy Credits',
    exhausted: 'Credits exhausted. Please topup to continue.',
  },
  attach: {
    addAttachment: 'Add Attachment',
    addReferenceImage: 'Upload the design you want to reference',
    uploadImageMaterial: 'Upload materials needed for your designs',
    uploading: 'Uploading...',
    supportedFormats: 'Supports PNG, JPG, JPEG, WEBP',
    selectMaterial: 'Select Material',
    failedToLoadAssets: 'Failed to load assets',
    material: 'Material',
    remove: 'Remove',
    asUsage: 'as {{usage}}',
    defaultUsage: 'Material',
    asReference: 'as {{usage}}',
    defaultReference: 'Reference',
    parsing: 'Parsing...',
    Category: {
      recommended: 'Recommended',
      businessMarketing: 'Business Marketing',
      creativeArt: 'Creative Art',
    },
    referenceImage: 'Reference',
    materialLibrary: 'Material',
    documents: 'Document',
    addDocuments: 'Upload document for one-click layout',
    supportedDocumentFormats: 'Supports DOCX, PDF, TXT, MD formats (max 15MB)',
    dragAndDropFiles: 'Drag and drop files here',
    or: 'or',
    browseFiles: 'Browse Files',
    uploadSuccessful: 'Upload successful',
    uploadFailed: 'Upload failed',
    fileTooBig: 'File size exceeds 10MB limit',
    unsupportedFormat: 'Unsupported file format',
    multipleFilesSupported: 'Multiple files supported',
    attachmentManager: 'Attachment Manager',
    noAttachments: 'No attachments',
    attachments: 'attachments',
  },
  material: {
    selectUsage: 'Select Usage',
    singleSelect: '(Single Select)',
    background: 'Background',
    product: 'Product',
    model: 'Model',
    logo: 'Logo',
    qrcode: 'QR Code',
    icon: 'Icon',
    decoration: 'Decoration',
    texture: 'Texture',
    chart: 'Chart',
    customUsagePlaceholder: 'Custom usage',
    add: 'Add',
    clickToSetUsage: 'Click to set usage',
    or: 'or',
  },
  editor: {
    commands: 'Commands',
    brand: 'Brand',
    slogan: 'Slogan',
    title: 'Title',
    description: 'Description',
    cta: 'Call to Action',
    insertTag: 'Insert tag',
    uploadImage: 'Upload Image',
    uploadImageDesc: 'Upload an image',
    insertColor: 'Insert Color',
    insertColorDesc: 'Add a color',
    clear: 'Clear All',
    clearDesc: 'Clear editor content',
  },
  color: {
    edit: 'Edit color',
    remove: 'Remove',
    editColor: 'Edit Color',
    preview: 'Preview',
    aiSuggest: 'AI Color Suggestions',
    colorTag: 'Color Tag',
    invalidColor: 'Invalid color value',
    colorInputHint:
      'Type a HEX color (e.g., #FF5500) then press space to auto-convert to a color tag',
  },
  attachTag: {
    editReferenceFeatures: 'Edit Reference Features',
    selectReferencePoints: 'Reference Points',
    multipleSelection: '(Multiple Selection)',
    referencePoints: {
      all: 'All',
      layout: 'Layout',
      style: 'Style',
      color: 'Color',
      texture: 'Texture',
      copy: 'Copy',
      font: 'Font',
    },
  },
  materialList: {
    viewMore: 'View more',
    selectUsage: 'Usage',
    selectedMaterials: 'Selected Materials',
    noSelectedMaterials: 'No materials selected',
    recentlyUploadedMaterials: 'Recently Uploaded Materials',
    failedToLoadAssets: 'Failed to load assets',
    noMaterials: 'No materials available',
    uploadMaterialsToUse: 'Upload materials to use them in your designs',
  },
  fillPanel: {
    color: 'Solid',
    solid: 'Solid',
    gradient: 'Gradient',
    dynamic: 'Dynamic Effects',
    image: 'Image',
    search: 'Search',
    uploadImage: 'Upload image',
    gradientDirection: 'Gradient Direction',
    documentColors: 'Document Colors',
    gradientType: {
      linear: 'Linear',
      radial: 'Radial',
      conic: 'Conic',
      mesh: 'Mesh',
    },
  },
  iconLibrary: {
    iconLibrary: 'Icon Library',
    chooseFrom: 'Choose from',
    icons: 'icons',
    searchIcons: 'Search icons...',
    allIcons: 'All Icons',
    noIconsFound: 'No icons found',
    tryDifferentKeywords: 'Try different keywords or select another category',
    showAllIcons: 'Show All Icons',
    loadingIconSets: 'Loading icon sets...',
  },
  qrcodePanel: {
    colorPresets: 'Color Presets',
    qrCodeGenerator: 'QR Code Generator',
    createCustomQrCodes: 'Create custom QR codes for your designs',
    content: 'Content',
    enterUrlOrText: 'Enter URL or text...',
    styleSettings: 'Style Settings',
    margin: 'Margin',
    foreground: 'Foreground',
    background: 'Background',
    generating: 'Generating...',
    generateQrCode: 'Generate QR Code',
    addToDesign: 'Add to Design',
    createQrCode: 'Create QR Code',
    enterContentAndCustomize:
      'Enter your content and customize the style to generate a beautiful QR code',
  },
  assetUpload: {
    uploadImage: 'Upload Image',
    dragDropOrClick: 'Drag & drop your image here, or click to browse',
    chooseFile: 'Choose File',
    uploadingImage: 'Uploading image...',
    supportedFormats: 'JPG, PNG, WebP, SVG',
  },
  imagePanel: {
    imageTypes: 'Image Types',
    unsplash: 'Images',
    highQualityImages: 'High quality images',
    icon: 'Icons',
    beautifulIcons: 'Beautiful icons',
    qr: 'QR Code',
    generateQrCode: 'Generate QR code',
    generate: 'AI Generate',
    generateImage: 'Generate images with AI',
    selectImageType: 'Select image type',
    browse: 'Browse',
    search: 'Search',
    upload: 'Upload',
    illustration: 'Illustration',
    illustrationDesc: 'Beautiful illustrations for your designs',
  },
  quickQrCode: {
    editQrCode: 'Edit QR Code',
    qrCodeEditor: 'QR Code Editor',
    typeSelection: 'Type Selection',
    content: 'Content',
    generating: 'Generating...',
    generateQrCode: 'Generate QR Code',
    dragToAdd: 'Drag to add to canvas',
    advancedSettings: 'Advanced Settings',
    foregroundColor: 'Foreground Color',
    backgroundColor: 'Background Color',
    margin: 'Margin',
    size: 'Size',
    download: 'Download',
    preview: 'Preview',
    enterContentToGenerate: 'Enter content to generate QR code',
    selectImageFirst: 'Select an image first',
    replaceTitle: 'Select a QR code or image within the canvas to replace it',
    replace: 'Replace',
    types: {
      text: 'Text',
      url: 'Link',
      email: 'Email',
      phone: 'Phone',
      wifi: 'WiFi',
      contact: 'Contact',
    },
    placeholders: {
      text: 'Enter any text',
      url: 'https://example.com',
      email: 'user@example.com',
      phone: '+1 555 123 4567',
      wifi: 'WiFi Name;Password;Encryption Type',
      contact: 'Name;Phone;Email',
    },
    generateFailed: 'Failed to generate QR code',
    tips: {
      dragToCanvas: 'Drag the QR code to canvas to add it',
      selectImageNode: 'Select an image node to replace with QR code',
      adjustSettings: 'Adjust margin and colors to customize appearance',
    },
  },
  chatContent: {
    referenceImages: 'Reference Images',
    documents: 'Document',
    colorScheme: 'Color Scheme',
    materialFiles: 'Material Files',
    libraryItems: 'Library Items',
    designLibrary: 'Design Library',
    material: 'Material',
    style: 'Style',
  },
  modelSelector: {
    selectModel: 'Select Model',
    recommendedModels: 'Recommended Models',
    allModels: 'All Models',
    exploreMore: 'Explore More Models',
    additionalModels: 'More models available',
    otherModels: 'More Models',
    showLess: 'Show Less',
    mostPopular: 'Most Popular',
    multimodal: 'Supports Image Input',
    limitedTimeFree: 'Limited Time Free',
    new: 'New',
    promoDefault: 'Gemini 3 Pro Now Available',
    promoGemini: 'Yes, this is the one, just try it',
  },
  browserCompatibility: {
    error: {
      title: 'Browser Compatibility Error',
      description: "Your browser doesn't support required features.",
      checkDetails: 'Check compatibility details',
    },
    checkDetails: 'Check browser compatibility details',
  },
  textEffect: {
    title: 'Text Effects',
    description: 'Select preset styles',
    clearEffects: 'Clear Effects',
    textBackground: 'Text Background',
    groups: {
      favorites: 'Favorites',
      recent: 'Recent',
      whiteOutline: 'White Outline',
      blackOutline: 'Black Outline',
      gradientModern: 'Gradient Modern',
      creativeEffects: 'Creative Effects',
      textWithBackground: 'Text with Background',
      neonGlow: 'Neon Glow',
      depth3d: '3D Depth',
      retroVibes: 'Retro Vibes',
      gradientText: 'Gradient Text',
      boldOutlines: 'Bold Outlines',
      premiumStroke: 'Premium Stroke',
      minimalElegance: 'Minimal Elegance',
      vibrantPop: 'Vibrant Pop',
    },
  },
  nodeTypes: {
    text: 'Text',
    image: 'Image',
    shape: 'Shape',
    line: 'Line',
    path: 'Path',
    col: 'Column',
    row: 'Row',
    group: 'Group',
    frame: 'Frame',
    video: 'Video',
  },
  shapeTypes: {
    rect: 'Rectangle',
    ellipse: 'Ellipse',
    triangle: 'Triangle',
    star: 'Star',
    polygon: 'Polygon',
    hexagon: 'Hexagon',
    octagon: 'Octagon',
    pentagon: 'Pentagon',
    cross: 'Cross',
    heart: 'Heart',
  },
  browser: {
    title: 'Browser Compatibility',
    description: "Check your browser's compatibility with Veeso AI features",
    overallStatus: 'Overall Status',
    browserInfo: 'Browser Info',
    featureDetails: 'Feature Details',
    status: 'Status',
    retest: 'Retest',
    testing: 'Testing...',
    analyzing: 'Analyzing compatibility...',
    checkComplete: 'Compatibility Check Complete',
    checkCompleteDesc: 'Browser compatibility analysis finished successfully.',
    checkFailed: 'Check Failed',
    checkFailedDesc: 'Failed to run compatibility check.',
    recommendChrome: 'Recommended Browser: Chrome',
    recommendChromeDesc:
      'For the best Veeso AI experience, we strongly recommend using the latest version of Google Chrome browser.',
    downloadChrome: 'Download Chrome',
    statuses: {
      fullyCompatible: 'Fully Compatible',
      compatibleWithWarnings: 'Compatible with Warnings',
      incompatible: 'Incompatible',
      unknown: 'Unknown',
      supported: 'Supported',
      notSupported: 'Not Supported',
    },
    categories: {
      required: 'Required',
      optional: 'Optional',
      compatible: 'Compatible',
      browser: 'Browser',
      version: 'Version',
      engine: 'Engine',
    },
    results: {
      pass: 'PASS',
      fail: 'FAIL',
      warn: 'WARN',
      yes: 'YES',
      no: 'NO',
    },
    issues: {
      criticalTitle: 'Critical Issues',
      criticalDesc: 'Update your browser for Veeso AI compatibility.',
      performanceTitle: 'Performance Warnings',
      performanceDesc: 'Some features may have reduced performance.',
      excellentTitle: 'Excellent',
      excellentDesc: 'Full compatibility with all Veeso AI features.',
    },
  },
  announcement: {
    title: 'Product Announcements',
    description:
      'Check the latest product updates, feature releases and important announcements from Veeso AI',
    keywords: 'product announcements,update logs,feature releases,Seede AI',
    pageTitle: 'Product Announcements',
    pageDescription: 'Explore the latest features and product progress',
    loading: 'Loading...',
    noAnnouncements: 'No announcements',
    noAnnouncementsDesc: 'Awaiting new messages',
    subscribeTitle: 'Subscribe to Updates',
    subscribeDesc: 'Get important update notifications first',
    subscribeButton: 'Subscribe Now',
    error: 'Error',
    loadError: 'Failed to load announcements, please try again later.',
    types: {
      feature: 'Feature',
      improvement: 'Improvement',
      promotion: 'Promotion',
    },
    priorities: {
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
    tags: {
      modelUpdate: 'Model Update',
      ai: 'AI',
      reasoningModel: 'Reasoning Model',
      newFeature: 'New Feature',
      uiOptimization: 'UI Optimization',
      userExperience: 'User Experience',
      performanceOptimization: 'Performance Optimization',
      systemUpgrade: 'System Upgrade',
      promotion: 'Promotion',
      discount: 'Discount',
    },
  },
  imageGeneratePanel: {
    title: 'AI Image Generation',
    subtitle: 'Create infinite possibilities with creative words',
    history: 'History',
    styleSection: 'Art Style',
    aiEnhanceTitle: 'AI Smart Enhancement',
    aiEnhanceDesc: 'Automatically translate and optimize your creative description',
    promptEnhanced: 'Prompt Enhanced',
    promptPlaceholder:
      'Describe the image in your mind, let AI present the beauty of art for you...',
    selectModel: 'Select Model',
    creditsCost: 'Costs {{n}} credits',
    modelTagBeta: 'Beta',
    estimatedWaitLabel: 'Est. wait',
    modelWaitDurations: {
      gptImage2: '3–5 min',
      gemini3ProImage: '2–3 min',
      gemini25FlashImage: '2–3 min',
      flux: '1–3 min',
    },
    startCreate: 'Start Creating',
    headerStageTitle: 'AI asset images',
    headerPeakWaitHint: 'Longer waits at peak times',
    resultTitle: 'Generation Result',
    creating: 'Creating...',
    createDone: 'Creation Complete',
    optimizingPrompt: 'Optimizing your creative description...',
    creatingImage: 'Carefully creating your image...',
    pleaseWait: 'Please wait, art is being born',
    selectArtwork: 'Select Artwork',
    generatedArtworkAlt: 'Generated artwork',
    generationFailed: 'Generation Failed',
    retry: 'Try Again',
    styleTitle: 'Style Selection',
    error: {
      generationFailedTitle: 'Generation Failed',
      generationFailed: 'Failed to generate image, please try again',
    },
    styles: {
      default: 'Default',
      defaultDesc: 'Natural style',
      cartoon: 'Cartoon',
      cartoonDesc: 'Cute cartoon style',
      photorealistic: 'Photorealistic',
      photorealisticDesc: 'Photorealistic style',
      render3d: '3D Render',
      render3dDesc: 'Exquisite 3D effects',
      oilPainting: 'Oil Painting',
      oilPaintingDesc: 'Classic oil painting texture',
      watercolor: 'Watercolor',
      watercolorDesc: 'Fresh watercolor style',
      cyberpunk: 'Cyberpunk',
      cyberpunkDesc: 'Futuristic sci-fi style',
      minimalist: 'Minimalist',
      minimalistDesc: 'Simple modern style',
    },
  },
  imageSearchPanel: {
    title: 'Image Search',
    subtitle: 'Find perfect materials for you',
    searchPlaceholder: 'Describe the image you need, intelligently match for you...',
    recommendedCategories: 'Recommended Categories',
    smartSuggestions: 'Smart Suggestions',
    searchResults: 'Search Results',
    backToSearch: 'Back to Search',
    resultsShown: 'images shown',
    searching: 'AI is searching for you...',
    importing: 'Importing...',
    use: 'Use',
    loadMore: 'Load More',
    loadingMore: 'Loading more images...',
    noResults: 'No related images found',
    tryDifferentKeywords: 'Try using different keywords',
    searchAgain: 'Search Again',
    allResultsShown: 'All results shown',
    categories: {
      landscape: 'Landscape',
      landscapeDesc: 'Natural scenery',
      abstract: 'Abstract',
      abstractDesc: 'Abstract art',
      business: 'Business',
      businessDesc: 'Business style',
      technology: 'Technology',
      technologyDesc: 'Tech feel',
      lifestyle: 'Lifestyle',
      lifestyleDesc: 'Life scenes',
      creative: 'Creative',
      creativeDesc: 'Creative design',
    },
    suggestions: {
      hdWallpaper: 'HD wallpaper background',
      productDisplay: 'Product display image',
      socialMedia: 'Social media materials',
      presentation: 'Presentation background',
      webBanner: 'Website banner image',
    },
  },
  transformPanel: {
    title: 'Transform',
    presets: 'Presets',
    parameters: 'Custom',
    reset: 'Reset',
    mixed: 'Mixed',
    matrix: {
      identity: 'Identity',
      identityDesc: 'No transformation',
      flipX: 'Flip X',
      flipXDesc: 'Horizontal flip',
      flipY: 'Flip Y',
      flipYDesc: 'Vertical flip',
      rotate90: 'Rotate 90°',
      rotate90Desc: 'Rotate 90 degrees',
      rotate180: 'Rotate 180°',
      rotate180Desc: 'Rotate 180 degrees',
      rotate270: 'Rotate 270°',
      rotate270Desc: 'Rotate 270 degrees',
      skewX: 'Skew X',
      skewXDesc: 'Skew horizontally',
      skewY: 'Skew Y',
      skewYDesc: 'Skew vertically',
      scaleUp: 'Scale Up',
      scaleUpDesc: 'Enlarge by 20%',
      scaleDown: 'Scale Down',
      scaleDownDesc: 'Shrink by 20%',
    },
  },
  imageEditor: {
    title: 'Image Editor',
    aspectRatio: 'Aspect Ratio',
    loading: 'Loading...',
    reset: 'Reset',
    saveChanges: 'Save Changes',
  },
  gradientPicker: {
    preview: 'Preview',
    type: 'Type',
    angle: 'Angle',
    colorStops: 'Color Stops',
    addStop: 'Add Stop',
    color: 'Color',
    colorValue: 'Color value',
    position: 'Position',
    linear: 'Linear',
    radial: 'Radial',
    conic: 'Conic',
    repeating: 'Repeating',
  },
  save: {
    saving: 'Saving...',
    saved: 'Saved',
    error: 'Save failed',
    unsaved: 'Unsaved changes',
  },
  shapePanel: {
    shape: 'Shape',
    selectShape: 'Select Shape',
    changeShapeDesc: 'Change shape for selected elements',
    rectangle: 'Rectangle',
    triangle: 'Triangle',
    circle: 'Circle',
    star: 'Star',
    polygon: 'Polygon',
    hexagon: 'Hexagon',
    octagon: 'Octagon',
    pentagon: 'Pentagon',
    cross: 'Cross',
    heart: 'Heart',
  },
  borderPanel: {
    border: 'Border',
    customizeBorder: 'Customize border color and width',
  },
  clipPathPanel: {
    clipPath: 'Clip Path',
    none: 'None',
    triangle: 'Triangle',
    parallelogram: 'Parallelogram',
    leftTiltedParallelogram: 'Left Tilted Parallelogram',
    trapezoid: 'Trapezoid',
    star: 'Star',
    pentagon: 'Pentagon',
    hexagon: 'Hexagon',
    octagon: 'Octagon',
    circle: 'Circle',
    selectClipPath: 'Select Clip Path',
    applyClipPath: 'Apply clip path to selected elements',
  },
  chatPanel: {
    local: 'Local',
    global: 'Global',
    localEdit: 'Local Edit:',
    localEditDesc: 'Edit only the currently selected elements, keeping other content unchanged',
    globalEdit: 'Global Edit:',
    globalEditDesc: 'Modify and reorganize the entire page',
  },
  recentAssetPanel: {
    all: 'All',
    favorites: 'Favorites',
    noFavoriteAssets: 'No Favorite Assets',
    noRecentAssets: 'No Recent Assets',
    favoriteAssetsWillAppearHere: 'Your favorite assets will appear here',
    recentlyUsedAssetsWillAppearHere: 'Your recently used assets will appear here',
    today: 'Today',
    yesterday: 'Yesterday',
    removeFromFavorites: 'Remove from favorites',
    addToFavorites: 'Add to favorites',
  },
  frameMenu: {
    lockPage: 'Lock page',
    unlockPage: 'Unlock page',
    duplicatePage: 'Duplicate page',
    moreOptions: 'More options',
    moreActions: 'More actions',
    moveToTop: 'Move to top',
    moveUp: 'Move up',
    moveDown: 'Move down',
    moveToBottom: 'Move to bottom',
    deletePage: 'Delete page',
  },
  textFillMode: {
    default: 'Default',
    char: 'Character',
    line: 'Line',
  },
  shaderEditor: {
    shaderEffects: 'Dynamic Effects',
    effectType: 'Effect Type',
    presets: 'Presets',
    preset: 'Preset',
    enableAnimation: 'Enable Animation',
    disableAnimation: 'Disable Animation',
    generateRandomSeed: 'Generate Random Seed',
    resetParameters: 'Reset Parameters',
    moreSettings: 'More Settings',
    shaderEditorDisabled: 'Shader editor is disabled',
    addColor: 'Add',
    seed: 'Seed',
    speed: 'Speed',
    animated: 'Animated',
  },
  imageMenu: {
    aiImage: 'AI Image',
    icons: 'Icons',
    QRCode: 'QR Code',
    title: 'AI Image Editor',
    description: 'Edit your image with AI',
    modelOptions: {
      gemini: 'Gemini',
      openai: 'GPT-4o',
      comingSoon: 'Coming soon',
    },
    placeholder: 'Describe the image modifications you want...',
    generate: 'Edit Image',
    loading: 'Generating...',
    editImage: 'AI Image Edit',
    selectModel: 'Select Model',
  },
  iconMenu: {
    iconLibrary: 'Icon Library',
    searchIcons: 'Search icons...',
    allIcons: 'All Icons',
    noIconsFound: 'No icons found',
    iconsAvailable: 'icons available',
    loadingIcons: 'Loading icons...',
    selectIconSet: 'Select Icon Set',
    categories: {
      all: 'All',
      general: 'General',
      arrows: 'Arrows',
      arrow: 'Arrow',
      media: 'Media',
      ui: 'UI',
      business: 'Business',
      technology: 'Technology',
      social: 'Social',
      travel: 'Travel',
      food: 'Food',
      weather: 'Weather',
      sports: 'Sports',
      medical: 'Medical',
      education: 'Education',
      shopping: 'Shopping',
      communication: 'Communication',
      design: 'Design',
      finance: 'Finance',
      security: 'Security',
      entertainment: 'Entertainment',
      file: 'File',
      alert: 'Alert',
      health: 'Health',
      tools: 'Tools',
      people: 'People',
    },
  },
  unsplashPanel: {
    title: 'Images',
    description: 'Use high-quality visual materials',
    searchPlaceholder: 'Search beautiful images...',
    searchButton: 'Search',
    loadingImages: 'Finding beautiful images...',
    loadMore: 'Load More',
    loading: 'Loading...',
    noImages: 'No Images',
    noImagesDescription: 'Try adjusting your search keywords',
    browseSelected: 'Browse Selected Images',
    use: 'Use',
    emptyState: {
      title: 'No images found',
      description: 'Try adjusting your search keywords',
    },
    presets: {
      featured: 'Featured',
      nature: 'Nature',
      abstract: 'Abstract',
      texture: 'Texture',
      gradient: 'Gradient',
    },
  },
  quickEditMenu: {
    placeholder: 'Ask for quick changes...',
    loading: 'Processing...',
    send: 'Send',
    addImage: 'Add image',
    duplicate: 'Duplicate',
    delete: 'Delete',
    error: 'Failed to process request',
    more: 'More',
    selectChildren: 'Select Children',
    shortcuts: {
      duplicate: '⌘D',
      delete: '⌫',
    },
    presets: {
      aiQuickPresets: 'AI Quick Presets',
      textEditing: 'Text Editing',
      imageProcessing: 'Image Processing',
      shapeEffects: 'Shape Effects',
      rewrite: 'Rewrite',
      refine: 'Refine',
      professional: 'Professional',
      brand: 'Brand Tone',
      concise: 'Concise',
      translate: 'Translate',
      correct: 'Correct',
      expand: 'Expand',
      summary: 'Summary',
    },
    prompts: {
      rewrite: 'Rewrite this text, keeping the original meaning but using different expressions',
      refine: 'Refine this text, remove redundancy and keep core information',
      professional: 'Rewrite the text in a more professional tone',
      brand: 'Rewrite the text to be more brand-focused',
      concise: 'Rewrite the text to be more concise',
      translate: 'Translate the text to another language (Chinese to English, English to Chinese)',
      correct: 'Check and correct grammar and spelling errors in the text',
      expand: 'Expand this text with more details and content',
      summary: 'Generate a concise summary of this text',
    },
  },
  textAlign: {
    left: 'Left',
    center: 'Center',
    right: 'Right',
    justify: 'Justify',
  },
  helpCenter: {
    title: 'Help Center',
    description: 'Everything you need to master Veeso Design',
    tabs: {
      shortcuts: 'Shortcuts',
      features: 'Features',
      tips: 'Tips',
    },
    shortcuts: {
      essential: {
        title: 'Essential',
        undo: 'Undo last action',
        redo: 'Redo last action',
        copy: 'Copy selected element',
        paste: 'Paste element',
        delete: 'Delete selected element',
        save: 'Save project',
      },
      selection: {
        title: 'Selection & Layers',
        selectAll: 'Select all elements',
        group: 'Group selected elements',
        ungroup: 'Ungroup selected elements',
        duplicate: 'Duplicate selected element',
        bringToFront: 'Bring to front',
        sendToBack: 'Send to back',
      },
      view: {
        title: 'View & Navigation',
        zoomToFit: 'Zoom to fit canvas',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        resetZoom: 'Reset zoom to 100%',
        pan: 'Pan canvas (hold and drag)',
      },
      text: {
        title: 'Text Editing',
        bold: 'Bold text',
        italic: 'Italic text',
        underline: 'Underline text',
        increaseFontSize: 'Increase font size',
        decreaseFontSize: 'Decrease font size',
      },
      help: {
        title: 'Help & Interface',
        openHelp: 'Open this help panel',
        closeDialogs: 'Close dialogs or deselect',
      },
    },
    features: {
      aiAssistant: {
        title: 'Smart AI Assistant',
        description:
          'Chat with AI to generate designs, modify elements, and get creative suggestions',
        tips: [
          'Ask for specific design changes',
          'Request color palette suggestions',
          'Get layout recommendations',
        ],
      },
      textTools: {
        title: 'Advanced Text Tools',
        description: 'Rich text editing with custom fonts, effects, and typography controls',
        tips: [
          'Choose fonts that best match your design',
          'Use text effects and shadows',
          'Apply gradient text fills',
        ],
      },
      imageProcessing: {
        title: 'Image Processing',
        description: 'Built-in image editing, background removal, and filter effects',
        tips: [
          'Remove backgrounds automatically',
          'Apply artistic filters',
          'Adjust brightness and contrast',
        ],
      },
      export: {
        title: 'Export Options',
        description: 'Export in multiple formats: PNG, JPG, PDF, SVG with custom dimensions',
        tips: [
          'Choose optimal format for your use case',
          'Set custom export dimensions',
          'Batch export multiple designs',
        ],
      },
      collaboration: {
        title: 'Collaboration',
        description: 'Share designs, collaborate in real-time, and manage project permissions',
        tips: ['Share via link', 'Set view/edit permissions', 'Track version history'],
      },
    },
    tips: {
      gettingStarted: {
        title: 'Getting Started',
        items: [
          'Start with a template and customize it to save time',
          'Use the AI chat to describe what you want to create',
          'Drag and drop images directly onto the canvas',
          'Double-click text elements to edit them quickly',
        ],
      },
      design: {
        title: 'Design Best Practices',
        items: [
          'Maintain consistent spacing between elements',
          'Use a limited color palette for cohesive designs',
          'Choose fonts that complement each other',
          'Keep important text readable with good contrast',
        ],
      },
      productivity: {
        title: 'Productivity Tips',
        items: [
          'Use keyboard shortcuts for faster workflow',
          'Group related elements for easier management',
          'Save frequently used elements as components',
          'Use the grid and alignment tools for precision',
        ],
      },
      advanced: {
        title: 'Advanced Techniques',
        items: [
          'Layer multiple effects for rich visual depth',
          'Use masks to create complex compositions',
          'Experiment with blend modes for unique effects',
          'Create custom color gradients for backgrounds',
        ],
      },
    },
    footer: {
      helpShortcut: 'to open this help',
      documentation: 'Need more help? Visit our documentation',
    },
  },
  createAssetPicker: {
    historyTitle: 'History',
    viewAll: 'View All',
    materialLibrary: 'Material Library',
    materialLibraryDesc: 'Select materials to add to your creation',
    loading: 'Loading...',
    loadingMaterials: 'Loading materials...',
    error: 'Failed to load',
    noMaterials: 'No materials',
    noMaterialsDesc: 'No materials available, uploaded items will appear here',
    showingAll: 'All materials displayed',
    retryLoad: 'Retry loading',
  },
  layerTree: {
    title: 'Layers',
    searchPlaceholder: 'Search layers...',
    showOnlyMatches: 'Show only matches',
    multiSelectMode: 'Multi-select mode',
    density: {
      standard: 'Standard',
      compact: 'Compact',
    },
    actions: {
      toggleLock: 'Toggle lock',
      toggleVisibility: 'Toggle visibility',
      bringForward: 'Bring forward',
      sendBackward: 'Send backward',
      expand: 'Expand',
      collapse: 'Collapse',
      dragToReorder: 'Drag to reorder',
      hideLayer: 'Hide layer',
      showLayer: 'Show layer',
      lockLayer: 'Lock layer',
      unlockLayer: 'Unlock layer',
    },
    contextMenu: {
      rename: 'Rename',
      duplicate: 'Duplicate',
      delete: 'Delete',
      group: 'Group',
      ungroup: 'Ungroup',
      bringToFront: 'Bring to Front',
      sendToBack: 'Send to Back',
    },
    messages: {
      cannotMoveToDescendant: 'Cannot move a layer into its own descendant.',
      confirmCrossFrameMove: 'Moving layers across frames may affect layout. Continue?',
      moveConfirm: 'Move',
      moveCancel: 'Cancel',
    },
    palette: {
      searchPlaceholder: 'Search actions...',
      noResults: 'No matching actions found',
    },
    shortcuts: {
      multiSelect: 'Hold Cmd/Ctrl to multi-select',
      rangeSelect: 'Hold Shift to range select',
      toggleExpand: 'Double-click to expand/collapse',
    },
    dropActions: {
      parentLocked: 'Parent is locked, cannot drop here.',
      parentHidden: 'Parent is hidden, cannot drop here.',
    },
  },
  interactive: {
    lockedNode: 'This element is locked',
    lockedNodeHint: 'Unlock to edit',
  },
  strokePanel: {
    stroke: 'Stroke',
    border: 'Border',
    customizeBorder: 'Customize border styles',
    quickStyles: 'Quick Styles',
    color: 'Color',
    width: 'Width',
    simple: 'Simple',
    advanced: 'Advanced',
    clear: 'Clear stroke',
    presetsTitle: 'Presets',
    strokeLayers: 'Stroke Layers',
    addLayer: 'Add Layer',
    removeLayer: 'Remove Layer',
    layer: 'Layer',
    opacity: 'Opacity',
    blur: 'Blur',
    offsetX: 'Offset X',
    offsetY: 'Offset Y',
    textPresets: {
      none: 'None',
      crisp: 'Crisp',
      bold: 'Bold',
      sticker: 'Sticker',
      shadow: 'Shadow',
      glow: 'Glow',
      neon: 'Neon',
      double: 'Double',
      retro: 'Retro',
    },
    dash: 'Dash',
    dashPattern: 'Dash Pattern',
    lineCap: 'Line Cap',
    lineJoin: 'Line Join',
    miterLimit: 'Miter Limit',
    presets: {
      none: 'None',
      thin: 'Thin',
      medium: 'Medium',
      thick: 'Thick',
      dashed: 'Dashed',
      dotted: 'Dotted',
    },
    lineCaps: {
      butt: 'Butt',
      round: 'Round',
      square: 'Square',
    },
    lineJoins: {
      miter: 'Miter',
      round: 'Round',
      bevel: 'Bevel',
    },
    placeholders: {
      dashPattern: 'e.g. 5,5 or 10,5,3,5',
      selectLineCap: 'Select line cap',
      selectLineJoin: 'Select line join',
      mixed: 'Mixed',
    },
  },
  editPanel: {
    searchPlaceholder: 'Search elements by name, text, or type...',
    clearSearch: 'Clear search',
    locateElement: 'Locate element',
    noEditableElements: 'No editable elements',
    elementsWillAppear: 'Elements will appear here when available',
    noMatchingElements: 'No matching elements',
    adjustSearchTerms: 'Try adjusting your search terms or clear the search to see all elements',
    noFramesFound: 'No frames found',
    importDesignFile: 'Import a design file to start editing your content',
    enterText: 'Enter text...',
    uploadImage: 'Upload image',
    replaceImage: 'Replace',
    element: 'Element',
    frame: 'Frame',
  },
  workspaceAsset: {
    all: 'All',
    favorites: 'Favorites',
    recent: 'Recently uploaded',
    ai: 'AI generated',
    searchAssets: 'Search assets...',
    upload: 'Upload',
    select: 'Select',
    cancel: 'Cancel',
    selectedCount: '{{count}} selected',
    selectAll: 'Select All',
    clearSelection: 'Clear Selection',
    delete: 'Delete',
    confirmBatchDelete:
      'Are you sure you want to delete {count} assets? This action cannot be undone.',
    loadingAssets: 'Loading assets...',
    emptyTitle: 'Your asset library is empty',
    emptyDescription: 'Upload your first asset to start building your creative media collection',
    uploadAsset: 'Upload Asset',
    loadingMore: 'Loading more...',
    noSearchResults: 'No assets match your search',
    noSearchResultsDescription:
      "We couldn't find any assets for “{{query}}”. Try a different keyword or upload a new asset.",
    somethingWentWrong: 'Something went wrong',
    tryAgain: 'Try Again',
    uploadFailed: 'Upload failed',
  },
  pageListPanel: {
    pages: 'Pages',
    addPage: 'Add page',
    duplicatePage: 'Duplicate page',
    deletePage: 'Delete page',
    pageActions: 'Page actions',
    page: 'Page',
  },
  filterPanel: {
    filter: 'Filter',
    presets: 'Presets',
    custom: 'Custom',
    filterType: 'Filter Type',
    intensity: 'Intensity',
    filterValue: 'Filter Value',
    none: 'None',
    grayscale: 'Grayscale',
    sepia: 'Sepia',
    blur: 'Blur',
    brightness: 'Brightness',
    contrast: 'Contrast',
    invert: 'Invert',
    saturate: 'Saturate',
    placeholder: 'e.g. blur(5px) brightness(120%)',
  },
  about: {
    title: 'About Us',
    subtitle:
      'Everyone carries a seed of creativity within, and it deserves to be nurtured, sprouted, and flourished',
    vision: {
      title: 'Vision',
      content: [
        'We believe AI will redefine the boundaries of creation. In the future, everyone will be able to transform ideas into beautiful visual works through simple conversation.',
        'Seede AI is committed to becoming a bridge between imagination and reality, making design accessible to everyone rather than a skill reserved for the few.',
      ],
    },
    mission: {
      title: 'Mission',
      content: [
        'Lower the barrier to design, enabling everyone to create professional-grade visual content with ease.',
        'Continuously optimize AI models and user experience to provide smarter, more efficient, and more creative design solutions.',
        'Build an open and friendly creative community where inspiration collides and grows.',
      ],
    },
    values: {
      title: 'Values',
      content: [
        "Creativity First: It's all about the ideas.",
        'Simplicity Matters: The best ideas are simple.',
        'Personalization is Power: Your design, your way.',
        "Design for Everyone: It's not just about the design, it's about the connection.",
        "From Content to Connection: From content to connection, we're here to help you create the perfect design.",
      ],
    },
    story: {
      title: 'Story',
      content: [
        "I'm Evan, founder of Veeso AI. Many people ask me why I chose to create AI design tools. My story might be different from what many expect.",
        'I\'ve been obsessed with "creating" since childhood. In high school, I built and maintained a local forum called "Deyang Forum" all by myself. I didn\'t know much about technology then, just some basic PHP, but when I saw users actually using my product, and some even mistook it for "officially operated," that sense of achievement made me feel the human warmth behind technology for the first time.',
        'I chose cybersecurity in college and won some awards, but gradually realized what I truly loved wasn\'t "hacking and defense," but "building things from scratch." So in my sophomore year, I chose to leave campus and enter the real industry to learn and grow.',
        "During my time at Meituan, deeply inspired by Serverless concepts and Vercel, I independently designed and implemented a mini version of the Vercel system from scratch. That was my first time truly transitioning from pure technical architecture to building solutions with product thinking. This experience made me deeply realize: Technology itself isn't the endpoint; creating smooth, efficient experiences for users is the core.",
        "After leaving Meituan, I joined the startup Dora AI, officially entering the design tools field. I invested significant time deeply studying Figma and Canva, not just as a user, but from a product builder's perspective, understanding the architectural logic, interaction concepts, and implementation paths behind modern design tools. That was my first systematic understanding that design tools are essentially about helping people express ideas more freely, and technology should serve this goal. After ChatGPT's release, I became even more convinced: AI will inevitably reshape the future of design.",
        'Thus, Veeso AI was born. The name "Seede" comes from combining "Seed" and "Design." We believe everyone carries a seed of creativity within, and it deserves to be nurtured, sprouted, and flourished—not hindered by complex technology and tools.',
        'Today, Veeso AI is committed to building such a product: You need no design background, just input your ideas, and AI will help you generate beautiful and practical images, posters, invitations, infographics... Especially in text and information design, we continuously optimize layout control and visual effects, hoping to enable every user to express themselves with ease and confidence.',
        "To me, technology has never been just code and models; it's a bridge to humanity. We're still in the early stages with much to improve, but I firmly believe: True technology should make people more free, more confident, and more creative.",
        'Looking forward to building a more intelligent and open design world together with you. —— Evan, Founder of Veeso AI',
      ],
    },
  },
  order: {
    title: 'Orders',
    loadingOrders: 'Loading orders...',
    noOrdersFound: 'No orders found',
    failedToLoadOrders: 'Failed to load orders',
    noMoreOrders: 'No more orders',
    id: 'ID',
    date: 'Date',
    details: 'Details',
    amount: 'Amount',
    status: 'Status',
    actions: 'Actions',
    paymentMethod: {
      alipay: 'Alipay',
      wxpay: 'WeChat Pay',
      gift: 'Gift',
      creditCard: 'Credit Card',
      payment: 'Payment',
    },
    statusLabels: {
      succeeded: 'Succeeded',
      paid: 'Paid',
      complete: 'Complete',
      pending: 'Pending',
      processing: 'Processing',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    invoice: {
      title: 'Invoice',
      apply: 'Apply for Invoice',
      cancel: 'Cancel Application',
      download: 'Download Invoice',
      applyTitle: 'Apply for Invoice',
      applyDescription:
        'Please fill in the invoice information. We will issue an electronic invoice within 1-3 business days.',
      orderNumber: 'Order Number',
      invoiceTitle: 'Invoice Title',
      titlePlaceholder: 'Enter company name or personal name',
      taxNumber: 'Tax Number',
      taxNumberPlaceholder: 'Business tax number (required for business invoices)',
      email: 'Email Address',
      emailPlaceholder: 'For receiving electronic invoice',
      remark: 'Remarks',
      remarkPlaceholder: 'Please note any special requirements',
      submit: 'Submit Application',
      status: {
        pending: 'Processing',
        issued: 'Issued',
        cancelled: 'Cancelled',
      },
      error: {
        titleRequired: 'Invoice title is required',
      },
      applySuccess: 'Invoice application submitted successfully',
      applyFailed: 'Failed to submit invoice application',
      cancelSuccess: 'Invoice application cancelled successfully',
      cancelFailed: 'Failed to cancel invoice application',
    },
    payment: 'Payment',
  },
  creditsHistory: {
    title: 'Credits History',
    loading: 'Loading...',
    failedToLoad: 'Failed to load credits history',
    noRecords: 'No credits history yet',
    noMoreRecords: 'No more records',
    expiresAt: 'Expires',
    remaining: 'Remaining',
    free: 'Free',
    expired: 'Expired',
    filters: {
      earned: 'Earned',
      spent: 'Spent',
    },
    types: {
      llm: 'AI Chat',
      image: 'Image Generation',
      spent: 'Credits Spent',
      system: 'System',
      dailyFree: 'Daily Check-in',
      purchase: 'Purchase',
      adminGrant: 'Admin Grant',
      migrationLegacy: 'Legacy Migration',
      gift: 'Gift',
      referral: 'Referral Reward',
      promotion: 'Promotion',
      earned: 'Credits Earned',
    },
  },
  usage: {
    title: 'Model Usage',
    subtitle: 'View your AI model usage history',
    failedToLoad: 'Failed to load usage records',
    noRecords: 'No usage records found',
    type: 'Type',
    model: 'Model',
    consumption: 'Consumption',
    types: {
      imageGeneration: 'Image Generation',
      llmChat: 'LLM Chat',
      unknown: 'Unknown',
    },
  },

  /**
   * V3 Landing Page (`/index3`)
   * --------------------------------------------------------------------
   * 仅覆盖 v3 landing 首屏可见的 marketing 文案。命名分组对应组件：
   *   seo       — index3.vue useHead (title / description / keywords / og)
   *   nav       — AppNav.vue 中 variant=index3 时的菜单与 CTA
   *   hero      — HeroV3.vue 文字组（h1 + 副标题 + CTA）
   *   panels    — OneInputManyOutputsV3.vue 三块 panel 的 heading/description/cta
   *   layered   — LayeredFileV3.vue 标题区 + 4 tab 标签 + Export 按钮
   *   cta       — CtaV3.vue 标题（三段式 + emphasis）+ 副标题 + 输入框 + 按钮
   *
   * Hero、LayeredFile 和 MockCanvas 的演示文案同样属于可见内容，统一由 i18n
   * 管理，并通过 Seede 生成日语、韩语和西语版本。
   */
  landing_v3: {
    seo: {
      title: 'Veeso AI | Turn your content into carousels, ads, and decks',
      description:
        'Paste a doc, a brief, or a spreadsheet. Get editable designs you can post, pitch, sell, or ship.',
      keywords:
        'AI design, content-first design, social media visuals, SMB marketing, auto layout, visual content tool',
    },
    nav: {
      use_case: 'Use case',
      pricing: 'Pricing',
      log_in: 'Log in',
      start_free: 'Start Free',
      workspace: 'Workspace',
      toggle_menu_aria: 'Toggle menu',
      close_menu_aria: 'Close menu',
    },
    hero: {
      title_part1: 'Turn your content into',
      title_emphasis: 'carousels, ads, and decks.',
      /** Hero 第二行：prefix/word/suffix 供模板 nowrap 包裹；英文整句落在 word */
      title_emphasis_prefix: '',
      title_emphasis_word: 'carousels, ads, and decks.',
      title_emphasis_suffix: '',
      subtitle_line1: 'Paste a doc, a brief, or a spreadsheet.',
      subtitle_line2: 'Get editable designs you can post, pitch, sell, or ship.',
      subtitle_line2_lead: 'Get editable designs you can post, pitch, sell, or ship.',
      subtitle_line2_tail: '',
      cta: 'Try with your content',
      showcases: {
        showcase1: {
          title: 'Album cover',
          prompt: 'Design a retro cassette album cover for our new EP.',
          attachment1: 'Album brief.docx',
          attachment2: 'Artist photo.jpg',
        },
        showcase2: {
          title: 'Event poster',
          prompt: "Design a bold poster for Julian Richards' live set.",
          attachment1: 'Run of show.pdf',
          attachment2: 'Key visual.jpg',
        },
        showcase3: {
          title: 'Workshop poster',
          prompt: 'Create a poster for the Shift Navigate workshop.',
          attachment1: 'Event brief.docx',
          attachment2: 'Venue photo.jpg',
        },
        showcase4: {
          title: 'Media kit slide',
          prompt: 'Design an audience insights slide for our media kit.',
          attachment1: 'Analytics.pdf',
          attachment2: 'Brand deck.docx',
        },
        showcase5: {
          title: 'Exhibition poster',
          prompt: 'Design a poster for the Julio Resende exhibition.',
          attachment1: 'Exhibition text.docx',
          attachment2: 'Artwork.jpg',
        },
        showcase6: {
          title: 'Workshop ad',
          prompt: 'Design a social ad for Focus Atelier workshops.',
          attachment1: 'Program.docx',
          attachment2: 'Portrait.jpg',
        },
        showcase7: {
          title: 'Conference poster',
          prompt: 'Design a poster for the Postwar Satellite Cities conference.',
          attachment1: 'Conference brief.pdf',
          attachment2: 'Archive photo.jpg',
        },
        showcase8: {
          title: 'Weekly report',
          prompt: 'Turn our weekly growth notes into a report cover.',
          attachment1: 'Growth notes.docx',
          attachment2: 'Product photo.jpg',
        },
        showcase9: {
          title: 'Editorial poster',
          prompt: 'Design an editorial poster from this manifesto draft.',
          attachment1: 'Manifesto.docx',
          attachment2: 'Reference.jpg',
        },
      },
    },
    /**
     * "One input, many outputs" 模块（OneInputManyOutputsV3）的标题副标题。
     * 三个 panel 自己的标题描述见下方 panels.{article,event_brief,report}.*。
     */
    one_input: {
      heading: 'One input, many outputs.',
      subheading: 'Start with one piece of content. Generate the whole set.',
    },
    panels: {
      customer_story_label: 'Customer Story {{current}} / {{total}}',
      cta_label: 'Try it',
      stage_composer_placeholder: 'Paste your content here and describe what you want to design',
      article: {
        heading: 'From Article to Carousel set',
        description: 'Give Veeso an article. Get a full set of social-ready pages.',
        product_name: 'Instagram & LinkedIn carousels',
        demo_user_message:
          'Turn this studio feature article into a 9-slide carousel for Instagram and LinkedIn — keep the 2,340 projects number and the dark editorial feel.',
        demo_ai_content:
          "Got it. I'll turn the article into a 9-slide carousel — one idea per page, swipe-friendly typography, consistent dark editorial.",
        demo_ai_plan_label: 'Carousel plan',
        demo_ai_plan_content:
          "Slide 1 — cover. Slides 2-3 — sustainable design and the firm's philosophy. Slides 4-5 — comfort-driven craft and the signature portfolio. Slide 6 — the 2,340 projects headline. Slides 7-8 — client reviews and sales growth. Slide 9 — closing on the dream home.",
        demo_attachment_name: 'ArchFirm-Studio-Feature.md',
        demo_attachment_size: '2.3 KB · Markdown',
      },
      event_brief: {
        heading: 'From Event brief to Promo kit',
        description: 'Give Veeso a brief. Get posters, posts, invites, and banners.',
        product_name: 'Promo kit',
        demo_user_message:
          'Build a full promo kit from this brief — main poster, social cover, platform ad and a 9:16 invitation.',
        demo_ai_content:
          "On it. From your brief I'll generate 4 deliverables that share the same lineup, colours and typography.",
        demo_ai_plan_label: 'Promo kit',
        demo_ai_plan_content:
          '1080×1527 main poster, 1280×720 social cover, 1280×764 platform ad with CTA, and a 1080×1920 invitation — all consistent with the Sunset Beach brand.',
        demo_attachment_name: 'Summer-Vibes-Festival_Brief.docx',
        demo_attachment_size: '12 KB · Word',
      },
      report: {
        heading: 'From Report to Pitch deck',
        description: 'Give Veeso a report. Get a presentation-ready deck.',
        product_name: 'Pitch deck',
        demo_user_message:
          'Distill this weekly growth report into a presentation-ready deck — 9 slides, keep the numbers and the Cedar Night gifting story.',
        demo_ai_content:
          "On it. I'll distill the report into a 9-slide deck — one idea per slide, large numbers, executive-ready.",
        demo_ai_plan_label: 'Deck outline',
        /* 注意：原 panel 数据里 plan_content 之后接续，本字段保留与原英文一致 */
        demo_ai_plan_content:
          'Slide 1 — Cover. Slides 2-3 — what moved this week + Cedar Night highlight. Slide 4 — what we did. Slide 5 — numbers. Slides 6-7 — Cedar Night story + why it happened. Slides 8-9 — next week plan + next update.',
        demo_attachment_name: 'Weekly-Growth-Pulse_Nov-11-17.pdf',
        demo_attachment_size: '1.4 MB · PDF',
      },
    },
    layered: {
      title: 'Veeso makes designs.',
      subtitle: 'An image is a dead end. A design is a file you can keep working with.',
      subtitle_line1: 'An image is a dead end.',
      subtitle_line2: 'A design is a file you can keep working with.',
      tab: {
        change_text: 'Change text',
        swap_visuals: 'Swap visuals',
        layered_file: 'Layered file',
        export: 'Export PPTX, PDF',
        resize_formats: 'Resize formats',
      },
      mobile_hint: {
        edit: 'Edit headlines and fonts directly on the poster.',
        swap: 'Generate alternate visuals from the same brief.',
        layered: 'Move and layer elements like a real design file.',
        export: 'Export to PPTX, PDF, and more formats.',
      },
      export_button: 'Export',
      export_menu: {
        title: 'Export',
        format: 'Format',
        resolution: 'Resolution',
        file_name: 'File Name',
        file_name_value: 'veeso-Veeso AI Launch',
        action: 'Export',
      },
      composer_placeholder: 'What do you want to design now?',
      chat: {
        swap_user_prompt: 'Extended version 2 design',
        swap_ai_reply: 'Got it. Here are 2 extended directions kept on the same lineup.',
        swap_core_concept_body:
          'Same lineup, different operating system. We carry JOEL MULL × UMFANG into a CRT-terminal aesthetic — mono-spaced type, neon-green system tags like [ SYS.DATE ] and > RUN_PROGRAM, plus a stuttering "SAKE / SAKE / SAKE" RGB-glitch motif. The night-rave attitude stays; the flyer now reads like pirate-radio firmware.',
      },
    },
    layered_brief: {
      brief_intro: "Now let's design directly. here are the brief.",
      core_concept: 'Core Concept',
      brief_body:
        'Lead with a single striking idea: a countdown-driven theme anchored by one hero visual and a crisp headline like "Build in 24 Hours." Keep the palette minimal—one vivid accent color with black/white—and a geometric motif that hints at speed and collaboration.',
      brief_prompt_demo:
        'Design a post for my new product and will send it to X social media platform. with oil painting style',
      composer_placeholder: 'what do you want to design?',
      send_aria: 'Send',
    },
    /**
     * MockCanvas（模拟工作区画板）内部的 chrome 文案。
     * 注意：MockCanvas 是 OneInputManyOutputsV3 / LayeredFileV3 共用的演示组件，
     * 这里翻译的是"产品 UI 的展示性副本"，而不是 demo 内的作品内容。
     */
    canvas: {
      untitled_design: 'Untitled Design',
      composer_placeholder: 'Want to try? Type something...',
      share_to: 'Share to',
      copy_link: 'Copy link',
      collapse_sidebar: 'Collapse sidebar',
      expand_sidebar: 'Expand sidebar',
      preview_unavailable: 'Preview unavailable',
    },
    cta: {
      // 标题在视觉上是「Now make [手势 icon] *something* / with your content.」两行，
      // italic 强调词位置受图标锚定影响，三段拆分让 ja/zh 翻译时能在保持图标位置的前提下
      // 重排自然语序。
      title_before_emphasis: 'Now make',
      title_emphasis: 'something',
      title_after_emphasis: 'with your content.',
      subtitle: 'Free to start. No prompts. No design skills required.',
      placeholder: 'What do want to design now?',
      generate: 'Generate',
      generating: 'Generating…',
      add_attachment_aria: 'Add attachment',
      presets: {
        categories: {
          presentation: 'Presentation',
          carousel: 'Carousel',
          long_form: 'Long-Form Content',
          event_poster: 'Event Poster',
        },
        presentation: {
          ai_design_intro: {
            label: 'AI Design Tool Intro',
            prompt:
              'Create a 10-slide Presentation for an AI design tool, aimed at small business marketing teams. Use it as a product introduction deck. Highlight use cases, core capabilities, and productivity gains. Make it professional, modern, and tech-forward.',
          },
          startup_pitch: {
            label: 'Startup Pitch Deck',
            prompt:
              'Create a startup pitch deck for an AI freelance marketplace tool for independent designers. The audience is investors. Highlight market opportunity, product solution, business model, and growth strategy. Keep it around 12 slides.',
          },
          bootcamp_intro: {
            label: 'Course Introduction',
            prompt:
              'Create a course introduction Presentation for an AI beginner bootcamp, aimed at young professionals. Highlight learning outcomes, course structure, target learners, and reasons to enroll. Make it clear, trustworthy, and approachable.',
          },
          smartwatch_launch: {
            label: 'Product Launch Deck',
            prompt:
              'Create a product launch deck for a minimalist smartwatch. The audience is media and potential users. Highlight design philosophy, core features, usage scenarios, and pricing. Make it premium, restrained, and modern.',
          },
        },
        carousel: {
          xhs_ai_productivity: {
            label: 'AI Productivity Tips',
            prompt:
              'Create an 8-page Xiaohongshu carousel about "How ordinary people can start using AI to improve work efficiency." The audience is young professionals. Make the first 2 pages engaging, then explain practical methods and tools step by step. Use a clear, youthful, and practical style.',
          },
          linkedin_ai_marketing: {
            label: 'AI Content Marketing',
            prompt:
              'Create a 7-page LinkedIn Carousel about "How AI is changing the content marketing workflow." The audience is B2B marketing teams. Highlight trends, examples, and actionable recommendations. Use a professional, restrained, business-oriented style.',
          },
          instagram_scheduling_app: {
            label: 'AI Scheduling App',
            prompt:
              'Create a 6-page Instagram Carousel introducing an AI scheduling app for knowledge workers. Highlight pain points, core features, usage scenarios, and productivity gains. Use a clean, modern, tech-forward style.',
          },
          sleep_education: {
            label: 'Sleep & Health',
            prompt:
              'Create an educational carousel about "Why sleep matters more than you think," aimed at general readers. Make it 8 pages. Keep the content easy to understand and the visual style clear and credible, without looking like a medical ad.',
          },
        },
        long_form: {
          desk_lamp_detail: {
            label: 'Smart Desk Lamp',
            prompt:
              'Create a product detail page for a smart desk lamp, aimed at home office users and parents of students. The goal is to help users understand the product value and increase purchase intent. Include pain points, core selling points, usage scenarios, specifications, and reasons to buy. Highlight eye protection, adjustable lighting, minimalist design, and home office use. Use a clean, premium, conversion-oriented style.',
          },
          ai_course_landing: {
            label: 'AI Course Landing',
            prompt:
              'Create a course landing page for an AI beginner course, aimed at young professionals. The goal is to drive sign-ups. Include target learners, learning outcomes, course structure, instructor credibility, and registration information. Highlight that the course is easy to follow, practical, and useful for improving work efficiency. Use a professional, clear, trustworthy style.',
          },
          tokyo_travel_guide: {
            label: 'Tokyo Travel Guide',
            prompt:
              'Create a long-form travel guide for a 3-day Tokyo trip, aimed at first-time visitors to Japan. The goal is to make users save and share the guide. Include itinerary, recommended attractions, transportation, budget tips, and important notes. Use a clean, readable, travel-oriented style.',
          },
          back_pain_education: {
            label: 'Back Pain Education',
            prompt:
              'Create a medical education long-form page about "Why sitting for too long causes lower back pain," aimed at office workers. The goal is to help users understand the cause and know how to improve it. Include symptoms, causes, risks, daily improvement methods, and precautions. Use a professional and credible style, without exaggerated marketing language.',
          },
        },
        event_poster: {
          ai_tool_launch: {
            label: 'AI Tool Launch',
            prompt:
              'Create an Event Poster for an AI tool launch event. The theme is "Generate editable designs from one sentence." The audience is content creators and marketing teams. It will be used for social media promotion. Highlight event time, product value, and registration method. Guide users to reserve a spot. Use a premium, tech-forward style, but avoid cheap sci-fi effects.',
          },
          designer_salon: {
            label: 'Designer Salon',
            prompt:
              'Create an Event Poster for an offline designer salon. The theme is "Design Workflows in the Age of AI." The audience is product designers and brand designers. It will be used on Xiaohongshu and WeChat Moments. Highlight the topic, speakers, time, location, and QR code. Use a restrained, professional, design-led style.',
          },
          coffee_tasting: {
            label: 'Coffee Tasting Event',
            prompt:
              'Create an Event Poster for a coffee shop weekend event. The event is a new drink tasting, aimed at nearby young consumers. It will be used for in-store display and social media. Highlight time, location, event benefits, and store visit CTA. Use a warm, modern, lifestyle-oriented style.',
          },
          gym_opening: {
            label: 'Gym Opening',
            prompt:
              'Create an Event Poster for a gym opening campaign, aimed at nearby office workers and fitness beginners. It will be used for social media and offline promotion. Highlight limited-time offers, trial classes, and sign-up method. Use an energetic, clear, conversion-oriented style.',
          },
        },
      },
    },
    /** Customer story deck slide copy (IG/SV/WGP) — English-only product output. */
    mock: {
      slides: landingV3SlidesEn,
    },
    /**
     * Footer 4 列链接（AppFooterV3.vue 经 useFooterLinks 渲染）。
     * Design Tools 列的链接 label 是产品类型 / SEO 关键词（"Instagram Post" 等），
     * 保留英文不翻译；只翻译 group title 与其它 3 列的 label。
     */
    footer: {
      group_design_tools: 'Design Tools',
      group_company: 'Company',
      group_resources: 'Resources',
      group_connect: 'Connect',
      company: {
        about: 'About',
        contact: 'Contact',
        terms: 'Terms',
        privacy: 'Privacy',
        cookie_policy: 'Cookie Policy',
      },
      resources: {
        help: 'Help',
        solutions: 'Solutions',
        tools: 'Tools',
        learn: 'Learn',
        blog: 'Blog',
        spotlight: 'Spotlight',
      },
    },
  },

  /**
   * Marketing landing pages（about / contact 等公开宣传页）
   * --------------------------------------------------------------------
   * 不与已有 `contact` namespace 复用 —— 那个 namespace 服务于 workspace
   * 内部应用层的「联系我们」链接，文案结构与 marketing contact 页面差异较大。
   * 这里独立 `landingPages.contact` 命名空间，避免改 workspace 时误伤。
   *
   * 翻译策略：
   *   - SEO meta (`seo.*`) 对每种 locale 都翻；ja 暂不影响 SSR (SSR 永远 'en')，
   *     hydration 后客户端 useHead 会更新到当前 locale。
   *   - About 的 hero `title` 用 `\n` 标记换行；模板会按行拆 + 按 word stagger
   *     播放 blur 入场动画。如需禁用动画或改成整句一次性出现，把 word 列表改成
   *     单元素数组即可。
   *   - About 的 mission `body` 含 `<span>` 高亮标记；模板用 `v-html` 渲染。
   *     译文要保留 span 结构（自由调整高亮短语位置以适配 native 语序）。
   */
  landingPages: {
    about: {
      seo: {
        title: 'About Us | Reinventing Design Process with AI',
        description:
          'Discover the mission behind Veeso AI – Content in, design out. We revolutionize the production process, increase efficiency, not just the output.',
        keywords:
          'Veeso AI about, content in design out, creative automation, AI design, design efficiency, content-first design',
        ogTitle: 'About Us | Transforming How Design is Made',
      },
      hero: {
        // 用 \n 分行；模板按行拆词 + word_blur stagger 动画。
        // 译文可自由调整分行（每行词数自适应），但建议总词数与英文相近以保持节奏。
        title: 'Building tools\nto amplify our\nimagination.',
      },
      mission: {
        srHeading: 'Mission',
        // body 含 <span class="text-black/90"> 标记高亮短语；模板用 v-html 渲染。
        // 译文要保留 span 闭合，自由调整高亮短语位置。
        body: 'Veeso AI is on a mission to build <span class="text-black/90"> an intelligent creation system</span> that merges <span class="text-black/90"> logic, aesthetics, and computation, </span> empower anyone, not just designers, to generate <span class="text-black/90"> structured and beautiful </span> content.',
      },
      values: {
        heading: 'Values',
        items: {
          imagination: {
            title: 'Imagination',
            description: 'Inspiring and building tools to amplify our imagination.',
          },
          simplicity: {
            title: 'Simplicity',
            description: 'Unify and connect different concepts and make them work together.',
          },
          personalization: {
            title: 'Personalization',
            description: 'Your design, your way.',
          },
          designForEveryone: {
            title: 'Design for Everyone',
            description: 'Building tools for everyone.',
          },
        },
      },
    },
    contact: {
      seo: {
        title: 'Contact Us | Veeso AI Support & Sales',
        description:
          'Contact the Veeso AI team for product support, sales questions, partnerships, or press. We help creators and SMBs design from content faster.',
        keywords:
          'Veeso AI support, contact Veeso, AI design help, sales inquiry, partnership inquiry, creator support, SMB design tool',
      },
      hero: {
        title: 'Contact Us',
        subtitle: "We'd love to hear from you. Get in touch with our team.",
      },
      options: {
        emailSupport: {
          title: 'Email Support',
          description: 'Have a question or need help? Send us an email and we’ll get back to you.',
          action: 'Send Email',
        },
      },
      faq: {
        heading: 'FAQ',
        // 当 FAQ 项目缺 discordText 但有 discordLink 时模板的 fallback 文案
        discordFallback: 'Join our Discord community',
        items: {
          responseTime: {
            question: 'What is your typical response time?',
            answer:
              'We aim to respond to all inquiries within 24-48 hours during business days (Monday-Friday). Priority support is available for Pro and Ultra plan subscribers.',
          },
          // FAQ 项中含内联链接：answerBefore + link + answerAfter 三段拼接，
          // 译文需保留分段（不要把链接前后合并成一句）。
          supportTypes: {
            question: 'What types of support do you offer?',
            answerBefore: 'We offer support through email at support@veeso.ai and our',
            discordText: 'Discord community',
            answerAfter:
              ' Email support allows us to provide detailed technical assistance and share relevant links, while the Discord community enables real-time discussions and peer support.',
          },
          reportBug: {
            question: 'How can I report a bug?',
            answerBefore:
              'If you encounter a bug, please email support@veeso.ai with details about the issue, including steps to reproduce it and any relevant screenshots. You can also report bugs in our',
            discordText: 'Discord community',
            answerAfter: ' for faster response and community support.',
          },
          hiring: {
            question: 'Are you hiring? How can I join?',
            answer: 'We welcome you to contact us at hi@veeso.ai',
          },
        },
      },
    },

    /**
     * Marketing 站的登录 / 注册 / 重置密码 / AuthModal / GoogleLoginButton 文案。
     * --------------------------------------------------------------------
     * 不复用 workspace 的 `signIn` / `forgotPassword` / `auth` namespace —— 那些
     * 服务于内部应用层（已签到用户的 SignIn 流程、邮箱二次验证等），文案语气
     * 和结构跟 marketing 站差异较大，强行共享会让两边在改文案时互相牵动。
     * 这里独立 `landingPages.auth.*` 命名空间。
     *
     * common 子节为 5 个文件共用的字段标签、校验文案、链接文案、条款行；
     * 各页面自己的 SEO meta、submit 文案、独有提示分别放在对应子节。
     */
    auth: {
      common: {
        // 字段 placeholder
        email: 'Email',
        password: 'Password',
        verificationCode: 'Verification Code',
        // 按钮态 / 倒计时
        sendCode: 'Send Code',
        countdownSeconds: '{{count}}s',
        or: 'or',
        // aria-label
        togglePasswordVisibility: 'Toggle password visibility',
        // 校验
        emailRequired: 'Please enter your email address',
        emailInvalid: 'Please enter a valid email address',
        emailPasswordRequired: 'Please enter both email and password',
        passwordRequired: 'Please enter your password',
        passwordMinLength: 'Password must be at least 6 characters',
        verificationCodeRequired: 'Please enter the verification code',
        // 错误
        sendCodeFailed: 'Failed to send verification code',
        invalidCredentials: 'Invalid email or password',
        genericError: 'Something went wrong. Please try again.',
        // 跳转链接 / 辅助文案
        forgotPassword: 'Forgot password?',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        createAccount: 'Create your account',
        logIn: 'Log in',
        // 条款行
        termsPrefix: 'By continuing, you agree to the',
        termsOfService: 'Terms of Service',
        and: 'and',
        privacyPolicy: 'Privacy Policy',
      },
      login: {
        // SEO（noindex 页面，不依赖搜索引擎索引，但 OG 分享展示仍需 i18n）
        seoDocumentTitle: 'Sign In - Veeso AI',
        seoTitle: 'Log In - Veeso AI',
        seoDescription: 'Log in to your Veeso AI account to start creating stunning designs.',
        title: 'Log in',
        submitting: 'Signing in...',
        submit: 'Log in',
      },
      signup: {
        seoDocumentTitle: 'Sign Up - Veeso AI',
        seoTitle: 'Sign Up - Veeso AI',
        seoDescription: 'Create your Veeso AI account to start creating stunning designs.',
        title: 'Sign up',
        submitting: 'Creating account...',
        submit: 'Create your account',
      },
      forgot: {
        seoDocumentTitle: 'Reset Password - Veeso AI',
        seoTitle: 'Reset Password - Veeso AI',
        seoDescription: 'Reset your Veeso AI account password.',
        // 三态标题
        titleInitial: 'Reset Password',
        titleCodeSent: 'New Password',
        titleSuccess: 'Password Reset',
        // 成功状态
        successMessage:
          'Your password has been reset successfully. You can now log in with your new password.',
        backToLogin: 'Back to Login',
        // 表单字段（forgot 独有）
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        // 表单按钮态
        resend: 'Resend',
        submittingReset: 'Resetting...',
        submittingSend: 'Sending...',
        submitReset: 'Reset Password',
        // 校验
        newPasswordRequired: 'Please enter a new password',
        passwordsMismatch: 'Passwords do not match',
        resetFailed: 'Failed to reset password',
        // 底部跳转
        rememberPassword: 'Remember your password?',
      },
      modal: {
        titleLogin: 'Log in',
        titleSignup: 'Sign up',
        descriptionLogin: 'Welcome back to Veeso AI',
        descriptionSignup: 'Create your Veeso AI account',
        continueWithEmail: 'Continue with Email',
        back: 'Back',
        sending: 'Sending...',
        registrationFailed: 'Registration failed',
      },
      google: {
        continue: 'Continue with Google',
        connecting: 'Connecting...',
      },
      callback: landingUnsupportedEn.auth.callback,
      loggedIn: {
        welcome: 'Welcome back!',
        currentlyLoggedIn: 'You are currently logged in as',
        goToWorkspace: 'Go to Workspace',
        signOut: 'Sign out',
      },
    },

    /** Secondary pages — see docs/landing-secondary-i18n-source-en.md */
    ...landingSecondaryEn,
    articleDetail: landingRemainingEn.articleDetail,
    paymentSuccess: landingUnsupportedEn.paymentSuccess,
    error: landingUnsupportedEn.error,
    blog: {
      ...landingUnsupportedEn.blog,
      index: {
        ...landingUnsupportedEn.blog.index,
        ...landingRemainingEn.blog.index,
        hero: {
          ...landingUnsupportedEn.blog.index.hero,
          ...landingRemainingEn.blog.index.hero,
        },
        seo: {
          ...landingUnsupportedEn.blog.index.seo,
          ...landingRemainingEn.blog.index.seo,
        },
        faq: {
          ...landingUnsupportedEn.blog.index.faq,
          ...landingRemainingEn.blog.index.faq,
        },
      },
      detail: landingRemainingEn.blog.detail,
    },
    spotlight: {
      ...landingUnsupportedEn.spotlight,
      index: {
        ...landingUnsupportedEn.spotlight.index,
        ...landingRemainingEn.spotlight.index,
        hero: {
          ...landingUnsupportedEn.spotlight.index.hero,
          ...landingRemainingEn.spotlight.index.hero,
        },
        seo: {
          ...landingUnsupportedEn.spotlight.index.seo,
          ...landingRemainingEn.spotlight.index.seo,
        },
        faq: {
          ...landingUnsupportedEn.spotlight.index.faq,
          ...landingRemainingEn.spotlight.index.faq,
        },
      },
      detail: landingRemainingEn.spotlight.detail,
    },
    aiToolsIndex: {
      ...landingSecondaryEn.aiToolsIndex,
      ...landingUnsupportedEn.aiToolsIndex,
      card: {
        ...landingSecondaryEn.aiToolsIndex?.card,
        ...landingUnsupportedEn.aiToolsIndex?.card,
      },
      seo_content: {
        ...landingSecondaryEn.aiToolsIndex?.seo_content,
        ...landingUnsupportedEn.aiToolsIndex?.seo_content,
        ...landingRemainingEn.aiToolsIndex.seo_content,
      },
    },
    aiToolsDetail: landingRemainingEn.aiToolsDetail,
    solutions: {
      ...landingSecondaryEn.solutions,
      slug: landingRemainingEn.solutions.slug,
      detail: landingUnifiedEn.solutions.detail,
    },
    legal: {
      ...landingSecondaryEn.legal,
      privacy: {
        ...landingSecondaryEn.legal.privacy,
        page: {
          ...landingSecondaryEn.legal.privacy.page,
          ...landingUnsupportedEn.legal.privacy.page,
        },
      },
      cookie: {
        ...landingSecondaryEn.legal.cookie,
        page: {
          ...landingSecondaryEn.legal.cookie.page,
          ...landingUnsupportedEn.legal.cookie.page,
        },
      },
    },
  },

  languageSwitcher: landingUnsupportedMiscEn.languageSwitcher,

  /** Homepage `/` — see docs/landing-home-i18n-source-en.md */
  landing_home: landingHomeEn,
};
